'use client';

import HoverPrefetchLink from '@/components/hover-prefetch-link';
import { websiteEnv } from '@/constants/website-env';
import { cn } from '@/lib/utils';
import clsx from 'clsx';

interface Props {
  mode?: 'link';
  label: string;
  attributeLabel: string;
  isActive: boolean;
  href?: string;
  className?: string;
  onClick?: () => void;
}

function processLabel(label: string) {
  if (label === 'Mini/Short') return 'Mini';

  if (label === 'Knee Length') return 'Knee';

  if (label === 'Tea Length') return 'Tea';

  if (websiteEnv.isUnitedKingdom) {
    if (label === 'Scoop/U-Neck') return 'Scoop Neck';

    if (label === 'Cowl Neckline') return 'Cowl Neck';

    if (label === 'Bateau Neckline') return 'Bateau';

    if (label === 'Sweetheart Neckline') return 'Sweetheart';

    if (label === 'Straight Neckline') return 'Straight Neck';

    if (label === 'Halter Neckline') return 'Halterneck';
  }

  return label;
}

const DressStyleFilterItem = (props: Props) => {
  const { mode, label, isActive, attributeLabel, href = '#', onClick, className } = props;

  const renderLabel = processLabel(label);
  const svgIcon = svgIconMap[attributeLabel]?.[label];

  if (mode === 'link') {
    return (
      <HoverPrefetchLink
        href={href}
        className={cn(
          clsx('group flex flex-col items-center gap-y-[5px] lg_gap-y-0.5', isActive ? 'svg-selected' : ''),
          className,
        )}
        onClick={() => {
          onClick?.();
        }}
        data-filter-link='1'
      >
        <div
          className={clsx(
            'lg_flex lg_h-[76px] lg_w-[56px] lg_items-center lg_justify-center',
            isActive ? 'border-1 border-solid border-BE9983' : '',
          )}
        >
          {svgIcon}
        </div>
        <div className='text-center text-xs leading-4'>{renderLabel}</div>
      </HoverPrefetchLink>
    );
  }

  return (
    <div
      className={cn(
        clsx(' flex flex-col items-center gap-y-[5px] lg_gap-y-0.5', isActive ? 'svg-selected group' : ''),
        className,
      )}
      onClick={() => {
        onClick?.();
      }}
    >
      <div className={clsx(' flex min-h-[60px] w-[50px] items-center justify-center lg_flex lg_h-[76px] lg_w-[56px]')}>
        {svgIcon}
      </div>
      <div className='text-center text-[13px] leading-4'>{renderLabel}</div>
    </div>
  );
};

const svgIconMap = {
  Silhouette: {
    'A-line': (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='42'
        height='62'
        fill='none'
        viewBox='0 0 42 62'
      >
        <path
          className='fill-f8f3ef  group-[.svg-selected]_fill-BE9983 lg_group-hover_fill-BE9983'
          d='M23.548 61.098q.9 0 1.8-.3c1.8-.5 3.4-1.8 5.3-1.9 2.1-.2 3.1.3 5.1.1 2.4-.3 5-2.7 4.9-3.5 0 0-3.8-8.5-4.6-11.8-1.1-4.5-2.7-9.2-3.7-13.7-.9-3.9-2.4-7.7-4.3-11.3-1-1.9-1.6-4-1.6-6.1 0-1.8 2.4-5.4 3.3-6.9.1-1.3-.2-2.5-.9-3.5-1.1-1.2-2.8-1.7-4.4-1.1-1.5.6-2.9 1.5-4 2.7q-1.8-1.8-4.2-2.7c-4.6-1.4-5.6 3.9-5.1 5.4 0 0 2.9 4 3.2 5.8.3 2-1.1 5.2-2.4 7.6-3.9 7.3-5.2 12.8-6.3 21-.8 6.6-4.5 15.4-4.5 15.4.1.3 1.1 1 1.4 1.2 2.1 2.3 5.3 3.4 8.4 2.8.6-.1 2.5-.8 3.1-.9 1.5-.2 3.1.5 4.6.9 1.6.6 3.2.8 4.9.8'
        />
        <path
          className='fill-80583F '
          d='M23.15 61.402c-1.6-.1-3.1-.3-4.6-.8l-1.1-.3c-1.1-.4-2.2-.6-3.4-.6-.5.1-1.1.3-1.6.5s-1 .3-1.5.4c-3.2.6-6.4-.5-8.6-2.9-.1-.1-.2-.2-.4-.3-.6-.4-1-.7-1-1v-.2c0-.1 3.7-8.9 4.5-15.2 1.1-8.3 2.4-13.8 6.3-21.1 1-1.9 2.7-5.4 2.4-7.5-.3-1.7-3.1-5.6-3.1-5.7v-.1c-.4-1.7.1-3.5 1.2-4.8 1.1-1.2 2.8-1.5 4.2-.9 1.5.6 2.9 1.5 4.1 2.6 1.1-1.1 2.5-2 4-2.6 1.7-.6 3.5-.1 4.7 1.2q1.05 1.65.9 3.6v.1c-.2.3-.4.7-.7 1.2-1 1.7-2.6 4.3-2.6 5.6 0 2.1.6 4.1 1.6 5.9 1.9 3.6 3.4 7.4 4.3 11.3.6 2.6 1.3 5.3 2.1 7.9.5 1.9 1.1 3.9 1.6 5.8.8 3.2 4.6 11.6 4.6 11.7v.1c-.1.4-.2.8-.5 1.2-1.2 1.4-2.8 2.3-4.6 2.6q-1.35.15-2.7 0c-.8-.1-1.6-.1-2.4-.1-1.1.2-2.2.5-3.2 1.1-.6.3-1.3.6-2 .8-.6.2-1.3.3-1.9.3-.4.1-.5.2-.6.2m-8.5-2.3c1 0 2.1.3 3 .7l1.1.3c1.6.5 3.2.7 4.8.7q.9 0 1.8-.3c.7-.2 1.4-.5 2-.8 1.1-.6 2.2-.9 3.4-1.1.8-.1 1.7 0 2.5.1.9.2 1.7.2 2.6 0 1.6-.3 3.1-1.1 4.2-2.4.2-.2.3-.5.4-.8-.3-.7-3.8-8.6-4.6-11.8-.5-1.9-1-3.9-1.6-5.8-.7-2.6-1.5-5.3-2.1-7.9-.9-3.9-2.4-7.7-4.3-11.2-1-1.9-1.6-4-1.6-6.2 0-1.5 1.5-4.1 2.7-5.9.3-.4.5-.8.7-1.1.1-1.1-.2-2.3-.8-3.2-1-1.2-2.7-1.6-4.2-1q-2.25.9-3.9 2.7-.15.15-.3 0c-1.2-1.2-2.6-2.1-4.1-2.7-1.3-.5-2.8-.2-3.7.8-1 1.2-1.4 2.8-1.1 4.3.3.4 2.9 4.1 3.2 5.9.3 2.2-1.4 5.8-2.5 7.8-4 7.2-5.2 12.7-6.3 20.9-.8 6-4.1 14.2-4.5 15.2.1.2.6.5.8.7.2.1.4.2.5.4 2.5 2.4 5 3.3 8.2 2.7.5-.1 1-.2 1.4-.4.7-.2 1.3-.4 1.7-.5.2 0 .4-.1.6-.1'
        />
        <path
          className='fill-f8f3ef group-[.svg-selected]_fill-BE9983 lg_group-hover_fill-BE9983'
          d='M14.048 41.203s-.4 7.8-2.4 13.5z'
        />
        <path
          className='fill-80583F'
          d='M11.65 55.002c-.2-.1-.3-.2-.2-.3 2-5.6 2.4-13.4 2.4-13.4s.1-.2.3-.2.2.1.2.3-.4 7.9-2.4 13.6c-.1-.1-.2 0-.3 0'
        />
        <path
          fill='#F8F3EF'
          d='M22.75 35.398s3.4 12.1 1.3 21.6z'
        />
        <path
          className='fill-80583F'
          d='M24.047 57.203c-.2 0-.3-.2-.2-.3 2.1-9.3-1.2-21.3-1.3-21.4s0-.3.2-.3.3 0 .3.2 3.4 12.2 1.3 21.7c-.1.1-.2.1-.3.1'
        />
        <path
          fill='#F8F3EF'
          d='M10.848 43.602c-.1 3.1-1 6-2.5 8.7z'
        />
        <path
          className='fill-80583F'
          d='M8.349 52.6c-.2-.1-.3-.2-.2-.3 1.5-2.6 2.4-5.6 2.5-8.6 0-.1.1-.2.2-.2s.2.1.2.2c-.1 3.1-1 6.1-2.7 8.9.1-.1 0 0 0 0'
        />
        <path
          fill='#F8F3EF'
          d='M29.45 41.203c.6 4.6 2.4 9 5.3 12.6z'
        />
        <path
          className='fill-80583F'
          d='M34.85 53.998c-.1 0-.1 0-.2-.1-2.9-3.7-4.8-8.1-5.4-12.7 0-.1.1-.3.2-.3s.3.1.3.2c.6 4.6 2.4 8.9 5.3 12.4.1.1.1.3 0 .3-.1.2-.2.2-.2.2'
        />
      </svg>
    ),
    Empire: (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='31'
        height='66'
        fill='none'
        viewBox='0 0 31 66'
      >
        <path
          fill='#F8F3EF'
          className='fill-f8f3ef group-[.svg-selected]_fill-BE9983 lg_group-hover_fill-BE9983'
          d='M14.967 62.92c3.6-1.2 6.4.7 9.4.7 3.5-.1 5.3-1.9 5.3-1.9s-4.4-6.8-4.8-10.8c-.7-7.3 0-14.9-1.3-23.5-.9-5.6-1.9-9.6-2-10-.5-1.6-.2-3.4 0-5.1.1-.8.2-1.6.3-2.3 0-.1.2-1 .1-1.1-2.2-2.2-1.8-5.1-1.8-5.1s-2.4 1.4-4.6 1.5h-1.1c-2.2-.1-4.6-1.5-4.6-1.5s.5 2.7-1.8 5.1c.2 1.5.5 3 .6 4.5.1 1.2.2 2.8-.2 4-.1.4-1.2 4.5-2 10-1.3 8.5-.6 16.2-1.3 23.5-.4 3.9-4.8 10.8-4.8 10.8s2.6 3 9.2 3c3.7 0 4.2-1.4 5.4-1.8'
        />
        <path
          className='fill-80583F'
          d='M9.467 65.022c-6.7 0-9.3-3-9.4-3.1q-.15-.15 0-.3c0-.1 4.4-6.8 4.8-10.7.3-3 .3-6 .4-9.2.1-4.5.1-9.2.9-14.2.9-5.6 1.9-9.6 2-10.1.4-1.1.2-2.7.2-3.9l-.3-2.7c-.1-.6-.2-1.3-.3-1.9 0-.1 0-.1.1-.2 2.1-2.3 1.7-4.9 1.7-4.9 0-.1 0-.2.1-.2q.15-.15.3 0s2.4 1.3 4.5 1.5h1.1c2.1-.1 4.5-1.5 4.5-1.5h.3c.1.1.1.1.1.2 0 0-.4 2.8 1.7 4.9.1.1.1.4 0 1.1v.2l-.3 2.3c0 .3-.1.5-.1.8-.2 1.4-.4 2.9.1 4.2.2.5 1.2 4.5 2 10.1.8 5 .9 9.7.9 14.2 0 3.2.1 6.2.4 9.2.4 3.8 4.7 10.6 4.8 10.7s0 .2 0 .3c-.1.1-1.9 1.8-5.5 1.9-1.2 0-2.3-.2-3.4-.5-1.8-.4-3.7-.9-5.9-.2-.3.1-.6.3-1 .5-.9.8-2 1.5-4.7 1.5m-8.8-3.3c.6.6 3.2 2.8 8.9 2.8 2.6 0 3.5-.7 4.3-1.2.4-.2.7-.5 1.1-.6 2.3-.8 4.2-.3 6.1.2 1.1.3 2.2.5 3.3.5 2.8-.1 4.5-1.3 5-1.7-.7-1.1-4.4-7.1-4.7-10.7-.3-3-.3-6.1-.4-9.3-.1-4.5-.1-9.2-.9-14.2-.9-5.5-1.9-9.6-2-10-.5-1.4-.3-3-.1-4.4 0-.3.1-.5.1-.8l.3-2.3v-.2c0-.2.1-.6.1-.7-1.7-1.7-1.8-3.9-1.8-4.8-.8.4-2.6 1.2-4.3 1.3h-1.1c-1.7-.1-3.5-.9-4.3-1.3 0 .9-.1 2.9-1.8 4.7.1.6.2 1.2.2 1.8.1.9.3 1.8.3 2.7.1 1.2.2 2.9-.2 4.1-.1.4-1.2 4.5-2 10-.8 5-.9 9.7-.9 14.2 0 3.2-.1 6.2-.4 9.3-.4 3.5-4.1 9.5-4.8 10.6'
        />
        <path
          className='fill-80583F group-[.svg-selected]_fill-f8f3ef lg_group-hover_fill-f8f3ef'
          d='M14.968 16.221c-3.5 0-6.1-1-6.1-1.1-.1-.1-.2-.2-.1-.3s.2-.2.3-.1c0 0 4.8 2 10.3.4.1 0 .3 0 .3.2 0 .1 0 .3-.2.3q-2.4.6-4.5.6M14.569 19.022c-3.6 0-5.8-1-5.9-1.1s-.2-.2-.1-.3.2-.2.3-.1c.1 0 5.1 2.4 12.4-.1.1 0 .3 0 .3.2 0 .1 0 .3-.2.3-2.6.8-4.9 1.1-6.8 1.1M9.966 4.016c-.1 0-.2-.1-.3-.2 0 0-.1-1.8-1-2.4-.1-.1-.1-.2-.1-.3.1-.1.2-.1.3-.1 1.1.7 1.2 2.7 1.2 2.8.1.1 0 .2-.1.2M20.269 4.118c-.1 0-.3-.1-.3-.3 0-.1.1-2 1.2-2.8.1-.1.3 0 .3.1.1.1 0 .3-.1.3-.9.6-1 2.4-1 2.4.1.2 0 .3-.1.3'
        />
      </svg>
    ),
    Mermaid: (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='32'
        height='62'
        fill='none'
        viewBox='0 0 32 62'
      >
        <path
          className='fill-f8f3ef group-[.svg-selected]_fill-BE9983 lg_group-hover_fill-BE9983'
          d='M21.653 15.197c3.3 7.7 4.3 11.6.8 26-1.1 4.4 2.8 10.4 8.7 17.2 0 0-3.7 3.3-15.1 3.3s-15.1-3.3-15.1-3.3c5.8-6.8 9.8-12.8 8.7-17.2-3.6-14.4-2.7-18.3.6-26 .3-.6-2.5-7.4-2.5-7.4 1-2.3 1-4.9 0-7.2l3.3-.3 4.9 6.8 5-6.8 3.3.4s-1.4 2.1 0 6.9c.1-.1-2.8 7.1-2.6 7.6'
        />
        <path
          fill='#80583F'
          d='M16.05 62C4.65 62 .95 58.8.75 58.6q-.15-.15 0-.3c4.9-5.8 9.8-12.3 8.6-17-3.5-14.4-2.7-18.4.7-26.2.1-.4-1.2-4-2.5-7.2v-.2c.9-2.2.9-4.8 0-7V.5c0-.1.1-.1.2-.1l3.3-.4c.1 0 .2 0 .2.1l4.8 6.6 4.8-6.6c.1-.1.1-.1.2-.1l3.3.4c.1 0 .1 0 .2.1v.2s-1.3 2.1 0 6.7v.2c-1.3 3.3-2.7 7.1-2.7 7.5 3.4 7.9 4.3 11.9.8 26.1-1.2 4.7 3.7 11.2 8.6 17 .1.1.1.3 0 .3-.1.2-3.8 3.5-15.2 3.5m-14.7-3.6c.9.6 5 3.1 14.7 3.1s13.9-2.5 14.8-3.1c-4.9-5.8-9.7-12.4-8.6-17.2 3.5-14.1 2.7-17.9-.8-25.8-.2-.6 1.5-5.1 2.6-7.8-1.1-3.8-.5-5.9-.1-6.7l-2.8-.3-4.9 6.7c0 .1-.1.1-.2.1s-.2 0-.2-.1L10.95.6l-2.8.3c.9 2.3.8 4.8-.1 7 .9 2.1 2.8 6.8 2.5 7.5-3.4 7.8-4.1 11.5-.6 25.8 1.1 4.8-3.7 11.3-8.6 17.2'
        />
      </svg>
    ),
    Jumpsuit: (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='30'
        height='66'
        fill='none'
        viewBox='0 0 30 66'
      >
        <path
          className='fill-f8f3ef group-[.svg-selected]_fill-BE9983 lg_group-hover_fill-BE9983'
          d='m16.2 64.756-1.2-34.5-1.2 34.5H.2s3.4-25.6 6-42.6c.5-3.1 1.9-6.1 2.6-7.6l-1-5.1c-.2-.6-.2-1.2 0-1.7.9-2.6-.6-6-.6-6l3.1-.6 4.6 3.5 4.7-3.4 3.1.6s-1.5 3.3-.6 6c.2.6.2 1.2 0 1.7l-1 5.1c.7 1.5 2.1 4.5 2.6 7.6 2.6 17 6 42.6 6 42.6z'
        />
        <path
          fill='#80583F'
          d='M29.7 65.053H16.2c-.1 0-.2-.1-.3-.2l-.9-27.2-.9 27.2c0 .1-.1.2-.3.2H.3c-.1 0-.1 0-.2-.1 0-.1-.1-.1-.1-.2 0-.3 3.5-25.8 6-42.6.5-3.1 1.9-6.1 2.6-7.6l-1-5c-.2-.6-.2-1.3 0-1.9.9-2.5-.5-5.7-.6-5.8v-.2c0-.1.1-.1.2-.1l3.1-.6h.2l4.5 3.4 4.5-3.3c.1 0 .1-.1.2 0l3.1.6c.1 0 .1.1.2.1v.2s-1.4 3.2-.6 5.8c.2.6.2 1.2 0 1.9l-1 5c.7 1.5 2.1 4.5 2.6 7.6 2.5 16.8 6 42.3 6 42.6 0 .1 0 .1-.1.2zm-13.3-.5h13c-.4-2.9-3.6-26.5-6-42.3-.5-3-1.9-6-2.6-7.4v-.3l1-5.1c.2-.6.2-1.1 0-1.6-.8-2.3.1-5 .4-5.8l-2.7-.5-4.5 3.5q-.15.15-.3 0l-4.5-3.5-2.6.5c.3.9 1.2 3.6.5 5.8-.2.5-.2 1 0 1.6l1 5.1v.3c-.7 1.4-2.1 4.4-2.6 7.4-2.4 15.8-5.6 39.4-5.9 42.3h13l1.2-34.2c0-.1.1-.2.3-.2s.2.1.3.2z'
        />
      </svg>
    ),
    Princess: (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='38'
        height='60'
        fill='none'
        viewBox='0 0 38 60'
      >
        <path
          className='fill-f8f3ef group-[.svg-selected]_fill-BE9983 lg_group-hover_fill-BE9983'
          d='M19.052 58.942c6.2 0 12.4-.7 18.4-2-.9-28.4-8.2-34.4-12.5-44.5-.4-.9 3.4-9 3.4-9-.7-.4-1.8-1.7-3.2-2-4-.6-8.1-.6-12.1 0-1.3.3-2.5 1.1-3.3 2.1 0 0 3.7 8.1 3.4 9-4.4 10-11.6 16-12.5 44.4 0 0 7 2 18.4 2'
        />
        <path
          fill='#80583F'
          d='M19.052 59.245c-6.2.1-12.4-.6-18.5-2-.1 0-.2-.1-.2-.2.8-22.4 5.4-30.9 9.4-38.5 1.1-2.1 2.2-4 3.1-6.2.2-.5-1.6-4.9-3.4-8.8 0-.1 0-.2.1-.3.1 0 .2-.1.3-.1.8-1 2-1.7 3.2-2 4-.6 8.2-.5 12.2.1 1 .3 1.9.8 2.6 1.5l.6.6c.1.1.1.2.1.3-1.8 3.9-3.6 8.2-3.4 8.8.9 2.2 2 4.1 3.1 6.2 4.1 7.5 8.7 16.1 9.4 38.4 0 .1-.1.2-.2.2-6 1.3-12.2 2-18.4 2m-18.2-2.5c6 1.3 12.1 2 18.2 1.9 6.1 0 12.2-.7 18.2-1.9-.8-22.1-5.3-30.5-9.4-38-1.1-2.1-2.2-4.1-3.1-6.2-.4-.9 2.3-6.9 3.3-9-.2-.1-.3-.3-.5-.4-.7-.7-1.5-1.1-2.4-1.4-4-.6-8.1-.6-12.1-.1-1.1.3-2.1.9-2.9 1.8l-.1.1c1 2.1 3.7 8.1 3.3 9-.9 2.2-2 4.1-3.1 6.2-4.1 7.5-8.6 16-9.4 38'
        />
      </svg>
    ),
    Column: (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='22'
        height='60'
        fill='none'
        viewBox='0 0 22 60'
      >
        <path
          className='fill-f8f3ef group-[.svg-selected]_fill-BE9983 lg_group-hover_fill-BE9983'
          d='M11.55 59.586c10.1 0 9.4-2 9.4-2-.9-28.4.9-34.4-3.5-44.5-.4-.9 3.4-9 3.4-9-.4-4.6-4.5-5.3-9.3-.9-4.4-4.4-8.9-3.7-9.3.9 0 0 3.7 8.1 3.4 9-4.4 10.1-3.7 16.1-4.7 44.5 0 0-.8 2 10.6 2'
        />
        <path
          fill='#80583F'
          d='M11.552 59.888c-6.2 0-9.9-.6-10.6-1.7-.1-.2-.1-.3-.1-.5.2-4.9.3-9.2.4-12.9.5-17.9.6-23.3 4.2-31.7.2-.5-1.6-4.9-3.4-8.8v-.1c.2-1.9 1-3.3 2.4-3.8 2-.7 4.6.2 7.1 2.6 2.7-2.4 5.3-3.4 7.2-2.6 1.5.7 2.4 2.2 2.3 3.8v.1c-1.8 3.9-3.6 8.2-3.4 8.8 3.1 7.1 3.1 12.1 3.2 24.3 0 5.2.1 11.7.3 20.2 0 .2-.1.4-.2.6-.5.7-2.6 1.7-9.4 1.7m-10.3-2.3c0 .1 0 .1.1.2.3.4 2 1.5 10.2 1.5 7.1 0 8.7-1 9-1.4.1-.1.1-.1.1-.2-.3-8.6-.3-15.1-.4-20.3-.1-12.1-.1-17.1-3.1-24.1-.4-.9 2.4-7.1 3.3-9.1-.2-1.7-.9-2.9-2-3.3-1.7-.7-4.3.3-6.8 2.7-.1.1-.3.1-.4 0-2.3-2.6-4.8-3.6-6.7-2.9-1.3.6-2.1 1.9-2.1 3.3.9 2 3.7 8.2 3.3 9.1-3.6 8.3-3.7 13.6-4.2 31.5 0 3.8-.1 8-.3 13'
        />
      </svg>
    ),
    Sheath: (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='28'
        height='66'
        fill='none'
        viewBox='0 0 28 66'
      >
        <path
          fill='#F8F3EF'
          d='M6.947 1.047c-.2 1.2-.2 2.3 0 3.5z'
        />
        <path
          fill='#80583F'
          d='M6.948 4.75c-.1 0-.2-.1-.2-.2q-.3-1.8 0-3.6c0-.1.1-.2.3-.2s.2.2.2.3c-.2 1.1-.2 2.3 0 3.4q0 .3-.3.3'
        />
        <path
          fill='#F8F3EF'
          d='M20.047 1.047c.2 1.2.2 2.3 0 3.5z'
        />
        <path
          fill='#80583F'
          d='M20.048 4.75c-.2 0-.2-.2-.2-.3.2-1.1.2-2.3 0-3.4 0-.1.1-.3.2-.3s.3.1.3.2q.3 1.8 0 3.6c0 .1-.2.2-.3.2'
        />
        <path
          className='fill-f8f3ef group-[.svg-selected]_fill-BE9983 lg_group-hover_fill-BE9983'
          d='M17.048 64.402c6.5-.4 10.1-3 10.1-3-2.4-2.4-4.5-7.1-4.5-9.3-.3-19.5.7-26.3-3.3-35.5-.4-.9 3.4-9 3.4-9-.4-4.6-4.5-5.3-9.3-.9-4.5-4.3-8.9-3.6-9.3.9 0 0 3.7 8.1 3.4 9-4 9.2-5 16.8-4.9 35.6 0 3.3-.6 6.6-1.8 9.7 0 0 1.5 2.8 7.9 1.1 2.3-.6 4.9 1.6 8.3 1.4'
        />
        <path
          fill='#80583F'
          d='M16.548 65.25c-1.3-.1-2.7-.3-3.9-.8s-2.6-.7-3.9-.5c-6.5 1.6-8.1-1.1-8.1-1.3v-.2c1.1-3.1 1.8-6.3 1.8-9.6-.1-19 1-26.6 4.9-35.7.2-.5-1.6-4.9-3.4-8.8v-.1c.2-1.9 1-3.3 2.4-3.8 2-.7 4.6.2 7.1 2.6 2.7-2.4 5.3-3.4 7.2-2.6 1.3.4 2.1 1.8 2.3 3.7v.1c-1.8 3.9-3.6 8.2-3.4 8.8 3.2 7.5 3.2 13.2 3.2 25.8v9.9c0 2.1 2.1 6.7 4.5 9.1.1.1.1.1.1.2s0 .1-.1.2-3.7 2.6-10.2 3c-.1-.1-.4 0-.5 0m-6.9-2c1.1.1 2.2.3 3.2.7 1.4.5 2.8.8 4.3.8 3.4-.2 6.7-1.1 9.7-2.8-2.4-2.5-4.4-7.1-4.4-9.2v-9.9c0-12.5 0-18.2-3.2-25.6-.4-.9 2.4-7.1 3.3-9.1-.2-1.7-.9-2.9-2-3.3-1.8-.7-4.4.3-6.9 2.6-.1.1-.3.1-.4 0-2.4-2.3-4.9-3.3-6.7-2.6-1.3.6-2.1 1.9-2.1 3.3.9 2 3.7 8.2 3.3 9.1-3.9 9-4.9 16.6-4.9 35.5-.1 3.3-.7 6.6-1.8 9.7.3.4 2.1 2.3 7.5.9.4 0 .6-.1 1.1-.1'
        />
      </svg>
    ),
    'Ball Gown': (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='52'
        height='66'
        fill='none'
        viewBox='0 0 52 66'
      >
        <g clipPath='url(#clip0_13118_89115)'>
          <path
            className='fill-f8f3ef group-[.svg-selected]_fill-BE9983 lg_group-hover_fill-BE9983'
            d='M39.374 27.522c-3.466-6.622-8.508-15.755-9.877-17.714 0-.168.014-.336.027-.505.56-1.85 1.902-4.915 2.436-6.104-.117-1.394-.69-2.368-1.602-2.74-1.382-.553-3.414.264-5.46 2.187-.078.072-.234.072-.3 0C22.697.723 20.69-.094 19.23.459c-.964.372-1.563 1.346-1.68 2.74.573 1.273 2.058 4.662 2.54 6.441.013.18.013.409 0 .66-2.436 4.471-7.022 13.148-10.423 21.284-4.847 11.585-13.342 28.974-7.27 32.46 6.071 3.484 9.693-1.155 12.13 0 2.423 1.153 4.847 1.153 7.27 1.153 2.424 0 4.847 1.153 7.27 0 2.424-1.154 2.424-2.32 7.27-1.154 4.848 1.154 12.535 2.896 12.73-1.742.183-4.135 9.981 2.836-9.693-34.779'
          />
          <path
            fill='#80583F'
            d='M26.647 66.001c-.834 0-1.668-.132-2.476-.264-.782-.12-1.59-.252-2.371-.252-2.424 0-4.925 0-7.414-1.19-.782-.373-1.785-.024-3.049.42-2.189.77-5.173 1.815-9.094-.432-5.564-3.197.469-17.221 5.316-28.482.638-1.502 1.263-2.944 1.837-4.314 3.387-8.112 7.96-16.776 10.41-21.259.013-.24.013-.42 0-.553-.482-1.766-2.045-5.311-2.515-6.357l-.039-.072v-.072c.13-1.502.795-2.572 1.877-2.98 1.563-.601 3.66.216 5.641 2.187C26.881.42 29.018-.395 30.503.206c1.017.408 1.655 1.466 1.785 2.98v.072l-.026.06c-.547 1.214-1.876 4.242-2.436 6.08-.013.097-.013.217-.013.35 1.459 2.127 6.45 11.176 9.85 17.665 15.53 29.695 12.847 31.762 10.684 33.433-.534.408-.925.709-.951 1.478-.04 1.082-.482 1.91-1.29 2.463-2.554 1.755-8.352.361-11.83-.468-3.649-.877-4.43-.397-5.708.384-.378.229-.807.493-1.355.757-.86.397-1.72.541-2.566.541m-13.017-2.44c.365 0 .704.06 1.03.23 2.358 1.129 4.677 1.129 7.127 1.129.834 0 1.667.132 2.475.264 1.616.252 3.127.493 4.652-.228.52-.253.925-.505 1.29-.721 1.38-.841 2.28-1.394 6.202-.457 3.361.805 8.99 2.151 11.296.565.664-.457 1.003-1.118 1.042-2.031.04-1.022.613-1.466 1.173-1.899 1.785-1.382 4.482-3.46-10.84-32.747-3.427-6.562-8.483-15.707-9.864-17.678l-.026-.096v-.084c0-.217.013-.373.026-.53.56-1.874 1.863-4.854 2.437-6.116-.117-1.25-.626-2.115-1.407-2.428-1.251-.516-3.166.289-5.108 2.127a.54.54 0 0 1-.365.133h-.013a.58.58 0 0 1-.378-.145C22.556 1.011 20.693.23 19.364.734c-.834.325-1.368 1.19-1.485 2.44.625 1.394 2.045 4.65 2.528 6.417.026.229.026.457 0 .745l-.026.108c-2.437 4.459-7.01 13.136-10.411 21.26-.573 1.37-1.199 2.824-1.837 4.314C3.376 47.086-2.552 60.87 2.568 63.814c3.662 2.103 6.372 1.154 8.56.385.9-.349 1.733-.637 2.502-.637'
          />
          <path
            fill='#80583F'
            d='M13.852 64.195h-.053c-.169-.023-.286-.168-.26-.324.026-.12 2.15-12.138 4.56-19.036 2.345-6.717 2.41-7.98 2.41-8.027 0-.157.144-.277.313-.277.17 0 .3.132.3.289 0 .048-.026 1.285-2.437 8.196-2.397 6.85-4.508 18.831-4.534 18.951s-.156.228-.3.228M40.378 64.884h-.026a.29.29 0 0 1-.274-.313c.013-.096 1.004-9.277-1.368-16.091-2.345-6.718-3.557-7.992-3.57-8.004a.267.267 0 0 1 .013-.396.313.313 0 0 1 .43.012c.052.048 1.29 1.286 3.714 8.22 2.41 6.934 1.407 16.235 1.394 16.32-.013.144-.157.252-.313.252M12.326 39.405c-.026 0-.052 0-.091-.012-.17-.048-.26-.205-.209-.349.04-.12 3.675-11.669 6.072-17.413 2.41-5.757 6.046-11.573 6.085-11.633a.31.31 0 0 1 .364-.12.3.3 0 0 1 .209.312l-2.424 15.07c-.026.156-.182.252-.351.24-.17-.023-.287-.168-.26-.324l2.175-13.532c-1.212 2.055-3.53 6.141-5.225 10.191-2.397 5.72-6.02 17.245-6.058 17.365-.026.12-.144.205-.287.205M38.46 32.703a.31.31 0 0 1-.3-.216c-.012-.036-1.028-3.545-2.097-5.877-1.068-2.343-7.648-14.168-7.713-14.288-.078-.145-.013-.313.13-.385a.33.33 0 0 1 .417.12c.274.493 6.658 11.97 7.74 14.325 1.08 2.368 2.11 5.913 2.123 5.949a.28.28 0 0 1-.221.348c-.026.024-.052.024-.078.024'
          />
        </g>
        <defs>
          <clipPath id='clip0_13118_89115'>
            <path
              fill='#fff'
              d='M0 0h52v66H0z'
            />
          </clipPath>
        </defs>
      </svg>
    ),
  },
  Hemline: {
    'Mini/Short': (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='40'
        height='60'
        fill='none'
        viewBox='0 0 40 60'
      >
        <g clipPath='url(#clip0_12360_24546)'>
          <mask
            id='mask0_12360_24546'
            width='40'
            height='61'
            x='0'
            y='0'
            maskUnits='userSpaceOnUse'
            style={{
              maskType: 'luminance',
            }}
          >
            <path
              fill='#fff'
              d='M39.902.102H.102v60h39.8z'
            />
          </mask>
          <g mask='url(#mask0_12360_24546)'>
            <path
              fill='#fff'
              d='M19.203 21.098s.9 7.4 1.1 9 2 3.9 2 7.5c0 5.6 2.8 9.1 2.8 12.2 0 1.7-.2 3.3.7 4.4 1.7 2.2 1.4 5.6 3.6 5.6 1.4 0-1.4-4.4-1.8-9.4-.3-3.5.5-11.5-.7-18 0 0-.4-5.4-.3-7.9.1-2-1.6-6.1-1.6-6.1z'
            />
            <path
              fill='#80583F'
              d='M29.404 60.1c-1.5 0-1.9-1.4-2.4-2.9-.3-.9-.7-2-1.3-2.9-.8-1-.8-2.4-.8-3.8v-.8c0-1.3-.5-2.6-1.1-4.2-.8-2.2-1.7-4.7-1.7-8 0-2.3-.7-4.1-1.3-5.5-.3-.8-.6-1.4-.7-2-.2-1.5-1.1-8.9-1.1-9s0-.2.1-.3l5.8-2.7h.2c.1 0 .1.1.1.1.1.2 1.7 4.2 1.7 6.2-.1 2.5.3 7.8.3 7.9.8 4.3.7 9.2.7 13.1 0 2-.1 3.8.1 4.9.2 2.4 1 4.7 1.5 6.4.5 1.6.9 2.6.5 3.1-.1.3-.3.4-.6.4m-10-38.9c.1 1.1.9 7.4 1.1 8.8q.15.75.6 1.8c.6 1.4 1.4 3.3 1.4 5.7 0 3.2.9 5.7 1.7 7.8.6 1.6 1.1 3 1.1 4.4v.8c0 1.4 0 2.6.7 3.5s1.1 2 1.4 3c.5 1.6.9 2.5 2 2.5h.1c.2-.2-.2-1.5-.6-2.6-.6-1.7-1.3-4-1.6-6.5-.1-1.2-.1-3-.1-5 .1-3.9.1-8.7-.7-13 0-.1-.4-5.4-.3-8 .1-1.7-1.2-4.9-1.5-5.8z'
            />
            <path
              fill='#fff'
              d='M19.204 18.302c-.1-1 .4 6.5-.1 11.2-.3 3.2-1.3 5.3-1.2 8.1.2 7.6-.7 8.9-1.1 13.6-.2 2.2 0 2.3-.2 4.4-.2 2-.5 4.2-1.2 4.1-1.3-.3-.7-1.5-.8-5.4 0-1.3-.1-2.4-.1-3.5.1-3-1.3-5.4-1.6-10.9 0-.7.3-5.8.2-6.5l-1.6-14.3c0 .2 7.7-.8 7.7-.8'
            />
            <path
              fill='#80583F'
              d='M15.505 60.1h-.1c-1.1-.2-1.1-1.1-1-2.8V54c0-1.1-.1-2.1-.1-3 0-1.4-.2-2.6-.6-4.2-.4-1.7-.8-3.8-1-6.7 0-.3 0-1.4.1-2.7.1-1.5.2-3.4.1-3.8l-1.6-14.3c0-.1.1-.3.2-.3l7.4-.9c.1-.1.1-.1.2-.1.2 0 .3.2.3.3v.4c.2 4.7.2 8.4-.1 10.9-.1 1.4-.4 2.5-.6 3.7-.3 1.5-.6 2.8-.6 4.4.2 5-.2 7.2-.6 9.6-.2 1.2-.4 2.4-.5 4.1-.1 1-.1 1.6-.1 2.1 0 .6 0 1.2-.1 2.3v.1c-.2 2.2-.5 3.7-1 4.1 0 0-.2.1-.3.1m-3.7-40.6 1.6 14.1c.1.4 0 1.8-.1 3.8-.1 1.2-.1 2.4-.1 2.7.2 2.9.6 4.8 1 6.6.3 1.5.6 2.8.6 4.3 0 .9 0 1.9.1 3v3.3c0 1.8 0 2.2.6 2.3.5-.3.7-2.3.9-3.8v-.1c.1-1.1.1-1.6.1-2.2 0-.5 0-1.1.1-2.2.1-1.6.3-2.9.5-4.1.4-2.3.7-4.6.6-9.5-.1-1.7.3-3.1.6-4.6.2-1.1.5-2.3.6-3.6.2-2.5.3-6.2.1-10.9v-.1z'
            />
            <path
              fill='#fff'
              d='M9.003 25.102s.5 1.2-.1.8c-.7-.6-.9-1.5-1.3-2.3-.4-1-.4-1.9-.4-2.9.1-2.4.7-4.8 1.4-7.1.3-1.1.6-2.2 1.1-3.2.5-1.1 1.1-2 1.5-3.1.1-.4.2-.8.2-1.2 0-6.1-1.5-12.3.5-13.8 1.1-.9 3.6-1.2 4.9-1.9 1.2-.7.9-4.8.9-4.8h4.5s-.4 4.6 1.9 4.7 4.2-.2 5.2 1.3 6.7 7.6 7.1 8.4.2 1.3-.5 2.4-6.1 7.9-7.1 8.2c-.8.3-.9-.2-1.5 0q-.6.15-1.5.6s-1 .5-1.2.2l2.4-1.6s-1.2 0-1.7.4c-.4.3-.9.5-1.3.3 0 0 .3-.2 1.3-1 .3-.3.8-.4 1.4-.4 0 0-1.4 0-1.8.3s-.9.8-1.1.5c0 0 .4-.6.8-.9 0 0 .5-.7 1.7-.5l-.7-.2s-.4.1-.6.2c-.7.5-1 .1-1 .1l1-1.2c.1 0 .3-.1.4-.1l3.1.4 3.8-6.5-5.9-5.4-5.8 5.2-4.5 2.7-2.1 5.1-2.6 4.2-1 3.7-.9 5.5c.1 2.8 0 2.3-.5 2.9'
            />
            <path
              fill='#80583F'
              d='M9.102 26.203c-.1 0-.2-.1-.4-.2-.6-.5-.9-1.3-1.2-2-.1-.1-.1-.3-.2-.4-.4-1-.4-1.9-.4-3 .1-2.5.7-5.1 1.4-7.2l.1-.5c.4-.9.6-1.9 1-2.7l.6-1.2c.3-.6.6-1.3.9-1.9.1-.4.2-.7.2-1.1 0-2-.2-4-.3-5.8-.3-4-.6-7.1.9-8.2.7-.5 1.8-.8 2.8-1.1.8-.2 1.6-.5 2.1-.8.8-.5.9-3.1.7-4.6 0-.1 0-.1.1-.2 0-.1.1-.1.2-.1h4.5c.1 0 .1 0 .2.1 0 .1.1.1.1.2 0 0-.2 2.9.8 4 .3.3.6.4.9.4h1.2c1.8 0 3.3 0 4.2 1.4.5.8 2.3 2.9 4 4.7 1.8 2 2.9 3.3 3.1 3.7.5.9.2 1.6-.5 2.6-.5.8-6.1 8-7.2 8.3-.5.2-.8.1-1 0s-.3-.1-.5 0c-.3.1-.6.2-.9.4-.2.1-.4.1-.5.2-.2.1-.9.4-1.3.3-.1 0-.2-.1-.3-.2s0-.2.1-.3l1.6-1c-.2 0-.5.1-.6.2q-.9.6-1.5.3c-.1 0-.1-.1-.1-.2s0-.2.1-.2l.2-.1c-.1 0-.2 0-.3-.1q-.15-.15 0-.3s.4-.5.7-.9c-.3 0-.5-.2-.6-.2 0-.1-.1-.1-.1-.2s0-.1.1-.2l1.1-.8c.1-.1.5-.1.6-.1l2.9.4 3.4-6.4-5.6-5.1-5.7 5.1-4.4 2.6-2 5.1-2.6 4.2-1 3.7-.9 5.4c.1 2.4.1 2.5-.4 2.9l-.1.1c.3.8.1.9 0 1zm8.7-40.5c.1 1 .2 4.1-1 4.8-.6.3-1.4.6-2.2.8-1 .3-2 .6-2.6 1.1-1.2.9-1 4.1-.7 7.7.1 1.8.3 3.8.3 5.8 0 .4-.1.9-.2 1.3-.2.7-.6 1.4-.9 2l-.6 1.2q-.6 1.35-.9 2.7l-.1.5c-.6 2.1-1.3 4.6-1.4 7.1 0 1.1 0 1.9.4 2.8 0 .1.1.2.1.3.2.6.4 1.2.9 1.6 0-.1-.1-.2-.1-.3s0-.2.1-.3l.1-.1c.3-.2.3-.2.2-2.5l.9-5.5 1-3.8v-.1l2.6-4.2 2.1-5.1c0-.1.1-.1.1-.1l4.5-2.7 5.8-5.2q.15-.15.3 0l6 5.5q.15.15 0 .3l-3.8 6.5c0 .1-.1.1-.2.1l-3.1-.4h-.2l-.6.5c.1 0 .1-.1.2-.1.2-.1.6-.2.7-.2h.1l.7.2c.1 0 .2.2.2.3s-.2.2-.3.2c-1-.1-1.4.4-1.5.4.3-.2 1.1-.3 1.6-.3h.3c.1 0 .2.1.2.2s-.1.2-.2.3h-.3c-.4 0-.8.1-1 .3s-.4.3-.6.5c.1 0 .2-.1.2-.2.6-.4 1.7-.4 1.9-.4.1 0 .2.1.2.2s0 .2-.1.3l-1.5 1c.2-.1.4-.2.6-.2.4-.2.7-.3 1-.4.4-.1.6 0 .8 0 .2.1.4.1.7 0 .9-.3 6.1-6.9 7-8.1.7-1 .8-1.5.5-2.1-.2-.3-1.6-2-3-3.6-1.7-2-3.5-4-4-4.8-.7-1.1-2-1.1-3.7-1.1h-1.2c-.5 0-.9-.2-1.3-.6-1-1.1-1-3.3-.9-4.1z'
            />
            <path
              fill='#80583F'
              d='M9.004 25.305c-.1 0-.2-.1-.2-.2l-.2-1.3c-.1 0-.1 0-.2-.1-.9-.8-.1-2.2 0-2.3s.2-.1.3-.1.2.1.2.2l.3 3.5c.1.2 0 .3-.2.3'
            />
            <path
              className='fill-f8f3ef group-[.svg-selected]_fill-BE9983 lg_group-hover_fill-BE9983'
              d='M19.402 32.206c-10-.6-12.8-3.9-12.8-3.9 2.3-6.1 3.6-10.2 4.4-15.3.9-5.1 4-8.2 3.8-10.2-.2-1.3-1.3-3-1.2-7.5 0-.2-.5-3.9-.2-4 .4-.1 2.1-.7 2.1-.7.3 1 .6 1.4 1.3 2.2 1.2 1.6 2.4 2.7 3 3.2.2.1.5-.5.7-.6.7-.8 3-5.5 3.3-5.5s2.5.1 2.5.1c-.5 0 0 4.1 0 4.6.1.8 0 1.6.2 2.3.3 1.6-.4 4-.9 5.3-.4 1 1.3 3.9 1.8 5.6 1.1 3.3 1.4 5.4 1.3 8.9-.2 5.4-1 6.4.5 13.3 0 .1-2.6 2.6-9.8 2.2'
            />
            <path
              fill='#80583F'
              d='M21.003 32.505h-1.6c-10-.6-12.9-3.9-13-4s-.1-.2 0-.2c2.2-5.8 3.6-10 4.4-15.1.5-2.8 1.6-5 2.5-6.7.7-1.4 1.3-2.6 1.2-3.4 0-.3-.2-.7-.3-1.2-.4-1.2-.9-3.1-.9-6.4 0-.1 0-.4-.1-.7-.3-3.2-.3-3.4 0-3.5.4-.1 2.1-.7 2.1-.7h.2c.1 0 .1.1.1.2.4.6.8 1.1 1.4 1.9 1 1.4 2.1 2.3 2.9 3.1l.3-.3c.1-.1.1-.2.2-.2.3-.4 1.1-1.7 1.8-2.9 1.4-2.5 1.5-2.6 1.8-2.6s2.3.1 2.5.1c.1 0 .2.1.2.2s-.1.2-.1.2c-.1.1-.2.9.1 4.3v.1c0 .3 0 .6.1.9 0 .5 0 1 .1 1.4.3 1.7-.4 4.3-.9 5.5-.3.6.4 2.2 1 3.5.3.7.6 1.3.8 1.9 1.1 3.2 1.4 5.4 1.3 9 0 1.4-.1 2.6-.2 3.6-.2 2.8-.4 4.7.7 9.7 0 .1 0 .2-.1.2-.2 0-2.5 2.1-8.5 2.1m-1.6-.6c6.2.3 9-1.5 9.5-1.9-1-5-.9-6.8-.7-9.7.1-1 .2-2.1.2-3.5.1-3.5-.2-5.6-1.3-8.8-.2-.5-.5-1.2-.8-1.8-.7-1.6-1.4-3.1-1.1-3.9.5-1.3 1.1-3.7.9-5.2-.1-.5-.1-1-.1-1.5 0-.3 0-.6-.1-.9v-.1c-.3-2.8-.2-3.9-.1-4.3-.6 0-1.7-.1-2.1-.1-.2.3-.9 1.4-1.4 2.3-.7 1.3-1.5 2.6-1.8 3 0 0-.1.1-.1.2q-.3.45-.6.6c-.1 0-.2 0-.2-.1-.8-.7-2-1.8-3.1-3.2-.6-.8-.9-1.2-1.2-2.1-.4.1-1.4.5-1.8.6-.1.4.1 2.2.2 3 .1.5.1.7.1.8-.1 3.2.4 5 .8 6.2.3.5.4.9.4 1.3.1 1-.5 2.2-1.3 3.7-.9 1.7-2 3.9-2.5 6.6-.9 5.1-2.2 9.3-4.4 15.1.6.5 3.9 3.2 12.6 3.7'
            />
            <mask
              id='mask1_12360_24546'
              width='12'
              height='12'
              x='21'
              y='4'
              maskUnits='userSpaceOnUse'
              style={{
                maskType: 'luminance',
              }}
            >
              <path
                fill='#fff'
                d='M26.602 15.703a5.5 5.5 0 1 0 0-11 5.5 5.5 0 0 0 0 11'
              />
            </mask>
            <g mask='url(#mask1_12360_24546)'>
              <path
                fill='#fff'
                d='M9.003 25.102s.5 1.2-.1.8c-.7-.6-.9-1.5-1.3-2.3-.4-1-.4-1.9-.4-2.9.1-2.4.7-4.8 1.4-7.1.3-1.1.6-2.2 1.1-3.2.5-1.1 1.1-2 1.5-3.1.1-.4.2-.8.2-1.2 0-6.1-1.5-12.3.5-13.8 1.1-.9 3.6-1.2 4.9-1.9 1.2-.7.9-4.8.9-4.8h4.5s-.4 4.6 1.9 4.7 4.2-.2 5.2 1.3 6.7 7.6 7.1 8.4.2 1.3-.5 2.4-6.1 7.9-7.1 8.2c-.8.3-.9-.2-1.5 0q-.6.15-1.5.6s-1 .5-1.2.2l2.4-1.6s-1.2 0-1.7.4c-.4.3-.9.5-1.3.3 0 0 .3-.2 1.3-1 .3-.3.8-.4 1.4-.4 0 0-1.4 0-1.8.3s-.9.8-1.1.5c0 0 .4-.6.8-.9 0 0 .5-.7 1.7-.5l-.7-.2s-.4.1-.6.2c-.7.5-1 .1-1 .1l1-1.2c.1 0 .3-.1.4-.1l3.1.4 3.8-6.5-5.9-5.4-5.8 5.2-4.5 2.7-2.1 5.1-2.6 4.2-1 3.7-.9 5.5c.1 2.8 0 2.3-.5 2.9'
              />
              <path
                fill='#80583F'
                d='M9.102 26.203c-.1 0-.2-.1-.4-.2-.6-.5-.9-1.3-1.2-2-.1-.1-.1-.3-.2-.4-.4-1-.4-1.9-.4-3 .1-2.5.7-5.1 1.4-7.2l.1-.5c.4-.9.6-1.9 1-2.7l.6-1.2c.3-.6.6-1.3.9-1.9.1-.4.2-.7.2-1.1 0-2-.2-4-.3-5.8-.3-4-.6-7.1.9-8.2.7-.5 1.8-.8 2.8-1.1.8-.2 1.6-.5 2.1-.8.8-.5.9-3.1.7-4.6 0-.1 0-.1.1-.2 0-.1.1-.1.2-.1h4.5c.1 0 .1 0 .2.1 0 .1.1.1.1.2 0 0-.2 2.9.8 4 .3.3.6.4.9.4h1.2c1.8 0 3.3 0 4.2 1.4.5.8 2.3 2.9 4 4.7 1.8 2 2.9 3.3 3.1 3.7.5.9.2 1.6-.5 2.6-.5.8-6.1 8-7.2 8.3-.5.2-.8.1-1 0s-.3-.1-.5 0c-.3.1-.6.2-.9.4-.2.1-.4.1-.5.2-.2.1-.9.4-1.3.3-.1 0-.2-.1-.3-.2s0-.2.1-.3l1.6-1c-.2 0-.5.1-.6.2q-.9.6-1.5.3c-.1 0-.1-.1-.1-.2s0-.2.1-.2l.2-.1c-.1 0-.2 0-.3-.1q-.15-.15 0-.3s.4-.5.7-.9c-.3 0-.5-.2-.6-.2 0-.1-.1-.1-.1-.2s0-.1.1-.2l1.1-.8c.1-.1.5-.1.6-.1l2.9.4 3.4-6.4-5.6-5.1-5.7 5.1-4.4 2.6-2 5.1-2.6 4.2-1 3.7-.9 5.4c.1 2.4.1 2.5-.4 2.9l-.1.1c.3.8.1.9 0 1zm8.7-40.5c.1 1 .2 4.1-1 4.8-.6.3-1.4.6-2.2.8-1 .3-2 .6-2.6 1.1-1.2.9-1 4.1-.7 7.7.1 1.8.3 3.8.3 5.8 0 .4-.1.9-.2 1.3-.2.7-.6 1.4-.9 2l-.6 1.2q-.6 1.35-.9 2.7l-.1.5c-.6 2.1-1.3 4.6-1.4 7.1 0 1.1 0 1.9.4 2.8 0 .1.1.2.1.3.2.6.4 1.2.9 1.6 0-.1-.1-.2-.1-.3s0-.2.1-.3l.1-.1c.3-.2.3-.2.2-2.5l.9-5.5 1-3.8v-.1l2.6-4.2 2.1-5.1c0-.1.1-.1.1-.1l4.5-2.7 5.8-5.2q.15-.15.3 0l6 5.5q.15.15 0 .3l-3.8 6.5c0 .1-.1.1-.2.1l-3.1-.4h-.2l-.6.5c.1 0 .1-.1.2-.1.2-.1.6-.2.7-.2h.1l.7.2c.1 0 .2.2.2.3s-.2.2-.3.2c-1-.1-1.4.4-1.5.4.3-.2 1.1-.3 1.6-.3h.3c.1 0 .2.1.2.2s-.1.2-.2.3h-.3c-.4 0-.8.1-1 .3s-.4.3-.6.5c.1 0 .2-.1.2-.2.6-.4 1.7-.4 1.9-.4.1 0 .2.1.2.2s0 .2-.1.3l-1.5 1c.2-.1.4-.2.6-.2.4-.2.7-.3 1-.4.4-.1.6 0 .8 0 .2.1.4.1.7 0 .9-.3 6.1-6.9 7-8.1.7-1 .8-1.5.5-2.1-.2-.3-1.6-2-3-3.6-1.7-2-3.5-4-4-4.8-.7-1.1-2-1.1-3.7-1.1h-1.2c-.5 0-.9-.2-1.3-.6-1-1.1-1-3.3-.9-4.1z'
              />
            </g>
          </g>
        </g>
        <defs>
          <clipPath id='clip0_12360_24546'>
            <path
              fill='#fff'
              d='M0 0h40v60H0z'
            />
          </clipPath>
        </defs>
      </svg>
    ),
    'Knee Length': (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='40'
        height='60'
        fill='none'
        viewBox='0 0 40 60'
      >
        <g clipPath='url(#clip0_12360_24579)'>
          <mask
            id='mask0_12360_24579'
            width='40'
            height='61'
            x='0'
            y='0'
            maskUnits='userSpaceOnUse'
            style={{
              maskType: 'luminance',
            }}
          >
            <path
              fill='#fff'
              d='M39.902.102H.102v60h39.8z'
            />
          </mask>
          <g mask='url(#mask0_12360_24579)'>
            <path
              fill='#fff'
              d='M17.5 21.098s.9 7.4 1.1 9 2 3.9 2 7.5c0 5.6 2.8 9.1 2.8 12.2 0 1.7-.2 3.3.7 4.4 1.7 2.2 1.4 5.6 3.6 5.6 1.4 0-1.4-4.4-1.8-9.4-.3-3.5.5-11.5-.7-18 0 0-.4-5.4-.3-7.9.1-2-1.6-6.1-1.6-6.1z'
            />
            <path
              fill='#80583F'
              d='M27.7 60.1c-1.5 0-1.9-1.4-2.4-2.9-.3-.9-.7-2-1.3-2.9-.8-1-.8-2.4-.8-3.8v-.8c0-1.3-.5-2.6-1.1-4.2-.8-2.2-1.7-4.7-1.7-8 0-2.3-.7-4.1-1.3-5.5-.3-.8-.6-1.4-.7-2-.2-1.5-1.1-8.9-1.1-9s0-.2.1-.3l5.8-2.7h.2c.1 0 .1.1.1.1.1.2 1.7 4.2 1.7 6.2-.1 2.5.3 7.8.3 7.9.8 4.3.7 9.2.7 13.1 0 2-.1 3.8.1 4.9.2 2.4 1 4.7 1.5 6.4.5 1.6.9 2.6.5 3.1-.2.3-.3.4-.6.4m-10-38.9c.1 1.1.9 7.4 1.1 8.8q.15.75.6 1.8c.6 1.4 1.4 3.3 1.4 5.7 0 3.2.9 5.7 1.7 7.8.6 1.6 1.1 3 1.1 4.4v.8c0 1.4 0 2.6.7 3.5s1.1 2 1.4 3c.5 1.6.9 2.5 2 2.5h.1c.2-.2-.2-1.5-.6-2.6-.6-1.7-1.3-4-1.6-6.5-.1-1.2-.1-3-.1-5 .1-3.9.1-8.7-.7-13 0-.1-.4-5.4-.3-8 .1-1.7-1.2-4.9-1.5-5.8z'
            />
            <path
              fill='#fff'
              d='M18.704 18.302c-.1-1 .4 6.5-.1 11.2-.3 3.2-1.3 5.3-1.2 8.1.2 7.6-.7 8.9-1.1 13.6-.2 2.2 0 2.3-.2 4.4-.2 2-.5 4.2-1.2 4.1-1.3-.3-.7-1.5-.8-5.4 0-1.3-.1-2.4-.1-3.5.1-3-1.3-5.4-1.6-10.9 0-.7.3-5.8.2-6.5l-1.6-14.3c.1.2 7.7-.8 7.7-.8'
            />
            <path
              fill='#80583F'
              d='M15.102 60.1h-.1c-1.1-.2-1.1-1.1-1-2.8v-3.4c0-1-.1-2-.1-2.9 0-1.4-.2-2.6-.6-4.2-.4-1.7-.8-3.8-1-6.7 0-.3 0-1.4.1-2.7.1-1.5.2-3.4.1-3.8l-1.6-14.3c0-.1.1-.3.2-.3l7.4-.9c0-.1.1-.1.2-.1.2 0 .3.2.3.3v.4c.2 4.7.2 8.4-.1 10.9-.1 1.4-.4 2.5-.6 3.7-.3 1.5-.6 2.8-.6 4.4.2 5-.2 7.2-.6 9.6-.2 1.2-.4 2.4-.5 4.1-.1 1-.1 1.6-.1 2.1 0 .6 0 1.2-.1 2.3v.1c-.2 2.2-.5 3.7-1 4.1-.1 0-.2.1-.3.1m-3.7-40.6 1.6 14.1c.1.4 0 1.8-.1 3.8-.1 1.2-.1 2.4-.1 2.7.2 2.9.6 4.8 1 6.6.3 1.5.6 2.8.6 4.3 0 .9 0 1.8.1 2.9v3.4c0 1.8 0 2.2.6 2.3.5-.3.7-2.3.9-3.8v-.1c.1-1.1.1-1.6.1-2.2 0-.5 0-1.1.1-2.2.1-1.6.3-2.9.5-4.1.4-2.3.7-4.6.6-9.5-.1-1.7.3-3.1.6-4.6.2-1.1.5-2.3.6-3.6.2-2.5.3-6.2.1-10.9v-.1z'
            />
            <path
              fill='#fff'
              d='M8.6 25.102s.5 1.2-.1.8c-.7-.6-.9-1.5-1.3-2.3-.4-1-.4-1.9-.4-2.9.1-2.4.7-4.8 1.4-7.1.3-1.1.6-2.2 1.1-3.2.5-1.1 1.1-2 1.5-3.1.1-.4.2-.8.2-1.2 0-6.1-1.5-12.3.5-13.8 1.1-.9 3.6-1.2 4.9-1.9 1.2-.7.9-4.8.9-4.8h4.5s-.4 4.6 1.9 4.7 4.2-.2 5.2 1.3c.9 1.5 6.7 7.6 7.1 8.4s.2 1.3-.5 2.4-6.1 7.9-7.1 8.2c-.8.3-.9-.2-1.5 0q-.6.15-1.5.6s-1 .5-1.2.2l2.4-1.6s-1.2 0-1.7.4c-.4.3-.9.5-1.3.3 0 0 .3-.2 1.3-1 .3-.3.8-.4 1.4-.4 0 0-1.4 0-1.8.3s-.9.8-1.1.5c0 0 .4-.6.8-.9 0 0 .5-.7 1.7-.5l-.7-.2s-.4.1-.6.2c-.7.5-1 .1-1 .1l1.1-.8c.1 0 .3-.1.4-.1l3.1.4 3.8-6.5-6-5.8-5.9 5.2-4.5 2.7-2.1 5.2-2.5 4.1-1 3.7-.9 5.5c.1 2.8 0 2.3-.5 2.9'
            />
            <path
              fill='#80583F'
              d='M8.7 26.202c-.1 0-.2-.1-.4-.2-.6-.5-.9-1.3-1.2-2-.1-.1-.1-.3-.2-.4-.4-1-.4-1.9-.4-3 .1-2.5.7-5.1 1.4-7.2l.1-.5c.3-.9.6-1.9 1-2.8.2-.3.4-.7.6-1.1.3-.6.6-1.3.9-1.9.1-.4.2-.7.2-1.1 0-2-.2-4-.3-5.8-.4-4.1-.6-7.2.8-8.3.7-.5 1.8-.8 2.8-1.1.8-.2 1.6-.5 2.1-.8.8-.5.9-3.1.7-4.6 0-.1 0-.1.1-.2 0-.1.1-.1.2-.1h4.5c.1 0 .1 0 .2.1 0 .1.1.1.1.2 0 0-.2 2.9.8 4 .3.3.6.4.9.4h1.2c1.8 0 3.3 0 4.2 1.4.5.8 2.3 2.9 4 4.8 1.8 2 2.9 3.3 3.1 3.7.5.9.2 1.6-.5 2.6-.5.8-6.1 8-7.2 8.3-.5.2-.8.1-1 0s-.3-.1-.5 0c-.3.1-.6.2-.9.4-.2.1-.3.1-.5.2s-.9.4-1.3.3c-.1 0-.2-.1-.3-.2s0-.2.1-.3l1.6-1c-.2 0-.5.1-.6.2q-.9.6-1.5.3c-.1 0-.1-.1-.1-.2s0-.2.1-.2l.2-.1c-.1 0-.2 0-.3-.1q-.15-.15 0-.3s.4-.5.7-.9c-.3 0-.5-.2-.6-.2 0-.1-.1-.1-.1-.2s0-.1.1-.2l1.1-.8c.1-.1.5-.1.6-.1l2.9.4 3.6-6.2-5.7-5.3-5.7 5.1-4.4 2.6-2 5.1-2.6 4.2-1 3.7-.9 5.4c.1 2.4.1 2.5-.4 2.9l-.1.1c.3.8.1.9 0 1zm8.7-40.5c.1 1 .2 4.1-1 4.8-.6.3-1.4.5-2.2.8-1 .3-2 .6-2.6 1.1-1.2.9-1 4.1-.7 7.7.1 1.8.3 3.8.3 5.8 0 .5-.1.9-.2 1.3-.2.7-.6 1.4-.9 2q-.45.6-.6 1.2-.6 1.35-.9 2.7l-.1.5c-.6 2.1-1.3 4.6-1.4 7.1 0 1.1 0 1.9.4 2.8.1.1.1.3.2.4.1.6.3 1.1.7 1.6 0-.1-.1-.2-.1-.3s0-.2.1-.3.2-.1.2-.2c.3-.2.3-.2.2-2.5l.9-5.5 1-3.8v-.1l2.6-4.2 2.1-5.1c0-.1.1-.1.1-.1l4.5-2.6 5.8-5.2q.15-.15.3 0l6 5.4q.15.15 0 .3l-3.8 6.5c-.1.1-.1.2-.3.2l-3.1-.4h-.2l-.5.4c.1 0 .1-.1.2-.1.2-.1.6-.2.7-.2h.1l.7.2c.1.1.2.2.1.3 0 .1-.2.2-.3.2-1-.1-1.4.4-1.5.4.3-.2 1.1-.3 1.6-.3h.3c.1 0 .2.1.2.2s-.1.2-.2.3h-.3c-.4 0-.8.1-1 .3s-.4.3-.6.5c.1 0 .2-.1.2-.2.6-.4 1.7-.4 1.9-.4.1 0 .2.1.2.2s0 .2-.1.3l-1.5 1c.2-.1.4-.2.6-.2.4-.2.7-.3 1-.4.4-.1.6 0 .8 0 .2.1.4.1.7 0 .9-.3 6.1-6.9 7-8.1.7-1 .8-1.5.5-2.1-.2-.3-1.6-2-3-3.6-1.7-2-3.5-4-4-4.8-.7-1.1-2-1.1-3.7-1.1h-1.2c-.5 0-.9-.2-1.3-.6-1-1.1-1-3.3-.9-4.1z'
            />
            <path
              fill='#80583F'
              d='M8.601 25.305c-.1 0-.2-.1-.2-.2l-.2-1.3c-.1 0-.1 0-.2-.1-.9-.8-.1-2.2 0-2.3s.2-.1.3-.1.2.1.2.2l.3 3.5c0 .2-.1.3-.2.3'
            />
            <path
              className='fill-f8f3ef group-[.svg-selected]_fill-BE9983 lg_group-hover_fill-BE9983'
              d='M18.702 44.005c-4.7 0-9.8-1.3-13.8-3.9 0-12 3.7-21.6 5.4-27.2 1.3-4.1 2.7-6.7 3.5-8 .4-.7.4-1.5 0-2.2-1.2-2-1.3-3.8-.7-7.5 0-.2-.5-3.9-.2-4 .4-.1 2.1-.7 2.1-.7.3 1 .6 1.4 1.3 2.2 1.2 1.5 2.4 2.6 3.1 3.2.2.1.5-.5.7-.6.7-.8 3-5.5 3.3-5.5s2.5.1 2.5.1c-.5 0 0 4.1 0 4.6.1 1 1.9 5.5-.2 8.1-.3.4-.3 1.1-.2 1.6.3 1.1.9 2.8 1.6 3.8 3.6 5.2 6.8 13.6 8 31.1.1.9-6.1 3.4-6.8 3.6-2.9.9-6 1.4-9.6 1.3'
            />
            <path
              fill='#80583F'
              d='M19.1 44.305h-.3c-5.1-.1-10.1-1.5-13.9-3.9-.1 0-.1-.1-.1-.2 0-10.4 2.8-19 4.6-24.7.3-.9.6-1.8.8-2.6 1.2-4.1 2.6-6.6 3.6-8.1.4-.6.4-1.3 0-1.9-1.3-2.1-1.3-4-.7-7.7 0-.1-.1-.6-.1-1-.3-2.7-.3-3 0-3.1.4-.1 2.1-.7 2.1-.7h.2c.1 0 .1.1.1.2.2.9.6 1.3 1.2 2.1 1.2 1.5 2.4 2.6 2.9 3.1l.3-.3c.1-.1.1-.2.2-.2.3-.4 1.1-1.7 1.8-2.9 1.4-2.5 1.5-2.6 1.8-2.6s2.3.1 2.5.1c.1 0 .2.1.2.2s-.1.2-.1.2c-.1.2-.2.9.1 4.3v.1c0 .1.1.4.2.7.4 1.6 1.4 5.1-.4 7.3-.3.4-.2 1-.1 1.4.3 1 .8 2.7 1.5 3.7 4.5 6.5 7 16.1 8 31.2.1 1.2-6.9 3.8-7 3.9-3.2.9-6.2 1.4-9.4 1.4m-.4-.5c3.3 0 6.3-.4 9.4-1.4 1.1-.4 6.5-2.7 6.7-3.4-1.1-15-3.5-24.5-8-30.9-.7-1-1.2-2.6-1.6-3.8-.1-.4-.2-1.2.2-1.8 1.6-2 .7-5.3.3-6.9-.1-.4-.2-.6-.2-.8v-.1c-.3-2.8-.2-3.9-.1-4.3-.6 0-1.7-.1-2.1-.1-.2.3-.9 1.4-1.4 2.3-.7 1.3-1.5 2.6-1.8 3 0 0-.1.1-.1.2q-.3.45-.6.6c-.1 0-.2 0-.2-.1-.5-.4-1.8-1.6-3.1-3.2-.6-.8-1-1.2-1.2-2.1-.4.1-1.4.5-1.8.6 0 .4.1 1.9.2 2.7s.1 1.1.1 1.2c-.5 3.5-.6 5.3.7 7.3.5.8.5 1.7 0 2.4-.9 1.3-2.3 3.8-3.5 7.8-.2.8-.5 1.6-.8 2.6-1.8 5.7-4.6 14.2-4.6 24.4 3.7 2.4 8.6 3.7 13.5 3.8'
            />
            <mask
              id='mask1_12360_24579'
              width='12'
              height='12'
              x='20'
              y='4'
              maskUnits='userSpaceOnUse'
              style={{
                maskType: 'luminance',
              }}
            >
              <path
                fill='#fff'
                d='M26.2 15.703a5.5 5.5 0 1 0 0-11 5.5 5.5 0 0 0 0 11'
              />
            </mask>
            <g mask='url(#mask1_12360_24579)'>
              <path
                fill='#fff'
                d='M8.6 25.102s.5 1.2-.1.8c-.7-.6-.9-1.5-1.3-2.3-.4-1-.4-1.9-.4-2.9.1-2.4.7-4.8 1.4-7.1.3-1.1.6-2.2 1.1-3.2.5-1.1 1.1-2 1.5-3.1.1-.4.2-.8.2-1.2 0-6.1-1.5-12.3.5-13.8 1.1-.9 3.6-1.2 4.9-1.9 1.2-.7.9-4.8.9-4.8h4.5s-.4 4.6 1.9 4.7 4.2-.2 5.2 1.3c.9 1.5 6.7 7.6 7.1 8.4s.2 1.3-.5 2.4-6.1 7.9-7.1 8.2c-.8.3-.9-.2-1.5 0q-.6.15-1.5.6s-1 .5-1.2.2l2.4-1.6s-1.2 0-1.7.4c-.4.3-.9.5-1.3.3 0 0 .3-.2 1.3-1 .3-.3.8-.4 1.4-.4 0 0-1.4 0-1.8.3s-.9.8-1.1.5c0 0 .4-.6.8-.9 0 0 .5-.7 1.7-.5l-.7-.2s-.4.1-.6.2c-.7.5-1 .1-1 .1l1.1-.8c.1 0 .3-.1.4-.1l3.1.4 3.8-6.5-6-5.8-5.9 5.2-4.5 2.7-2.1 5.2-2.5 4.1-1 3.7-.9 5.5c.1 2.8 0 2.3-.5 2.9'
              />
              <path
                fill='#80583F'
                d='M8.7 26.202c-.1 0-.2-.1-.4-.2-.6-.5-.9-1.3-1.2-2-.1-.1-.1-.3-.2-.4-.4-1-.4-1.9-.4-3 .1-2.5.7-5.1 1.4-7.2l.1-.5c.3-.9.6-1.9 1-2.8.2-.3.4-.7.6-1.1.3-.6.6-1.3.9-1.9.1-.4.2-.7.2-1.1 0-2-.2-4-.3-5.8-.4-4.1-.6-7.2.8-8.3.7-.5 1.8-.8 2.8-1.1.8-.2 1.6-.5 2.1-.8.8-.5.9-3.1.7-4.6 0-.1 0-.1.1-.2 0-.1.1-.1.2-.1h4.5c.1 0 .1 0 .2.1 0 .1.1.1.1.2 0 0-.2 2.9.8 4 .3.3.6.4.9.4h1.2c1.8 0 3.3 0 4.2 1.4.5.8 2.3 2.9 4 4.8 1.8 2 2.9 3.3 3.1 3.7.5.9.2 1.6-.5 2.6-.5.8-6.1 8-7.2 8.3-.5.2-.8.1-1 0s-.3-.1-.5 0c-.3.1-.6.2-.9.4-.2.1-.3.1-.5.2s-.9.4-1.3.3c-.1 0-.2-.1-.3-.2s0-.2.1-.3l1.6-1c-.2 0-.5.1-.6.2q-.9.6-1.5.3c-.1 0-.1-.1-.1-.2s0-.2.1-.2l.2-.1c-.1 0-.2 0-.3-.1q-.15-.15 0-.3s.4-.5.7-.9c-.3 0-.5-.2-.6-.2 0-.1-.1-.1-.1-.2s0-.1.1-.2l1.1-.8c.1-.1.5-.1.6-.1l2.9.4 3.6-6.2-5.7-5.3-5.7 5.1-4.4 2.6-2 5.1-2.6 4.2-1 3.7-.9 5.4c.1 2.4.1 2.5-.4 2.9l-.1.1c.3.8.1.9 0 1zm8.7-40.5c.1 1 .2 4.1-1 4.8-.6.3-1.4.5-2.2.8-1 .3-2 .6-2.6 1.1-1.2.9-1 4.1-.7 7.7.1 1.8.3 3.8.3 5.8 0 .5-.1.9-.2 1.3-.2.7-.6 1.4-.9 2q-.45.6-.6 1.2-.6 1.35-.9 2.7l-.1.5c-.6 2.1-1.3 4.6-1.4 7.1 0 1.1 0 1.9.4 2.8.1.1.1.3.2.4.1.6.3 1.1.7 1.6 0-.1-.1-.2-.1-.3s0-.2.1-.3.2-.1.2-.2c.3-.2.3-.2.2-2.5l.9-5.5 1-3.8v-.1l2.6-4.2 2.1-5.1c0-.1.1-.1.1-.1l4.5-2.6 5.8-5.2q.15-.15.3 0l6 5.4q.15.15 0 .3l-3.8 6.5c-.1.1-.1.2-.3.2l-3.1-.4h-.2l-.5.4c.1 0 .1-.1.2-.1.2-.1.6-.2.7-.2h.1l.7.2c.1.1.2.2.1.3 0 .1-.2.2-.3.2-1-.1-1.4.4-1.5.4.3-.2 1.1-.3 1.6-.3h.3c.1 0 .2.1.2.2s-.1.2-.2.3h-.3c-.4 0-.8.1-1 .3s-.4.3-.6.5c.1 0 .2-.1.2-.2.6-.4 1.7-.4 1.9-.4.1 0 .2.1.2.2s0 .2-.1.3l-1.5 1c.2-.1.4-.2.6-.2.4-.2.7-.3 1-.4.4-.1.6 0 .8 0 .2.1.4.1.7 0 .9-.3 6.1-6.9 7-8.1.7-1 .8-1.5.5-2.1-.2-.3-1.6-2-3-3.6-1.7-2-3.5-4-4-4.8-.7-1.1-2-1.1-3.7-1.1h-1.2c-.5 0-.9-.2-1.3-.6-1-1.1-1-3.3-.9-4.1z'
              />
            </g>
          </g>
        </g>
        <defs>
          <clipPath id='clip0_12360_24579'>
            <path
              fill='#fff'
              d='M0 0h40v60H0z'
            />
          </clipPath>
        </defs>
      </svg>
    ),
    'Ankle Length': (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='40'
        height='60'
        fill='none'
        viewBox='0 0 40 60'
      >
        <g clipPath='url(#clip0_12360_5503)'>
          <mask
            id='mask0_12360_5503'
            width='40'
            height='61'
            x='0'
            y='0'
            maskUnits='userSpaceOnUse'
            style={{
              maskType: 'luminance',
            }}
          >
            <path
              fill='#fff'
              d='M39.902.102H.102v60h39.8z'
            />
          </mask>
          <g mask='url(#mask0_12360_5503)'>
            <path
              fill='#fff'
              d='M20.002 16.705s0 8.4-.1 9.9-.1 3.5-.6 7.6c-.5 4.8 1 9.7.7 12.8-.5 4 .8 9.7 2.4 9.8 1.5.1-.3-4.5.3-9.5.4-3.4 1.7-9.7 2.4-17.9 0 0 .7-5.1 1.3-7.6.5-1.9.2-6.5.2-6.5z'
            />
            <path
              fill='#80583F'
              d='M22.402 57.103c-.4 0-.7-.2-.9-.5-1.3-1.5-2.1-6.2-1.7-9.6.2-1.4-.1-3.1-.3-5.1-.3-2.4-.7-5.1-.4-7.7.4-3.4.5-5.4.5-6.8v-.7c.1-1.5.1-9.8.1-9.9s.1-.2.2-.2l6.6-1.4h.2c.1 0 .1.1.1.2 0 .2.3 4.6-.2 6.6-.6 2.4-1.3 7.5-1.3 7.5-.4 5.4-1.2 10.1-1.8 13.5-.3 1.7-.5 3.2-.7 4.3-.3 2.4 0 4.7.2 6.3.2 1.7.3 2.8-.1 3.2-.1.2-.3.3-.5.3m-2.2-40.2c0 1.3 0 8.3-.1 9.7v.7c-.1 1.4-.2 3.4-.5 6.8-.3 2.6.1 5.3.4 7.6.2 1.9.5 3.8.3 5.2-.4 3.2.4 7.8 1.6 9.2.1.2.3.3.5.4h.2c.3-.3.1-1.5 0-2.8-.2-1.7-.4-4-.2-6.4.1-1.2.4-2.6.7-4.4.6-3.4 1.3-8.1 1.8-13.5 0-.1.7-5.2 1.3-7.6.4-1.6.3-5.1.2-6.2z'
            />
            <path
              fill='#fff'
              d='M15.3 18.003c.1-1 .4 5.4.8 10.2.2 3.2.8 5.1.2 8.3-1.3 7 .3 9.8.6 14.6.1 2.2 0 3 .2 4.4.3 1.8.4 3.6 1.1 4.2 1.1 1 1-1.5 1.3-5.3.1-1.3.2-2.4.2-3.5 0-3 .6-8.5 1.4-13.1.1-.7 1-3.2 1.1-3.8l1.8-14.6c.1-.1-8.7-1.4-8.7-1.4'
            />
            <path
              fill='#80583F'
              d='M18.602 60.106c-.3 0-.5-.2-.6-.3-.6-.5-.8-1.9-1-3.4 0-.3-.1-.6-.1-1-.1-.8-.1-1.4-.1-2.3 0-.6 0-1.2-.1-2.1-.1-1.5-.3-2.9-.5-4.2-.5-2.8-.9-5.7-.1-10.5.4-2.3.2-4 0-5.9-.1-.7-.2-1.5-.2-2.4-.1-1.6-.2-3.3-.3-4.9-.1-2.2-.3-4.5-.4-5.2-.1-.1-.1-.1-.1-.2 0-.3.2-.3.3-.3s.2 0 .2.1l8.6 1.3c.1 0 .2.1.2.3l-1.8 14.6c-.1.3-.3 1.1-.5 1.8-.3.8-.5 1.7-.6 2-.8 4.5-1.4 10-1.4 13 0 .9-.1 1.9-.2 3v.5c-.1.7-.1 1.4-.1 1.9-.2 2.4-.2 3.5-.8 3.8-.3.4-.4.4-.4.4m-2.9-41.8c.1.7.2 2.1.4 5 .1 1.6.2 3.3.3 4.9.1.9.1 1.6.2 2.4.2 2 .4 3.7 0 6-.9 4.7-.4 7.5.1 10.3.2 1.3.4 2.7.5 4.2.1.9.1 1.6.1 2.2 0 .8 0 1.4.1 2.2 0 .3.1.6.1 1 .2 1.4.4 2.7.8 3.1.2.1.3.2.3.2.3-.1.4-1.8.5-3.3 0-.6.1-1.3.1-2v-.5c.1-1.1.2-2 .2-2.9 0-3.1.6-8.6 1.4-13.1.1-.4.3-1.3.6-2.1.2-.7.5-1.4.5-1.7l1.8-14.3z'
            />
            <path
              fill='#fff'
              d='M9.8 24.803s.3 1.5-.3 1c-.7-.6-1.1-1.4-1.3-2.3-.2-1.1-.2-2.2-.2-3.2.1-2.4.7-4.8 1.4-7.1.3-1.1.6-2.2 1.1-3.2.5-1.1 1.1-2 1.5-3.1.1-.4.2-.8.2-1.2 0-6.1-1.5-12.3.5-13.8 1.1-.9 3.6-1.2 4.9-1.9 1.2-.7.9-4.8.9-4.8H23s-.4 4.6 1.9 4.7 4-.6 4.9.9 6.9 7.9 7.3 8.7.2 1.3-.5 2.4-6.1 7.9-7.1 8.2c-.8.3-.9-.2-1.5 0q-.6.15-1.5.6s-1 .5-1.2.2l2.4-1.6s-1.2 0-1.7.4c-.4.3-.9.5-1.3.3 0 0 .3-.2 1.3-1 .3-.3.8-.4 1.4-.4 0 0-1.4 0-1.8.3s-.9.8-1.1.5c0 0 .4-.6.8-.9 0 0 .5-.7 1.7-.5l-.7-.2s-.4.1-.6.2c-.7.5-1 .1-1 .1l1.1-.8c.1 0 .3-.1.4-.1l3.1.4 3.8-6.5-5.4-5.4-6.3 5.1-4.5 2.7-2.1 5.2-2.6 4.2-1 3.7-.9 5.5c.1 2.6 0 2.2-.5 2.7'
            />
            <path
              fill='#80583F'
              d='M9.701 26.105c-.1 0-.2 0-.4-.2-.7-.6-1.2-1.4-1.3-2.4-.2-1.1-.3-2.2-.2-3.2.1-2.5.7-5.1 1.4-7.2l.1-.5c.3-.9.6-1.9 1-2.8l.6-1.2c.3-.6.6-1.3.9-1.9.1-.4.2-.7.2-1.1 0-2-.2-4-.3-5.8-.3-4-.6-7.1.9-8.2.7-.5 1.8-.8 2.8-1.1.8-.2 1.6-.5 2.1-.8.8-.5.9-3.1.7-4.6 0-.1 0-.1.1-.2 0-.1.1-.1.2-.1H23c.1 0 .1 0 .2.1 0 .1.1.1.1.2 0 0-.2 2.9.8 4 .3.3.6.4.9.4h1.7c1.5-.1 2.7-.1 3.4 1.1.5.8 2.4 2.9 4.1 4.9 1.9 2.1 3 3.5 3.2 3.9.3.9.1 1.6-.6 2.6-.5.8-6.1 8-7.2 8.3-.5.2-.8.1-1 0s-.3-.1-.5 0c-.3.1-.6.2-.9.4-.2.1-.3.1-.5.2s-.9.4-1.3.3c-.1 0-.2-.1-.3-.2s0-.2.1-.3l1.6-1c-.2 0-.5.1-.6.2q-.9.6-1.5.3c-.1 0-.1-.1-.1-.2s0-.2.1-.2l.2-.1c-.1 0-.2 0-.3-.1q-.15-.15 0-.3s.4-.5.7-.9c-.3 0-.5-.2-.6-.2 0-.1-.1-.1-.1-.2s0-.1.1-.2l1.1-.8c.1-.1.5-.1.6-.1l2.9.4 3.6-6.2-5.1-5.1-6.2 5-4.4 2.6-2 5.1-2.6 4.2-1 3.7-.9 5.4c.1 2.4.1 2.5-.4 2.9 0 0-.1 0-.1.1.1.6.1 1-.2 1.2-.2-.1-.2-.1-.3-.1m8.9-40.7c.1 1 .2 4.1-1 4.8-.6.3-1.4.5-2.2.8-1 .3-2 .6-2.6 1.1-1.2.9-1 4.1-.7 7.7.1 1.8.3 3.8.3 5.8 0 .5-.1.9-.2 1.3-.2.7-.6 1.4-.9 2l-.6 1.2q-.6 1.35-.9 2.7l-.1.5c-.6 2.1-1.3 4.6-1.4 7.1 0 1 0 2.1.2 3.1.1.8.5 1.5 1.1 2 0-.2 0-.5-.1-.7 0-.1 0-.2.1-.2.1-.1.2-.1.2-.2.3-.2.3-.2.2-2.5l.9-5.5 1-3.8v-.1l2.6-4.2 2.1-5.1c0-.1.1-.1.1-.1l4.5-2.7 6.3-5.1q.15-.15.3 0l5.4 5.4q.15.15 0 .3l-3.8 6.5c-.1.1-.1.1-.2.1l-3.1-.4h-.2l-.5.4c.1 0 .1-.1.2-.1.2-.1.6-.2.7-.2h.1l.7.2c.1 0 .2.2.2.3s-.2.2-.3.2c-1-.1-1.4.4-1.5.4.3-.2 1.1-.3 1.6-.3h.3c.1 0 .2.1.2.2s-.1.2-.2.3h-.3c-.4 0-.8.1-1 .3s-.4.3-.6.5c.1 0 .2-.1.2-.2.7-.2 1.8-.2 1.9-.2s.2.1.2.2 0 .2-.1.3l-1.5 1c.2-.1.4-.2.5-.2.4-.2.7-.3 1-.4.4-.1.6 0 .8 0 .2.1.4.1.7 0 .8-.3 6.1-6.9 7-8.1.7-1 .8-1.5.5-2.1-.2-.3-1.7-2.1-3.1-3.8-1.8-2-3.6-4.1-4.1-4.9-.6-1-1.6-.9-3-.8-.5 0-1.1.1-1.8 0-.5 0-.9-.2-1.3-.6-1-1.1-1-3.3-.9-4.1h-3.9z'
            />
            <path
              fill='#80583F'
              d='M9.801 25.006c-.1 0-.2-.1-.2-.2l-.2-1.3c-.1 0-.1 0-.2-.1-.7-.6-.3-1.5 0-1.9.1-.1.2-.1.3-.1s.2.1.2.2l.4 3.1q-.15.3-.3.3'
            />
            <path
              className='fill-f8f3ef group-[.svg-selected]_fill-BE9983 lg_group-hover_fill-BE9983'
              d='M28.102 5.202c-.1-.2-.3-.4-.4-.7s-.3-1.1-.3-1.1c-.1-.6-.4-3-.2-3.6.4-.9.5-1.9.5-3 0-.7-.4-3.1-.3-5 0-.9.4-2 .4-2-.3-.4-2.8-.1-2.9-.1-.3.1-2.8 4.7-3.6 5.5-.1.1-.5.7-.7.6-.7-.6-2-1.7-3.3-3.2-.7-.8-1-1-1.6-1.9-.2-.4-1.9.5-2 .6-.1.2.3 3.5.3 3.7-.1 4.5.8 4.3 1.5 4.9.1 0-.3 1.4-.3 1.4 0 .3-.3 1.1-.3 1.7 0 .4-.6 1.8-.8 2.2-1.1 1.8-3.1 5.6-3.4 8.3-.5 5.1 2.7 29 2.7 32.7l2.5.1h3.9c1.5-.1 6.1-.5 6.1-.5.2-3.3.7-10.1 1.7-17.1 1.3-9.9 3-20.3.5-23.5'
            />
            <path
              fill='#80583F'
              d='M18.302 46.502h-2.5l-2.5-.1c-.1 0-.2-.1-.2-.3 0-1.4-.5-5.9-1-11.1-.9-8.2-2-18.4-1.7-21.6s3.1-7.9 3.4-8.4c.2-.3.8-1.7.8-2s.1-.8.2-1.2c0-.2.1-.4.1-.5s0-.1.1-.5c.1-.2.2-.6.2-.9-.1 0-.1-.1-.2-.1-.9-.5-1.4-1.1-1.3-4.9 0-.1 0-.5-.1-.9-.2-2.3-.3-2.8-.2-3 .1-.3 1.4-.9 2-.9.3 0 .4.1.5.2.3.6.6.8.9 1.2l.7.7c1.3 1.5 2.6 2.6 3.1 3.1l.3-.3c.1.1.2 0 .2 0 .4-.4 1.2-1.8 2-3.1 1.3-2.1 1.5-2.4 1.7-2.5 0 0 2.7-.3 3.1.2.1.1.1.2 0 .2 0 0-.4 1-.4 1.9 0 1.3.1 2.8.2 3.9q.3.75.3 1.2c0 1.2-.1 2.2-.5 3.1-.2.4.1 2.7.2 3.4 0 0 .2.8.3 1.1 0 .1.1.2.2.4 0 .1.1.1.1.2 2.4 3.1 1 12.7-.3 21.9l-.2 1.7c-1.1 7.6-1.6 14.3-1.7 17.1 0 .1-.1.2-.2.2 0 0-4.6.4-6.1.5-.3.1-.9.1-1.5.1m-4.7-.6 2.2.1h3.9c1.3-.1 4.9-.4 5.9-.5.1-2.9.6-9.5 1.7-16.9l.2-1.7c1.3-9.2 2.6-18.6.4-21.6 0-.1-.1-.1-.1-.2-.1-.2-.3-.4-.3-.6-.1-.3-.3-1.1-.3-1.1-.1-.4-.5-3-.2-3.7.4-.8.5-1.8.5-2.9 0-.2 0-.6-.1-1.1-.1-1.1-.3-2.6-.3-3.9 0-.7.2-1.5.3-1.9-.5-.1-2-.1-2.5 0-.2.2-.9 1.3-1.4 2.3-.8 1.3-1.6 2.7-2.1 3.2 0 0-.1.1-.1.2-.2.2-.4.4-.6.4-.1 0-.2 0-.2-.1-.5-.4-1.9-1.6-3.3-3.2-.2-.3-.4-.5-.6-.7-.3-.3-.6-.6-1-1.3-.2-.1-1.3.4-1.6.6 0 .3.1 1.9.2 2.7.1.6.1.9.1 1-.1 3.8.5 4.1 1.1 4.5.1.1.2.1.3.2.2.1.2.2-.1 1.3 0 .2-.1.3-.1.4s0 .3-.1.6-.2.8-.2 1c0 .5-.7 2-.9 2.3-1.4 2.3-3.1 5.8-3.4 8.2-.3 3.1.8 13.3 1.7 21.5.5 5 1 9.3 1 10.9'
            />
            <mask
              id='mask1_12360_5503'
              width='11'
              height='11'
              x='22'
              y='4'
              maskUnits='userSpaceOnUse'
              style={{
                maskType: 'luminance',
              }}
            >
              <path
                fill='#fff'
                d='M27.502 14.403a5.1 5.1 0 1 0 0-10.2 5.1 5.1 0 0 0 0 10.2'
              />
            </mask>
            <g mask='url(#mask1_12360_5503)'>
              <path
                fill='#fff'
                d='M9.8 24.803c-.1.3.3 1.5-.3 1-.7-.6-1.1-1.4-1.3-2.3-.2-1.1-.2-2.2-.2-3.2.1-2.4.7-4.8 1.4-7.1.3-1.1.6-2.2 1.1-3.2.5-1.1 1.1-2 1.5-3.1.1-.4.2-.8.2-1.2 0-6.1-1.5-12.3.5-13.8 1.1-.9 3.6-1.2 4.9-1.9 1.2-.7.9-4.8.9-4.8H23s-.4 4.6 1.9 4.7 4-.6 4.9.9 6.9 7.9 7.3 8.7.2 1.3-.5 2.4-6.1 7.9-7.1 8.2c-.8.3-.9-.2-1.5 0q-.6.15-1.5.6s-1 .5-1.2.2l2.4-1.6s-1.2 0-1.7.4c-.4.3-.9.5-1.3.3 0 0 .3-.2 1.3-1 .3-.3.8-.4 1.4-.4 0 0-1.4 0-1.8.3s-.9.8-1.1.5c0 0 .4-.6.8-.9 0 0 .5-.7 1.7-.5l-.7-.2s-.4.1-.6.2c-.7.5-1 .1-1 .1l1.1-.8c.1 0 .3-.1.4-.1l3.1.4 3.8-6.5-5.4-5.4-6.3 5.1-4.5 2.7-2.1 5.2-2.6 4.2-1 3.7-.9 5.5c.1 3-.1 1.4-.5 2.7'
              />
              <path
                fill='#80583F'
                d='M9.701 26.105c-.2 0-.3-.1-.4-.2-.7-.6-1.2-1.4-1.3-2.4-.2-1.1-.3-2.2-.2-3.2.1-2.5.7-5.1 1.4-7.2l.1-.5c.3-.9.6-1.9 1-2.8l.6-1.2c.3-.6.6-1.3.9-1.9.1-.4.2-.7.2-1.1 0-2-.2-4-.3-5.8-.3-4-.6-7.1.9-8.2.7-.5 1.8-.8 2.8-1.1.8-.2 1.6-.5 2.1-.8.8-.5.9-3.1.7-4.6 0-.1 0-.1.1-.2 0-.1.1-.1.2-.1H23c.1 0 .1 0 .2.1 0 .1.1.1.1.2 0 0-.2 2.9.8 4 .3.3.6.4.9.4h1.7c1.5-.1 2.7-.1 3.4 1.1.5.8 2.4 2.9 4.1 4.9 1.9 2.1 3 3.5 3.2 3.9.3.9.1 1.6-.6 2.6-.5.8-6.1 8-7.2 8.3-.5.2-.8.1-1 0s-.3-.1-.5 0c-.3.1-.6.2-.9.4-.2.1-.3.1-.5.2s-.9.4-1.3.3c-.1 0-.2-.1-.3-.2s0-.2.1-.3l1.6-1c-.2 0-.5.1-.6.2q-.9.6-1.5.3c-.1 0-.1-.1-.1-.2s0-.2.1-.2l.2-.1c-.1 0-.2 0-.3-.1q-.15-.15 0-.3s.4-.5.7-.9c-.3 0-.5-.2-.6-.2 0-.1-.1-.1-.1-.2s0-.1.1-.2l1.1-.8c.1-.1.5-.1.6-.1l2.9.4 3.6-6.2-5.1-5.1-6.2 5-4.4 2.6-2 5.1-2.6 4.2-1 3.7-.9 5.4c.1 2.1 0 2.2-.2 2.4-.1.1-.2.1-.3.5v.4c0 .4.1.7-.2.8zm8.9-40.7c.1 1 .2 4.1-1 4.8-.6.3-1.4.5-2.2.8-1 .3-2 .6-2.6 1.1-1.2.9-1 4.1-.7 7.7.1 1.8.3 3.8.3 5.8 0 .5-.1.9-.2 1.3-.2.7-.6 1.4-.9 2l-.6 1.2q-.6 1.35-.9 2.7l-.1.5c-.6 2.1-1.3 4.6-1.4 7.1 0 1 0 2.1.2 3.1.1.8.5 1.5 1 2v-.1c0-.3-.1-.5 0-.7.2-.5.3-.6.4-.7s.1-.2.1-2.1l.9-5.5 1-3.8v-.1l2.6-4.2 2.1-5.1c0-.1.1-.1.1-.1l4.5-2.7 6.3-5.1q.15-.15.3 0l5.4 5.4q.15.15 0 .3l-3.8 6.5c-.1.1-.1.1-.2.1l-3.1-.4h-.2l-.5.4c.1 0 .1-.1.2-.1.2-.1.6-.2.7-.2h.1l.7.2c.1 0 .2.2.2.3s-.2.2-.3.2c-1-.1-1.4.4-1.5.4.3-.2 1.1-.3 1.6-.3h.3c.1 0 .2.1.2.2s-.1.2-.2.3h-.3c-.4 0-.8.1-1 .3s-.4.3-.6.5c.1 0 .2-.1.2-.2.7-.2 1.8-.2 1.9-.2s.2.1.2.2 0 .2-.1.3l-1.5 1c.2-.1.4-.2.5-.2.4-.2.7-.3 1-.4.4-.1.6 0 .8 0 .2.1.4.1.7 0 .8-.3 6.1-6.9 7-8.1.7-1 .8-1.5.5-2.1-.2-.3-1.7-2.1-3.1-3.8-1.8-2-3.6-4.1-4.1-4.9-.6-1-1.6-.9-3-.8-.5 0-1.1.1-1.8 0-.5 0-.9-.2-1.3-.6-1-1.1-1-3.3-.9-4.1h-3.9z'
              />
            </g>
          </g>
        </g>
        <defs>
          <clipPath id='clip0_12360_5503'>
            <path
              fill='#fff'
              d='M0 0h40v60H0z'
            />
          </clipPath>
        </defs>
      </svg>
    ),
    'Floor Length': (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='40'
        height='60'
        fill='none'
        viewBox='0 0 40 60'
      >
        <g clipPath='url(#clip0_12360_16755)'>
          <mask
            id='mask0_12360_16755'
            width='40'
            height='61'
            x='0'
            y='0'
            maskUnits='userSpaceOnUse'
            style={{
              maskType: 'luminance',
            }}
          >
            <path
              fill='#fff'
              d='M39.902.102H.102v60h39.8z'
            />
          </mask>
          <g mask='url(#mask0_12360_16755)'>
            <path
              fill='#fff'
              d='M32.398-6.703c0 .3.2 4.8.2 6.3 0 .3.1.6.3.8.4.5 1.2 1.4 2.2 2.8 1.4 2.1 2.5 3.6 3.9 5.1 1.5 1.4 0 4.8 0 4.8s-.6.2-.7-1.6-7-9.8-7-9.8l-1.9-8.2h3z'
            />
            <path
              fill='#80583F'
              d='M39.1 13.2c-.1 0-.2 0-.3-.1q-.45-.3-.6-1.8c-.1-1.4-5-7.5-7-9.7 0 0 0-.1-.1-.1l-1.9-8.2v-.2c0-.1.1-.1.2-.1h2.9c.1 0 .2.1.2.2 0 .2.1 3.5.2 6.2 0 .2.1.5.3.6.4.4 1.3 1.4 2.2 2.8l.2.3c1.3 1.9 2.3 3.4 3.7 4.7 1.6 1.5.1 4.9.1 5.1 0 .1-.1.1-.1.1.1.2 0 .2 0 .2M31.6 1.4c.9 1 6.9 8.1 7 9.9 0 .9.2 1.2.3 1.3.3-.7 1.1-3.2 0-4.3-1.4-1.4-2.5-2.9-3.8-4.8l-.1-.2C34 1.9 33.2 1 32.8.5c-.2-.3-.4-.6-.4-1 0-.9-.2-4.9-.2-6h-2.4z'
            />
            <path
              className='fill-f8f3ef group-[.svg-selected]_fill-BE9983 lg_group-hover_fill-BE9983'
              d='m31.4.197-.2-6.4-11.3.5 2.8 7.2c-2 .7-5.3 14.8-5.6 23.4-.8 1.7-1.7 3.1-2.5 4L.2 51.397s6.2 10.4 18.7 6.7c3.5-1.1 5.4 3.1 10 1.3 3.7-1.4 5.4-2.3 5.5-2.4 0 0 2.9-16.8 3-19.3.2-2.5 1.7-25 1.7-26.2 0-1.1-7.3-10-7.7-11.3'
            />
            <path
              fill='#80583F'
              d='M26.7 60.1c-1.4 0-2.6-.5-3.7-1-1.3-.6-2.5-1.2-4-.7C6.4 62.1.1 51.7 0 51.6v-.3l14.4-22.6q1.2-1.2 2.4-3.9c.3-8.1 3.4-21.9 5.5-23.5l-2.7-7v-.2c0-.1.1-.1.2-.1l11.4-.5c.1 0 .1 0 .2.1 0 0 .1.1.1.2l.2 6.3c.2.6 1.8 2.7 3.3 4.7 2.7 3.6 4.5 6 4.5 6.7 0 .9-.9 13.6-1.4 21.2-.2 2.5-.3 4.4-.3 5-.2 2.5-2.9 18.6-3 19.3-.1.4-3.2 1.7-5.7 2.6-1 .4-1.7.5-2.4.5m-6.6-2.4c1.2 0 2.2.5 3.2.9 1.6.7 3.2 1.5 5.6.6 3.5-1.3 5.1-2.1 5.4-2.3.1-.9 2.8-16.8 3-19.2 0-.6.2-2.5.3-5 .5-7.1 1.4-20.2 1.4-21.1 0-.6-2.5-4-4.4-6.4C32.9 2.9 31.5 1 31.2.4V.3L31-5.9l-10.8.4 2.7 6.9v.2c0 .1-.1.1-.1.1-1.7.6-5.1 14.1-5.4 23.2v.1c-.9 1.9-1.7 3.2-2.5 4L.5 51.4c.8 1.2 6.9 9.8 18.3 6.4.5-.1.9-.1 1.3-.1'
            />
            <path
              fill='#80583F'
              d='M25.098 1.802c-.9 0-1.5 0-1.5-.1-.1 0-.2-.1-.2-.3 0-.1.1-.2.3-.2 0 0 5 .4 7.7-1.2.1-.1.3 0 .3.1.1.1 0 .3-.1.3-1.9 1.2-4.8 1.4-6.5 1.4'
            />
            <path
              fill='#fff'
              d='M20.7-7.203c-.1.4.5 6.9.5 6.9s1.1 2 .8 4.7c-.1 1.6-.4 3.9-.6 5.5-.1.9 0 1.8.3 2.6.2.4.6 1.5.6 2.2 0 1.6-.3 1.1-.3 1.1l-.6-2-1 3.1c0 .1-.2.1-.2 0 0-1.1 0-4-.3-5.7-.3-2.2 0-6.1-.5-8.5s-1.6-9.4-1.6-9.4z'
            />
            <path
              fill='#80583F'
              d='M20.2 17.2c-.2 0-.3-.2-.3-.3 0-1.1 0-4-.3-5.7-.1-1-.2-2.3-.2-3.8 0-1.7-.1-3.4-.3-4.7-.4-2.4-1.6-9.3-1.6-9.4v-.2c0-.1.1-.1.2-.1l2.9-.5c.1 0 .2 0 .2.1.1.1.1.1.1.2-.1.4.3 4.3.5 6.8.2.3 1.1 2.2.8 4.8-.1 1.6-.4 3.9-.6 5.5-.1.9 0 1.8.3 2.5.2.4.6 1.6.6 2.3 0 1.1-.1 1.3-.4 1.4-.1 0-.3-.1-.4-.2l-.4-1.3-.8 2.4c0 .1-.1.2-.3.2M18.1-6.5c.2 1.3 1.2 7 1.6 9.1.2 1.3.3 3.1.3 4.8 0 1.4 0 2.7.2 3.7.2 1.3.3 3.4.3 4.8l.7-2.2c0-.1.1-.2.2-.2s.2.1.2.2L22 15v-.3c0-.6-.4-1.7-.5-2.1-.4-.8-.5-1.8-.3-2.8.2-1.6.5-3.9.6-5.5.2-2.5-.8-4.4-.9-4.5v-.1c-.1-1.7-.5-5.4-.5-6.6z'
            />
            <path
              fill='#80583F'
              d='M17.099 25.1c-.2-.1-.3-.2-.2-.3l3.7-9.3c.1-.1.2-.2.3-.1s.2.2.1.3l-3.7 9.3c0 .1-.1.1-.2.1'
            />
          </g>
        </g>
        <defs>
          <clipPath id='clip0_12360_16755'>
            <path
              fill='#fff'
              d='M0 0h40v60H0z'
            />
          </clipPath>
        </defs>
      </svg>
    ),
    'Hi Lo': (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='40'
        height='60'
        fill='none'
        viewBox='0 0 40 60'
      >
        <g clipPath='url(#clip0_12360_16640)'>
          <mask
            id='mask0_12360_16640'
            width='40'
            height='61'
            x='0'
            y='0'
            maskUnits='userSpaceOnUse'
            style={{
              maskType: 'luminance',
            }}
          >
            <path
              fill='#fff'
              d='M39.902.102H.102v60h39.8z'
            />
          </mask>
          <g mask='url(#mask0_12360_16640)'>
            <path
              fill='#F8F3EF'
              d='M6.902 47.905s3.7 3.6 13.5 3.5c5 0 16.2-3.5 16.2-3.5l-13.6-18.1-11.6 7.7z'
            />
            <path
              fill='#80583F'
              d='M20.203 51.704c-9.6 0-13.3-3.4-13.4-3.6-.1-.1-.1-.2-.1-.3l4.6-10.4s0-.1.1-.1l11.6-7.7c.1-.1.3 0 .3.1l13.6 18.1c0 .1.1.1 0 .2 0 .1-.1.1-.2.2-.5.1-11.3 3.5-16.3 3.5zm-13-3.9c.7.6 4.6 3.3 13 3.3h.2c4.4 0 13.9-2.8 15.8-3.4l-13.2-17.6-11.3 7.5z'
            />
            <path
              fill='#fff'
              d='M20.303 16.705s0 8.4-.1 9.9-.1 3.5-.6 7.6c-.5 4.8 1 9.7.7 12.8-.5 4 .8 9.7 2.4 9.8 1.5.1-.3-4.5.3-9.5.4-3.4 1.7-9.7 2.4-17.9 0 0 .7-5.1 1.3-7.6.5-1.9.2-6.5.2-6.5z'
            />
            <path
              fill='#80583F'
              d='M22.702 57.103c-.4 0-.7-.2-.9-.5-1.3-1.6-2.1-6.3-1.8-9.6.2-1.4-.1-3.1-.3-5.1-.3-2.4-.7-5.1-.4-7.7.4-3.4.5-5.4.5-6.8v-.7c.1-1.5.1-9.8.1-9.9s.1-.2.2-.2l6.6-1.4h.2c.1 0 .1.1.1.2 0 .2.3 4.6-.2 6.6-.6 2.4-1.3 7.5-1.3 7.5-.4 5.4-1.2 10.1-1.8 13.5-.3 1.7-.5 3.2-.7 4.3-.3 2.4 0 4.7.2 6.3.2 1.7.3 2.8-.1 3.2 0 .2-.1.3-.4.3m-2.2-40.2c0 1.3 0 8.3-.1 9.7v.7c-.1 1.4-.2 3.4-.5 6.8-.3 2.6.1 5.3.4 7.6.2 1.9.5 3.8.3 5.2-.4 3.2.4 7.8 1.6 9.2.1.2.3.3.5.4h.2c.3-.3.1-1.5 0-2.8-.2-1.7-.4-4-.2-6.4.1-1.2.4-2.6.7-4.4.6-3.4 1.3-8.1 1.8-13.5 0-.1.7-5.2 1.3-7.6.4-1.6.3-5.1.2-6.2z'
            />
            <path
              fill='#fff'
              d='M15.602 18.003c.1-1 .4 5.4.8 10.2.2 3.2.8 5.1.2 8.3-1.3 7 .3 9.8.6 14.6.1 2.2 0 3 .2 4.4.3 1.8.4 3.6 1.1 4.2 1.1 1 1-1.5 1.3-5.3.1-1.3.2-2.4.2-3.5 0-3 .6-8.5 1.4-13.1.1-.7 1-3.2 1.1-3.8l1.8-14.6c.1-.1-8.7-1.4-8.7-1.4'
            />
            <path
              fill='#80583F'
              d='M18.9 60.102c-.3 0-.5-.2-.6-.3-.6-.5-.8-1.8-1-3.4 0-.3-.1-.6-.1-1-.2-.7-.2-1.3-.2-2.2 0-.6 0-1.2-.1-2.1-.1-1.5-.3-2.9-.5-4.2-.5-2.8-.9-5.7-.1-10.5.4-2.3.2-4 0-5.9-.1-.7-.2-1.5-.2-2.4-.1-1.5-.2-3.3-.3-4.8-.1-2.2-.3-4.5-.4-5.2-.1-.1-.1-.1-.1-.2 0-.3.2-.3.3-.3s.2 0 .2.1l8.6 1.3c.1 0 .2.1.2.3l-1.8 14.6c-.1.3-.3 1.1-.5 1.8-.3.8-.5 1.7-.6 2-.8 4.5-1.4 10-1.4 13 0 .9-.1 1.9-.2 3v.5c-.1.7-.1 1.4-.1 2-.2 2.4-.2 3.5-.8 3.8-.2.1-.3.1-.3.1m-2.9-41.8c.1.7.2 2.1.4 5 .1 1.6.2 3.3.3 4.8.1.9.1 1.6.2 2.4.2 2 .4 3.7 0 6-.9 4.7-.4 7.5.1 10.3.2 1.3.4 2.7.5 4.2 0 .9.1 1.6.1 2.2 0 .8 0 1.4.1 2.2 0 .3.1.7.1 1 .2 1.4.4 2.6.8 3.1.2.1.3.2.3.2.3-.1.4-1.8.5-3.3 0-.6.1-1.3.1-2v-.5c.1-1.1.2-2 .2-2.9 0-3.1.6-8.6 1.4-13.1.1-.4.3-1.3.6-2.1.2-.7.5-1.4.5-1.7l1.8-14.3z'
            />
            <path
              fill='#fff'
              d='M10.1 24.803s.3 1.5-.299 1c-.7-.6-1.1-1.4-1.3-2.3-.2-1-.3-2.1-.2-3.2.1-2.4.7-4.8 1.4-7.1.3-1.1.6-2.2 1.1-3.2.5-1.1 1.1-2 1.5-3.1.1-.4.2-.8.2-1.2 0-6.1-1.5-12.3.5-13.8 1.1-.9 3.6-1.2 4.9-1.9 1.2-.7.9-4.8.9-4.8h4.5s-.4 4.6 1.9 4.7 4-.6 4.9.9 6.9 7.9 7.3 8.7.2 1.3-.5 2.4-6.1 7.9-7.1 8.2c-.8.3-.9-.2-1.5 0q-.6.15-1.5.6s-1 .5-1.2.2l2.4-1.6s-1.2 0-1.7.4c-.4.3-.9.5-1.3.3 0 0 .3-.2 1.3-1 .3-.3.8-.4 1.4-.4 0 0-1.4 0-1.8.3s-.9.8-1.1.5c0 0 .4-.6.8-.9 0 0 .5-.7 1.7-.5l-.7-.2s-.4.1-.6.2c-.7.5-1 .1-1 .1l1.1-.8c.1 0 .3-.1.4-.1l3.1.4 3.8-6.5-5.4-5.6-6.3 5.1-4.5 2.7-2.1 5.2-2.6 4.2-1 3.7-.9 5.5c.1 2.8 0 2.4-.5 2.9'
            />
            <path
              fill='#80583F'
              d='M10.002 26.105c-.1 0-.2 0-.4-.2-.7-.6-1.2-1.4-1.3-2.4-.2-1.1-.3-2.2-.2-3.2.1-2.5.7-5.1 1.4-7.2l.1-.5c.3-.9.6-1.9 1-2.8l.6-1.2c.3-.6.6-1.3.9-1.9.1-.4.2-.7.2-1.1 0-2-.2-4-.3-5.8-.3-4-.6-7.1.9-8.2.7-.5 1.8-.8 2.8-1.1.8-.2 1.6-.5 2.1-.8.8-.5.9-3.1.7-4.6 0-.1 0-.1.1-.2 0-.1.1-.1.2-.1h4.5c.1 0 .1 0 .2.1 0 .1.1.1.1.2 0 0-.2 2.9.8 4 .3.3.6.4.9.4h1.7c1.5-.1 2.7-.1 3.4 1.1.5.8 2.4 2.9 4.1 4.9 1.9 2.1 3 3.5 3.2 3.9.3.9.1 1.6-.6 2.6-.5.8-6.1 8-7.2 8.3-.5.2-.8.1-1 0s-.3-.1-.5 0c-.3.1-.6.2-.9.4-.2.1-.3.1-.5.2s-.9.4-1.3.3c-.1 0-.2-.1-.3-.2s0-.2.1-.3l1.6-1c-.2 0-.5.1-.6.2q-.9.6-1.5.3c-.1 0-.1-.1-.1-.2s0-.2.1-.2l.2-.1c-.1 0-.2 0-.3-.1q-.15-.15 0-.3s.4-.5.7-.9c-.3 0-.5-.2-.6-.2 0-.1-.1-.1-.1-.2s0-.1.1-.2l1-.9c.1-.1.5-.1.6-.1l2.9.4 3.6-6.2-5.1-5.1-6.2 5-4.4 2.6-2 5.1-2.6 4.2-1 3.7-.9 5.4c.1 2.4.1 2.5-.4 2.9 0 0-.1 0-.1.1.1.6.1 1-.2 1.2zm8.9-40.7c.1 1 .2 4.1-1 4.8-.6.3-1.4.6-2.2.8-1 .3-2 .6-2.6 1.1-1.2.9-1 4.1-.7 7.7.1 1.8.3 3.8.3 5.8 0 .5-.1.9-.2 1.3-.2.7-.6 1.4-.9 2l-.6 1.2q-.6 1.35-.9 2.7l-.1.5c-.6 2.1-1.3 4.6-1.4 7.1 0 1 0 2.1.2 3.1.1.9.5 1.6 1.1 2.1 0-.2 0-.5-.1-.7 0-.1 0-.2.1-.2.1-.1.2-.1.2-.2.3-.2.3-.2.2-2.5l.9-5.5 1-3.8v-.1l2.6-4.2 2.1-5.1c.1-.1.1-.1.1-.2l4.5-2.7 6.3-5.1q.15-.15.3 0l5.4 5.4q.15.15 0 .3l-3.8 6.5c0 .1-.1.1-.2.1l-3.1-.4h-.2l-.5.4c.1 0 .1-.1.2-.1.2-.1.6-.2.7-.2h.1l.7.2c.1 0 .2.2.2.3s-.1.2-.3.2c-1-.1-1.4.4-1.5.4.3-.2 1.1-.3 1.6-.3h.3c.1 0 .2.1.2.2s-.1.2-.2.3h-.3c-.4 0-.8.1-1 .3s-.4.3-.6.5c.1 0 .2-.1.2-.2.7-.2 1.8-.2 1.9-.2s.2.1.2.2 0 .2-.1.3l-1.5 1c.2-.1.4-.2.5-.2.4-.2.7-.3 1-.4.4-.1.6 0 .8 0 .2.1.4.1.7 0 .8-.3 6.1-6.9 7-8.1.7-1 .8-1.5.5-2.1 0-.4-1.6-2.2-3-3.8-1.8-2-3.6-4.1-4.1-4.9-.6-1-1.6-.9-3-.8-.5 0-1.1.1-1.8 0-.5 0-.9-.2-1.3-.6-1-1.1-1-3.3-.9-4.1h-4z'
            />
            <path
              fill='#80583F'
              d='M10.102 25.006c-.1 0-.2-.1-.2-.2l-.2-1.3c-.1 0-.1 0-.2-.1-.7-.6-.3-1.5 0-1.9.1-.1.2-.1.3-.1s.2.1.2.2l.4 3.1q-.15.3-.3.3'
            />
            <path
              className='fill-f8f3ef group-[.svg-selected]_fill-BE9983 lg_group-hover_fill-BE9983'
              d='M20.302 34.999s-1.2 6.6-6.1 7.6c-3.4.7-7.3 5.4-7.3 5.4 2.6-9.9 3.6-29.3 4.1-34.4.3-2.7 2.3-6.5 3.4-8.3.2-.3.8-1.8.8-2.2 0-.5.3-1.4.3-1.7 0-.1.4-1.4.3-1.4-.8-.5-1.7-.4-1.5-4.9 0-.2-.7-3.6-.4-3.8.6-.4 2.1-1 2.3-.7.6.7.8 1.2 1.5 2 1.3 1.5 2.6 2.6 3.3 3.2.2.1.6-.5.7-.6.8-.8 3.5-5.7 3.9-5.8.1 0 2.5-.1 2.6.2.1.4-.4 1.3-.4 2.2 0 1.9.3 4.3.3 5 0 1.2-.1 2.2-.5 3-.2.5.1 3 .2 3.6 0 0 .2.8.3 1.1 0 .2.2.4.4.7 2.4 3.2 1 12.4-.4 22.2-1 7.1 2.9 11.9 8.5 20.5l-11.4-5.6c-1.7-.9-3.1-2.2-4-3.8-.5-1.1-.9-2.2-.9-3.5'
            />
            <path
              fill='#80583F'
              d='M36.602 48.202h-.1l-11.4-5.6c-1.8-.9-3.2-2.3-4.1-3.9-.4-.7-.7-1.5-.8-2.2-.6 2-2.3 5.7-5.9 6.5-3.3.7-7.1 5.2-7.2 5.3s-.2.1-.3.1c-.1-.1-.2-.2-.1-.3 2.1-8 3.1-22.4 3.7-30.1.1-1.8.2-3.3.3-4.3.3-3.2 3.1-7.9 3.4-8.4.2-.3.8-1.7.8-2s.1-.8.2-1.2c0-.2.1-.4.1-.5s0-.1.1-.5c.1-.2.2-.6.2-.9-.1 0-.1-.1-.2-.1-.8-.7-1.4-1.3-1.3-5.1 0-.1-.1-.5-.1-.8-.5-2.7-.4-3-.2-3.1s2.2-1.2 2.6-.7c.4.4.6.7.8 1.1.2.3.4.6.7 1 1.3 1.4 2.6 2.5 3.2 3l.3-.3c0-.1.1-.2.1-.2.4-.4 1.3-1.9 2.1-3.2 1.5-2.4 1.7-2.6 1.9-2.7 0 0 2.7-.2 2.9.3.1.3 0 .6-.1 1s-.2.8-.2 1.3c0 1.3.1 2.8.2 3.9 0 .5.1.9.1 1.1 0 1.2-.1 2.2-.5 3.1-.2.4.1 2.7.2 3.4 0 0 .2.8.3 1.1 0 .1.1.2.2.4 0 .1.1.1.1.2 2.4 3.1 1.2 11.3-.1 20.7l-.2 1.7c-.9 6.2 2.1 10.7 6.6 17.5.6.9 1.2 1.9 1.9 2.9q.15.15 0 .3c-.1.1-.1.2-.2.2m-16.3-13.5c.2 0 .2.1.2.3 0 1.1.3 2.2.9 3.4.8 1.5 2.2 2.8 3.9 3.7l10.6 5.2c-.5-.7-.9-1.4-1.4-2.1-4.6-6.9-7.6-11.5-6.7-17.8l.2-1.7c1.3-8.9 2.5-17.4.2-20.3 0-.1-.1-.1-.1-.2-.1-.2-.3-.4-.3-.6-.1-.3-.3-1.1-.3-1.1-.1-.5-.5-3.1-.2-3.8.4-.8.5-1.8.5-2.9 0-.2 0-.6-.1-1.1-.1-1.1-.3-2.6-.3-3.9 0-.5.1-1 .2-1.4.1-.3.1-.5.1-.7-.4-.1-1.9-.1-2.3-.1-.2.2-1 1.4-1.6 2.4-.9 1.4-1.8 2.9-2.2 3.3 0 0-.1.1-.1.2-.1.3-.3.5-.5.5-.1 0-.2 0-.2-.1-.5-.4-1.9-1.6-3.3-3.2-.3-.4-.5-.7-.7-1s-.4-.6-.7-1c-.3 0-1.3.3-1.9.7 0 .4.2 2 .3 2.7.1.6.1.9.1.9-.1 3.8.5 4.1 1.1 4.5.1.1.2.1.3.2.2.1.2.2-.1 1.3 0 .2-.1.3-.1.4s0 .3-.1.6-.2.8-.2 1c0 .5-.7 2-.9 2.3-1.4 2.3-3.1 5.8-3.4 8.2-.1 1-.2 2.4-.3 4.3-.6 7.4-1.6 21-3.5 29.2 1.3-1.4 4.1-4.1 6.8-4.7 4.7-1 5.9-7.3 5.9-7.4-.1-.1 0-.2.2-.2'
            />
            <mask
              id='mask1_12360_16640'
              width='11'
              height='11'
              x='22'
              y='4'
              maskUnits='userSpaceOnUse'
              style={{
                maskType: 'luminance',
              }}
            >
              <path
                fill='#fff'
                d='M27.803 14.403a5.1 5.1 0 1 0 0-10.2 5.1 5.1 0 0 0 0 10.2'
              />
            </mask>
            <g mask='url(#mask1_12360_16640)'>
              <path
                fill='#fff'
                d='M10.1 24.803c-.1.3.3 1.5-.299 1-.7-.6-1.1-1.4-1.3-2.3-.2-1-.3-2.1-.2-3.2.1-2.4.7-4.8 1.4-7.1.3-1.1.6-2.2 1.1-3.2.5-1.1 1.1-2 1.5-3.1.1-.4.2-.8.2-1.2 0-6.1-1.5-12.3.5-13.8 1.1-.9 3.6-1.2 4.9-1.9 1.2-.7.9-4.8.9-4.8h4.5s-.4 4.6 1.9 4.7 4-.6 4.9.9 6.9 7.9 7.3 8.7.2 1.3-.5 2.4-6.1 7.9-7.1 8.2c-.8.3-.9-.2-1.5 0q-.6.15-1.5.6s-1 .5-1.2.2l2.4-1.6s-1.2 0-1.7.4c-.4.3-.9.5-1.3.3 0 0 .3-.2 1.3-1 .3-.3.8-.4 1.4-.4 0 0-1.4 0-1.8.3s-.9.8-1.1.5c0 0 .4-.6.8-.9 0 0 .5-.7 1.7-.5l-.7-.2s-.4.1-.6.2c-.7.5-1 .1-1 .1l1.1-.8c.1 0 .3-.1.4-.1l3.1.4 3.8-6.5-5.4-5.6-6.3 5.1-4.5 2.7-2.1 5.2-2.6 4.2-1 3.7-.9 5.5c.1 3.2-.1 1.6-.5 2.9'
              />
              <path
                fill='#80583F'
                d='M10.002 26.105c-.2 0-.3-.1-.4-.2-.7-.6-1.2-1.4-1.3-2.4-.2-1.1-.3-2.2-.2-3.2.1-2.5.7-5.1 1.4-7.2l.1-.5c.3-.9.6-1.9 1-2.8l.6-1.2c.3-.6.6-1.3.9-1.9.1-.4.2-.7.2-1.1 0-2-.2-4-.3-5.8-.3-4-.6-7.1.9-8.2.7-.5 1.8-.8 2.8-1.1.8-.2 1.6-.5 2.1-.8.8-.5.9-3.1.7-4.6 0-.1 0-.1.1-.2 0-.1.1-.1.2-.1h4.5c.1 0 .1 0 .2.1 0 .1.1.1.1.2 0 0-.2 2.9.8 4 .3.3.6.4.9.4h1.7c1.5-.1 2.7-.1 3.4 1.1.5.8 2.4 2.9 4.1 4.9 1.9 2.1 3 3.5 3.2 3.9.3.9.1 1.6-.6 2.6-.5.8-6.1 8-7.2 8.3-.5.2-.8.1-1 0s-.3-.1-.5 0c-.3.1-.6.2-.9.4-.2.1-.3.1-.5.2s-.9.4-1.3.3c-.1 0-.2-.1-.3-.2s0-.2.1-.3l1.6-1c-.2 0-.5.1-.6.2q-.9.6-1.5.3c-.1 0-.1-.1-.1-.2s0-.2.1-.2l.2-.1c-.1 0-.2 0-.3-.1q-.15-.15 0-.3s.4-.5.7-.9c-.3 0-.5-.2-.6-.2 0-.1-.1-.1-.1-.2s0-.1.1-.2l1-.9c.1-.1.5-.1.6-.1l2.9.4 3.6-6.2-5.1-5.1-6.2 5-4.4 2.6-2 5.1-2.6 4.2-1 3.7-.9 5.4c.1 2.1 0 2.2-.2 2.4-.1.1-.2.1-.3.5v.4c0 .4.1.7-.2.8-.1.1-.1.1-.2.1m8.9-40.7c.1 1 .2 4.1-1 4.8-.6.3-1.4.6-2.2.8-1 .3-2 .6-2.6 1.1-1.2.9-1 4.1-.7 7.7.1 1.8.3 3.8.3 5.8 0 .5-.1.9-.2 1.3-.2.7-.6 1.4-.9 2l-.6 1.2q-.6 1.35-.9 2.7l-.1.5c-.6 2.1-1.3 4.6-1.4 7.1 0 1 0 2.1.2 3.1.1.8.5 1.6 1.1 2.1v-.1c0-.3-.1-.5 0-.7.2-.5.3-.6.4-.7s.1-.2.1-2.1l.9-5.5 1-3.8v-.1l2.6-4.2 2.1-5.1v-.2l4.5-2.7 6.3-5.1q.15-.15.3 0l5.4 5.4q.15.15 0 .3l-3.8 6.5c0 .1-.1.1-.2.1l-3.1-.4h-.2l-.5.4c.1 0 .1-.1.2-.1.2-.1.6-.2.7-.2h.1l.7.2c.1 0 .2.2.2.3s-.1.2-.3.2c-1-.1-1.4.4-1.5.4.3-.2 1.1-.3 1.6-.3h.3c.1 0 .2.1.2.2s-.1.2-.2.3h-.3c-.4 0-.8.1-1 .3s-.4.3-.6.5c.1 0 .2-.1.2-.2.7-.2 1.8-.2 1.9-.2s.2.1.2.2 0 .2-.1.3l-1.5 1c.2-.1.4-.2.5-.2.4-.2.7-.3 1-.4.4-.1.6 0 .8 0 .2.1.4.1.7 0 .8-.3 6.1-6.9 7-8.1.7-1 .8-1.5.5-2.1 0-.4-1.6-2.2-3-3.8-1.8-2-3.6-4.1-4.1-4.9-.6-1-1.6-.9-3-.8-.5 0-1.1.1-1.8 0-.5 0-.9-.2-1.3-.6-1-1.1-1-3.3-.9-4.1h-4z'
              />
            </g>
            <path
              fill='#F8F3EF'
              d='M15.203 3.007s3.5-2 12.4-1z'
            />
            <path
              fill='#80583F'
              d='M15.202 3.307c-.1 0-.2 0-.2-.1-.1-.1 0-.3.1-.3.1-.1 3.7-2.1 12.5-1 .1 0 .2.1.2.3 0 .1-.1.2-.3.2-8.7-1-12.2.9-12.2 1 0-.1-.1-.1-.1-.1'
            />
          </g>
        </g>
        <defs>
          <clipPath id='clip0_12360_16640'>
            <path
              fill='#fff'
              d='M0 0h40v60H0z'
            />
          </clipPath>
        </defs>
      </svg>
    ),
    'Tea Length': (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='40'
        height='60'
        fill='none'
        viewBox='0 0 40 60'
      >
        <mask
          id='mask0_13014_143868'
          width='40'
          height='61'
          x='0'
          y='0'
          maskUnits='userSpaceOnUse'
          style={{
            maskType: 'luminance',
          }}
        >
          <path
            fill='#fff'
            d='M40 .273H0v60h40z'
          />
        </mask>
        <g mask='url(#mask0_13014_143868)'>
          <path
            fill='#F8F3EF'
            d='M19.956 16.81s-.02 8.36-.11 9.93-.151 3.55-.573 7.56c-.503 4.8 1.055 9.7.693 12.82-.452 4.02.854 9.7 2.382 9.82 1.467.11-.332-4.45.261-9.53.392-3.44 1.76-9.69 2.433-17.85 0 0 .693-5.1 1.306-7.57.493-1.95.191-6.53.191-6.53z'
          />
          <path
            fill='#80583F'
            d='M22.411 57.19h-.07c-.312-.02-.603-.21-.885-.54-1.276-1.53-2.11-6.17-1.728-9.55.16-1.38-.06-3.09-.322-5.07-.311-2.38-.653-5.08-.372-7.75.362-3.41.463-5.37.533-6.81l.04-.74c.09-1.55.11-9.83.11-9.92 0-.12.081-.22.202-.24l6.583-1.36c.07-.02.14 0 .2.05.06.04.091.11.101.18.01.19.302 4.64-.19 6.61-.614 2.43-1.297 7.49-1.307 7.54-.443 5.44-1.216 10.1-1.77 13.51-.28 1.71-.522 3.2-.652 4.34-.272 2.36-.02 4.65.17 6.33.181 1.67.302 2.77-.12 3.22a.8.8 0 0 1-.523.2m-2.2-40.18c0 1.31-.031 8.3-.111 9.74l-.04.74c-.07 1.44-.171 3.42-.533 6.84-.272 2.61.07 5.27.372 7.63.25 1.93.482 3.75.321 5.19-.362 3.21.432 7.76 1.618 9.18.131.16.332.35.533.36.1.01.14-.02.17-.05.272-.28.131-1.51-.01-2.82-.19-1.7-.441-4.03-.17-6.44.13-1.15.382-2.64.663-4.36.553-3.4 1.317-8.05 1.77-13.48.01-.06.703-5.15 1.316-7.61.402-1.61.25-5.14.19-6.17z'
          />
          <path
            fill='#F8F3EF'
            d='M15.285 18.083c.12-.97.432 5.43.774 10.2.231 3.21.794 5.12.201 8.32-1.297 7.01.332 9.8.593 14.6.12 2.24 0 2.99.211 4.38.271 1.78.382 3.6 1.055 4.18 1.136.97 1.026-1.48 1.327-5.32.1-1.29.211-2.43.211-3.48 0-2.99.583-8.45 1.447-13.08.141-.73 1.015-3.18 1.146-3.84l1.86-14.59c-.01 0-8.825-1.37-8.825-1.37'
          />
          <path
            fill='#80583F'
            d='M18.552 60.232c-.271 0-.503-.2-.593-.27-.623-.53-.804-1.85-1.005-3.37-.04-.32-.09-.64-.13-.96-.131-.84-.131-1.44-.151-2.27-.01-.57-.02-1.22-.07-2.13-.081-1.52-.312-2.86-.523-4.16-.473-2.8-.955-5.69-.06-10.5.422-2.3.24-3.96.02-5.88-.08-.74-.171-1.5-.232-2.38-.11-1.55-.22-3.28-.311-4.87-.14-2.2-.282-4.47-.362-5.16a.26.26 0 0 1-.09-.22c.03-.27.18-.31.28-.32.091 0 .152 0 .212.13l8.613 1.34c.13.02.231.14.211.28l-1.86 14.59c-.07.34-.3 1.06-.542 1.82-.261.82-.533 1.67-.603 2.04-.834 4.47-1.437 9.95-1.437 13.03 0 .92-.08 1.91-.16 2.96l-.041.55c-.05.7-.1 1.35-.13 1.95-.152 2.45-.242 3.54-.805 3.77-.1.02-.17.03-.231.03m-2.915-41.84c.09.69.171 2.1.352 5.01.1 1.59.201 3.31.312 4.87.06.86.14 1.62.23 2.36.222 1.96.403 3.65-.03 6.02-.874 4.73-.411 7.45.071 10.33.221 1.31.442 2.67.533 4.21.05.92.06 1.6.07 2.15.01.81.02 1.4.14 2.2.051.32.091.65.131.97.181 1.36.352 2.65.835 3.06.17.14.261.16.281.15.302-.13.402-1.76.503-3.35.04-.6.08-1.25.13-1.96l.04-.55c.09-1.09.161-2.02.161-2.92 0-3.11.613-8.63 1.447-13.13.07-.4.352-1.26.624-2.1.23-.71.462-1.45.522-1.75l1.82-14.33z'
          />
          <path
            fill='#F8F3EF'
            d='M9.713 24.888s.302 1.47-.301.98c-.704-.57-1.116-1.41-1.256-2.29-.181-1.05-.252-2.12-.211-3.18.08-2.42.683-4.8 1.376-7.11.322-1.07.624-2.17 1.086-3.19.472-1.05 1.075-2.04 1.467-3.13.14-.4.211-.8.211-1.22 0-6.14-1.497-12.29.483-13.79 1.135-.86 3.668-1.18 4.914-1.88 1.247-.7.865-4.82.865-4.82h4.542s-.392 4.65 1.96 4.73c2.352.09 4.03-.56 4.965.93s6.925 7.92 7.316 8.71c.392.79.232 1.32-.472 2.37-.703 1.05-6.16 7.92-7.156 8.24-.804.26-.884-.22-1.517-.05-.412.11-.885.34-1.498.59 0 0-1.055.54-1.216.2l2.402-1.58s-1.186-.02-1.718.35c-.412.29-.915.49-1.267.32 0 0 .312-.24 1.337-1.04.342-.27.824-.41 1.437-.41 0 0-1.447.02-1.779.27-.382.29-.874.77-1.115.55 0 0 .432-.56.794-.93 0 0 .522-.67 1.698-.51l-.744-.24s-.392.08-.562.19c-.684.45-1.026.1-1.026.1l1.116-.79c.05-.03.322-.07.382-.07l3.126.37 3.839-6.53-5.468-5.36-6.361 5.14-4.503 2.66-2.09 5.15-2.623 4.18-.985 3.75-.915 5.46c.09 2.81.02 2.38-.533 2.88'
          />
          <path
            fill='#80583F'
            d='M9.626 26.23c-.1 0-.221-.04-.372-.16-.703-.58-1.176-1.44-1.346-2.44-.181-1.08-.252-2.16-.222-3.23.09-2.54.754-5.05 1.387-7.18l.131-.45c.271-.92.553-1.88.955-2.77.18-.4.382-.79.573-1.18.321-.62.643-1.27.884-1.93.13-.36.201-.75.201-1.14 0-1.98-.16-4.02-.301-5.81-.312-3.96-.563-7.08.884-8.18.673-.51 1.759-.83 2.814-1.14.824-.24 1.598-.47 2.13-.77.825-.45.895-3.05.744-4.58 0-.07.02-.14.07-.19.041-.05.111-.08.182-.08h4.542c.07 0 .141.03.181.08.05.05.07.12.07.19 0 .03-.23 2.88.785 4.02.25.29.552.43.924.44.613.02 1.206-.01 1.719-.03 1.497-.08 2.683-.14 3.457 1.08.482.77 2.402 2.95 4.09 4.88 1.88 2.14 3.046 3.47 3.237 3.86.462.93.22 1.56-.483 2.62-.542.81-6.14 7.96-7.286 8.34-.513.17-.794.07-1.025-.01-.171-.06-.282-.1-.503-.04-.271.07-.573.2-.924.35-.171.07-.342.15-.543.23-.181.1-.895.43-1.276.3a.44.44 0 0 1-.262-.22.254.254 0 0 1 .09-.32l1.579-1.04c-.242.05-.463.12-.613.22-.583.41-1.126.53-1.518.34a.25.25 0 0 1-.14-.2c-.01-.09.03-.17.1-.22l.16-.13a.45.45 0 0 1-.301-.12.236.236 0 0 1-.03-.33c.02-.02.382-.49.733-.86-.351 0-.542-.2-.552-.21a.26.26 0 0 1-.07-.19c.01-.07.05-.14.1-.18l1.116-.79c.12-.08.462-.12.562-.11l2.955.35 3.658-6.22L27.697-4l-6.191 5-4.463 2.64-2.06 5.07-2.623 4.19-.975 3.71-.905 5.43c.1 2.37.08 2.5-.392 2.87-.03.02-.06.04-.09.07.12.65.06 1.05-.17 1.19-.061.03-.121.06-.202.06m8.985-40.73c.07 1 .191 4.12-1.005 4.79-.583.33-1.387.56-2.231.81-1.005.3-2.05.6-2.653 1.05-1.237.93-.985 4.09-.694 7.74.14 1.8.302 3.84.302 5.85 0 .45-.08.89-.222 1.31-.25.69-.582 1.35-.904 1.99-.19.38-.392.77-.563 1.16-.392.86-.663 1.8-.934 2.71l-.131.46c-.623 2.09-1.277 4.57-1.367 7.05-.04 1.03.03 2.08.211 3.13.14.86.553 1.61 1.136 2.11.01-.17-.02-.45-.07-.71-.02-.08.01-.17.07-.23.08-.08.16-.14.22-.19.262-.2.292-.23.202-2.51l.915-5.51.994-3.77c.01-.02.02-.05.03-.07l2.624-4.18 2.07-5.11c.02-.05.06-.09.1-.12L21.215.6l6.332-5.12c.1-.08.24-.07.331.02L33.345.86c.08.08.1.21.04.3l-3.84 6.53c-.05.09-.15.14-.24.12l-3.126-.37c-.04 0-.191.02-.231.04l-.513.36c.05-.03.11-.06.161-.1.201-.13.583-.21.653-.22.04-.01.09 0 .12.01l.745.24c.12.04.19.17.17.29-.03.12-.15.21-.27.19-1.006-.13-1.448.4-1.468.42l-.02.02c.301-.22 1.155-.29 1.648-.31.09-.01.18-.01.281-.01.141 0 .252.11.252.25s-.111.25-.242.25c-.09 0-.18 0-.271.01-.422.03-.764.15-1.025.35-.241.19-.442.35-.613.48.08-.04.16-.1.251-.16.583-.41 1.739-.4 1.87-.4.11 0 .2.07.23.18.03.1-.01.22-.1.28l-1.477.97c.02-.01.03-.02.05-.02.211-.09.382-.16.553-.23.372-.16.693-.3.995-.38.371-.1.603-.02.794.05.2.07.351.12.703 0 .854-.28 6.181-6.88 7.025-8.14.694-1.04.784-1.45.453-2.12-.171-.34-1.689-2.08-3.166-3.75-1.79-2.04-3.639-4.14-4.141-4.94-.613-.97-1.568-.92-3.005-.85-.523.03-1.126.06-1.759.03a1.73 1.73 0 0 1-1.286-.61c-.965-1.09-.965-3.34-.925-4.12z'
          />
          <path
            fill='#80583F'
            d='M9.714 25.14a.26.26 0 0 1-.251-.22l-.181-1.32c-.06 0-.11-.02-.161-.06-.744-.63-.342-1.51-.02-1.89.06-.08.17-.1.261-.08.09.03.16.11.18.21l.413 3.07c.02.14-.08.26-.211.28-.01.01-.02.01-.03.01'
          />
          <path
            className='fill-f8f3ef group-[.svg-selected]_fill-BE9983 lg_group-hover_fill-BE9983'
            d='M28.192 5.322c-.18-.24-.382-.42-.432-.66-.06-.3-.271-1.13-.271-1.13-.141-.59-.453-3.05-.222-3.59.372-.86.543-1.85.503-3.03-.03-.72-.372-3.09-.352-4.99.01-.95.382-1.99.382-1.99-.301-.38-2.804-.14-2.904-.12-.322.05-2.825 4.7-3.608 5.47-.151.14-.543.75-.704.61-.703-.57-2-1.66-3.276-3.21-.674-.82-1.046-1-1.618-1.94-.242-.39-1.94.46-2.04.64-.131.24.29 3.52.28 3.76-.12 4.53.775 4.34 1.539 4.88.06.04-.342 1.37-.342 1.44 0 .26-.302 1.14-.302 1.68 0 .38-.623 1.85-.824 2.17-1.105 1.79-3.166 5.62-3.417 8.3-.482 5.14 2.764 24.01 2.734 27.68l2.482.1s2.975.04 3.97 0c1.538-.06 6.11-.51 6.11-.51.151-3.29.714-5.06 1.71-12.14 1.396-9.77 3.054-20.21.602-23.42'
          />
          <path
            fill='#80583F'
            d='M18.312 41.659c-1.146 0-2.412-.02-2.492-.02l-2.483-.1a.26.26 0 0 1-.241-.25c.01-1.41-.482-.91-1.045-6.12-.885-8.2-1.99-18.42-1.699-21.58.302-3.24 3.126-7.89 3.448-8.41.2-.33.793-1.73.793-2.04 0-.35.111-.8.201-1.17.05-.2.101-.43.101-.51 0-.05.01-.09.11-.46.06-.22.181-.65.222-.85-.06-.04-.11-.07-.171-.1-.885-.51-1.457-1.13-1.347-4.91 0-.07-.04-.49-.09-.9-.252-2.32-.262-2.8-.171-2.97.15-.27 1.387-.86 1.96-.88.291-.01.442.12.502.22.352.57.613.84.935 1.18.19.2.402.42.663.73a22.6 22.6 0 0 0 3.156 3.1c.09-.08.221-.25.291-.33.07-.09.131-.16.171-.2.382-.37 1.236-1.8 2-3.06 1.277-2.12 1.508-2.44 1.739-2.48.03 0 2.713-.32 3.136.21.05.07.07.16.04.24 0 .01-.362 1.02-.372 1.91-.01 1.29.14 2.78.251 3.86.05.49.09.89.1 1.12.05 1.2-.12 2.23-.522 3.14-.19.43.07 2.73.231 3.43 0 0 .201.84.272 1.13.04.14.14.26.26.41.041.05.091.11.131.17 2.372 3.11 1.016 12.68-.301 21.94l-.241 1.67c-1.076 7.62-1.578 9.35-1.709 12.12-.01.12-.1.22-.22.24-.051 0-4.604.44-6.121.51-.342.01-.895.01-1.488.01m-4.724-.61 2.242.09c.02 0 2.975.04 3.95 0 1.316-.05 4.954-.4 5.879-.48.14-2.9.653-4.5 1.698-11.95l.242-1.67c1.296-9.15 2.643-18.62.402-21.57l-.121-.15c-.15-.18-.302-.36-.352-.6-.06-.29-.261-1.12-.261-1.12-.11-.47-.493-3.09-.201-3.75.372-.84.523-1.8.482-2.93-.01-.23-.05-.61-.1-1.09-.11-1.09-.262-2.59-.252-3.92.01-.74.222-1.52.332-1.88-.452-.13-1.98-.06-2.512.01-.211.21-.885 1.34-1.438 2.25-.814 1.35-1.648 2.74-2.08 3.15-.03.03-.08.09-.13.16-.242.3-.433.52-.654.54a.37.37 0 0 1-.251-.07c-.533-.44-1.95-1.59-3.307-3.24-.24-.3-.452-.51-.633-.7-.331-.35-.623-.65-1.005-1.26-.19-.07-1.337.41-1.598.63-.03.33.13 1.91.221 2.68.07.63.09.88.09.96-.1 3.77.513 4.13 1.106 4.47q.165.09.332.21c.17.13.15.21-.14 1.31-.051.18-.091.32-.101.36 0 .1-.04.28-.11.6-.081.34-.182.76-.182 1.05 0 .46-.673 1.99-.864 2.3-1.417 2.3-3.156 5.82-3.377 8.19-.291 3.12.804 13.3 1.699 21.48.492 4.97.964 4.29.995 5.94'
          />
          <mask
            id='mask1_13014_143868'
            width='11'
            height='11'
            x='22'
            y='4'
            maskUnits='userSpaceOnUse'
            style={{
              maskType: 'luminance',
            }}
          >
            <path
              fill='#fff'
              d='M27.504 14.493c2.859 0 5.176-2.287 5.176-5.11 0-2.822-2.317-5.11-5.176-5.11s-5.176 2.288-5.176 5.11 2.317 5.11 5.176 5.11'
            />
          </mask>
          <g mask='url(#mask1_13014_143868)'>
            <path
              fill='#F8F3EF'
              d='M9.71 24.888c-.121.35.301 1.47-.302.98-.704-.57-1.116-1.41-1.256-2.29-.181-1.05-.252-2.12-.211-3.18.08-2.42.683-4.8 1.377-7.11.321-1.07.623-2.17 1.085-3.19.472-1.05 1.075-2.04 1.467-3.13.141-.4.211-.8.211-1.22 0-6.14-1.497-12.29.483-13.79 1.135-.86 3.668-1.18 4.914-1.88 1.247-.7.865-4.82.865-4.82h4.542s-.392 4.65 1.96 4.73c2.352.09 4.03-.56 4.965.93s6.925 7.92 7.317 8.71.23 1.32-.473 2.37-6.16 7.92-7.156 8.24c-.804.26-.884-.22-1.517-.05-.412.11-.885.34-1.498.59 0 0-1.055.54-1.216.2l2.402-1.58s-1.186-.02-1.718.35c-.412.29-.915.49-1.267.32 0 0 .312-.24 1.337-1.04.342-.27.824-.41 1.437-.41 0 0-1.447.02-1.779.27-.382.29-.874.77-1.115.55 0 0 .432-.56.794-.93 0 0 .522-.67 1.698-.51l-.744-.24s-.392.08-.562.19c-.684.45-1.025.1-1.025.1l1.115-.79c.05-.03.322-.07.382-.07l3.126.37 3.839-6.53-5.467-5.36-6.362 5.14-4.503 2.66-2.09 5.15-2.623 4.18-.985 3.75-.915 5.46c.09 3.2-.09 1.63-.533 2.88'
            />
            <path
              fill='#80583F'
              d='M9.642 26.25c-.17 0-.321-.13-.392-.18-.703-.58-1.176-1.44-1.346-2.44-.181-1.08-.252-2.16-.221-3.23.09-2.54.753-5.05 1.386-7.18l.131-.45c.271-.92.553-1.88.955-2.77.18-.4.382-.79.573-1.18.321-.62.643-1.27.884-1.93.13-.36.201-.75.201-1.14 0-1.98-.16-4.02-.301-5.81-.312-3.96-.563-7.08.884-8.18.673-.51 1.759-.83 2.814-1.14.824-.24 1.598-.47 2.13-.77.825-.45.895-3.05.744-4.58 0-.07.02-.14.07-.19.041-.05.111-.08.182-.08h4.543c.07 0 .14.03.18.08.05.05.07.12.07.19 0 .03-.23 2.88.785 4.02.25.29.552.43.924.44.613.02 1.206-.01 1.719-.03 1.497-.08 2.683-.14 3.457 1.08.483.77 2.402 2.95 4.09 4.88 1.88 2.14 3.046 3.47 3.237 3.86.462.93.22 1.56-.483 2.62-.542.81-6.14 7.96-7.286 8.34-.513.17-.794.07-1.025-.01-.171-.06-.282-.1-.503-.04-.271.07-.573.2-.924.35-.171.07-.342.15-.543.23-.18.1-.895.43-1.276.3a.44.44 0 0 1-.262-.22.254.254 0 0 1 .09-.32l1.579-1.04c-.241.05-.463.12-.613.22-.583.41-1.126.53-1.518.34a.25.25 0 0 1-.14-.2c-.01-.09.03-.17.1-.22l.16-.13a.44.44 0 0 1-.3-.12.236.236 0 0 1-.031-.33c.02-.02.382-.49.734-.86-.352 0-.543-.2-.553-.21a.26.26 0 0 1-.07-.19c.01-.07.05-.14.1-.18l1.116-.79c.12-.08.462-.12.562-.11l2.955.35 3.658-6.22L27.693-4l-6.191 5-4.463 2.64-2.06 5.07-2.623 4.19-.975 3.71-.905 5.43c.08 2.12.04 2.22-.24 2.44-.071.06-.152.13-.282.49-.02.07 0 .3.02.44.04.35.08.66-.16.79-.061.04-.111.05-.172.05m8.965-40.75c.07 1 .191 4.12-1.005 4.79-.583.33-1.387.56-2.231.81-1.005.3-2.05.6-2.653 1.05-1.236.93-.985 4.09-.694 7.74.14 1.8.302 3.84.302 5.85 0 .45-.08.89-.221 1.31-.252.69-.583 1.35-.905 1.99-.19.38-.392.77-.563 1.16-.392.86-.663 1.8-.934 2.71l-.131.46c-.623 2.09-1.276 4.57-1.367 7.05-.04 1.03.03 2.08.211 3.13.14.84.523 1.56 1.086 2.06 0-.05-.01-.09-.01-.13-.03-.25-.06-.49.01-.66.16-.45.301-.6.442-.71.08-.06.12-.2.06-2.09l.915-5.51.995-3.77c.01-.02.02-.05.03-.07l2.623-4.18 2.07-5.11c.02-.05.06-.09.1-.12L21.24.6l6.332-5.12c.1-.08.241-.07.332.02L33.37.86c.08.08.1.21.04.3l-3.839 6.53c-.05.09-.15.14-.241.12l-3.126-.37c-.04 0-.19.02-.231.04l-.513.36c.05-.03.111-.06.161-.1.201-.13.583-.21.654-.22.04-.01.09 0 .12.01l.744.24c.12.04.19.17.17.29-.03.12-.15.21-.27.19-1.006-.13-1.448.4-1.468.42l-.02.02c.301-.22 1.156-.29 1.648-.31.09-.01.181-.01.282-.01.14 0 .25.11.25.25s-.11.25-.24.25c-.09 0-.181 0-.272.01-.422.03-.764.15-1.025.35-.241.19-.442.35-.613.48.08-.04.16-.1.251-.16.583-.41 1.739-.4 1.87-.4.11 0 .2.07.23.18.03.1-.01.22-.1.28l-1.477.97c.02-.01.03-.02.05-.02.211-.09.382-.16.553-.23.372-.16.693-.3.995-.38.372-.1.603-.02.794.05.2.07.352.12.703 0 .855-.28 6.181-6.88 7.026-8.14.693-1.04.783-1.45.452-2.12-.171-.34-1.689-2.08-3.166-3.75-1.789-2.04-3.638-4.14-4.14-4.94-.614-.97-1.569-.92-3.006-.85-.522.03-1.125.06-1.759.03a1.73 1.73 0 0 1-1.286-.61c-.965-1.09-.965-3.34-.925-4.12z'
            />
          </g>
        </g>
      </svg>
    ),
  },
  Neckline: {
    'One Shoulder': (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='38'
        height='60'
        fill='none'
        viewBox='0 0 38 60'
      >
        <path
          fill='#fff'
          d='M28.102 8.597c-4-.4-5.2-8.3-5.2-8.3h-8s-1.2 7.9-5.2 8.3-11.3.4-8.9 6.7 9.1 21.77 8.9 23.77-8.5 10.3-7.7 20.6h34.1c.8-10.3-7.5-18.6-7.7-20.6s6.5-17.5 8.9-23.8-5.2-6.27-9.2-6.67'
        />
        <path
          className='fill-f8f3ef group-[.svg-selected]_fill-BE9983 lg_group-hover_fill-BE9983'
          d='M4.705 25.348c9.69-2.27 18.1-8.27 23.4-16.7l4.8.5c1.86 4.51 2.25 9.5 1.1 14.25l-5.6 15.2s4.8 9 6.3 12.1c1.4 2.9 1.4 9 1.4 9h-34.2c-.16-3.68.56-7.35 2.1-10.7 1.3-2.5 5.9-9.8 5.9-9.8z'
        />
        <path
          fill='#80583F'
          d='M4.704 25.5a.215.215 0 0 1-.2-.2c0-.1.1-.3.2-.3a37.82 37.82 0 0 0 23.3-16.6c.1-.1.2-.2.3-.1s.2.2.1.3a38.5 38.5 0 0 1-23.7 16.9M34.003 23.698c-.2 0-.3-.2-.2-.3 2.3-8.4-1-14.1-1.1-14.1a.21.21 0 0 1 .07-.29c.01 0 .02-.01.03-.01a.23.23 0 0 1 .3.1c0 .1 3.5 5.9 1.1 14.5-.05.05-.12.09-.2.1'
        />
        <path
          fill='#80583F'
          d='M36.105 60h-34.2a.215.215 0 0 1-.2-.2c-.6-7.6 3.6-13.9 6.1-17.8.9-1.4 1.6-2.4 1.6-2.9.1-1.3-3.2-9.4-5.8-16-1.2-3-2.3-5.8-3.1-7.7-.64-1.34-.6-2.9.1-4.2 1.4-2.2 5.3-2.5 8.1-2.8.4 0 .8-.1 1.1-.1 3.7-.4 4.9-8 4.9-8.1s.2-.2.3-.2h8c.1.01.19.1.2.2 0 .1 1.2 7.7 4.9 8.1.4 0 .7.1 1.1.1 2.9.2 6.8.6 8.2 2.8.69 1.31.73 2.86.1 4.2-.7 1.9-1.9 4.8-3.1 7.7-2.6 6.5-5.9 14.6-5.8 16 .34 1.06.88 2.05 1.6 2.9 2.5 3.8 6.7 10.2 6.1 17.7 0 .2-.1.3-.2.3m-33.9-.5h33.7c.5-7.3-3.6-13.5-6-17.2-1-1.5-1.7-2.5-1.7-3.2-.2-1.3 2.3-7.5 5.8-16.1 1.2-3 2.3-5.8 3.1-7.7.6-1.2.6-2.6 0-3.8-1.3-2-5-2.3-7.8-2.6-.4 0-.8-.1-1.1-.1-3.8-.4-5.1-7-5.3-8.3h-7.5c-.2 1.3-1.6 8-5.3 8.3-.4 0-.7.1-1.1.1-3 .3-6.7.6-8 2.6-.6.9-.6 2.2 0 3.8.7 1.9 1.8 4.7 3 7.6 3.5 8.6 6 14.9 5.8 16.2-.41 1.14-.98 2.22-1.7 3.2-2.3 3.7-6.4 9.9-5.9 17.2'
        />
        <path
          className='fill-f8f3ef group-[.svg-selected]_fill-BE9983 lg_group-hover_fill-BE9983'
          d='M28.906 8.697s2.3 5.3 3.2 4.7c.9-.81 1.37-1.99 1.3-3.2 0-2.2-1-1.85-4.5-1.5'
        />
        <path
          fill='#80583F'
          d='M31.903 13.698h-.1c-1-.3-2.8-4.1-3.1-4.9v-.2c0-.1.1-.1.2-.1l.9-.1c1.8-.2 2.8-.3 3.4.2.37.45.55 1.02.5 1.6.11 1.29-.41 2.56-1.4 3.4-.2 0-.2.1-.4.1m-2.6-4.8c1.1 2.3 2.3 4.3 2.6 4.3.84-.76 1.28-1.87 1.2-3 .06-.46-.05-.92-.3-1.3-.4-.4-1.4-.2-3-.1z'
        />
        <path
          className='fill-f8f3ef group-[.svg-selected]_fill-BE9983 lg_group-hover_fill-BE9983'
          d='m29.004 8.703 1.5 9.5 1.9.2z'
        />
        <path
          fill='#80583F'
          d='m32.405 18.599-1.9-.2a.215.215 0 0 1-.2-.2l-1.5-9.5c0-.1.1-.2.2-.3s.3 0 .3.2l3.4 9.7v.2c-.08.08-.19.12-.3.1m-1.7-.6 1.3.07-2.4-6.75z'
        />
        <path
          fill='#80583F'
          d='M31.906 12.803c-.2 0-.3-.2-.2-.3.1-.5-1.4-2.2-2.5-3.2a.27.27 0 0 1-.02-.38l.02-.02c.11-.11.29-.11.4 0 .5.4 2.8 2.7 2.6 3.7-.1.1-.2.2-.3.2'
        />
        <path
          className='fill-f8f3ef group-[.svg-selected]_fill-BE9983 lg_group-hover_fill-BE9983'
          d='m29.003 8.703-1.8 7.2 2.39 1z'
        />
        <path
          fill='#80583F'
          d='m29.603 17.2-2.6-1a.19.19 0 0 1-.15-.21c0-.01 0-.01.01-.02a.09.09 0 0 1 0-.07l1.8-7.2c0-.1.1-.2.3-.2.1.01.19.1.2.2l.6 8.2c0 .1 0 .2-.1.2s.04.1-.06.1m-2.1-1.4 1.9.8-.5-6.3z'
        />
      </svg>
    ),
    'Off the Shoulder': (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='38'
        height='60'
        fill='none'
        viewBox='0 0 38 60'
      >
        <path
          fill='#fff'
          d='M28.202 8.503c-4-.4-5.2-8.3-5.2-8.3h-8s-1.2 7.9-5.2 8.3-11.5.4-9.1 6.7 9.1 21.8 8.9 23.8c0 2-8.3 10.4-7.6 20.7h34.2c.8-10.3-7.5-18.6-7.7-20.6s6.5-17.4 8.9-23.8-5.2-6.35-9.2-6.8'
        />
        <path
          className='fill-f8f3ef group-[.svg-selected]_fill-BE9983 lg_group-hover_fill-BE9983'
          d='m1.703 17.5 3.7 9.3 4.3 12.2s-4 6.6-4.9 8.5-3.3 6.7-2.8 12.2h34.1c.05-2.42-.22-4.85-.8-7.2 0 0-3.6-9.5-5.9-11.4l-1-2.1 1.8-5.9 2.7-7s3.3-6.9 3.5-8.5c-4.49-.3-8.98.49-13.1 2.3-1.47.62-2.88 1.39-4.2 2.3 0 0-2.2-1.3-4.2-2.3a33.3 33.3 0 0 0-8.5-2.1c-1.1-.2-4.7-.3-4.7-.3'
        />
        <path
          fill='#80583F'
          d='M2.202 60a.215.215 0 0 1-.2-.2c-.6-7.5 3.5-13.9 6-17.7.7-.9 1.24-1.92 1.6-3 .1-1.3-3.2-9.4-5.9-16-1.2-3-2.4-5.8-3.1-7.7a4.97 4.97 0 0 1 0-4.2c1.4-2.1 5.3-2.5 8.1-2.7.4 0 .8-.1 1.1-.1 3.8-.4 4.9-8.1 4.9-8.2s.2-.2.3-.2h7.9c.1 0 .2 0 .2.1s1.3 7.8 5 8.1c.4 0 .7.1 1.1.1 2.8.2 6.7.5 8.1 2.7.69 1.31.73 2.86.1 4.2-.7 1.9-1.8 4.8-3 7.7-2.6 6.5-5.8 14.7-5.7 16 0 .5.8 1.6 1.7 2.9 2.5 3.8 6.7 10.2 6.2 17.7-.01.11-.09.19-.2.2zM15.102.4c-.2 1.2-1.5 8-5.3 8.4-.4 0-.7.1-1.1.1-2.7.2-6.5.6-7.8 2.6a4.17 4.17 0 0 0 0 3.8c.8 1.9 1.9 4.7 3.2 7.7 2.8 6.9 6 14.7 5.9 16.1 0 .6-.7 1.6-1.7 3.1-2.4 3.8-6.4 10-5.9 17.3l33.7-.2c.4-7.3-3.7-13.5-6.1-17.2-1-1.5-1.7-2.5-1.7-3.1-.1-1.4 3-9.3 5.7-16.2 1.2-3 2.3-5.8 3-7.7.6-1.6.6-2.9 0-3.8-1.3-2-5.1-2.3-7.8-2.5-.4 0-.8-.1-1.1-.1-3.8-.4-5.1-7.1-5.4-8.3z'
        />
        <path
          className='fill-f8f3ef group-[.svg-selected]_fill-BE9983 lg_group-hover_fill-BE9983'
          d='M33.9 24.004c4.7-3.2 3.7-6.6 3.7-6.6s-11-.7-18.4 4.8c-7.4-5.4-18.4-4.8-18.4-4.8s-1 3.3 3.7 6.6'
        />
        <path
          fill='#80583F'
          d='M33.9 24.197c-.1 0-.1 0-.2-.1s0-.2 0-.3c4-2.8 3.7-5.6 3.6-6.2-1.3-.1-11.3-.3-18.1 4.7-.1.1-.2.1-.2 0-6.8-5-16.8-4.8-18.1-4.7-.1.6-.4 3.4 3.6 6.2.08.08.08.22 0 .3-.07.08-.2.09-.28.02l-.02-.02c-4.7-3.3-3.8-6.6-3.7-6.8s.1-.1.2-.1 11-.6 18.4 4.7c7.4-5.3 18.3-4.7 18.4-4.7.07.01.14.05.2.1 0 .1 1 3.5-3.7 6.8z'
        />
      </svg>
    ),
    'Square Neckline': (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='38'
        height='60'
        fill='none'
        viewBox='0 0 38 60'
      >
        <path
          fill='#fff'
          d='M28.12 8.597c-4-.4-5.2-8.3-5.2-8.3h-8s-1.2 7.9-5.2 8.3-11.5.4-9.1 6.7 9.1 21.8 8.9 23.8-8.5 10.3-7.7 20.6h34.3c.8-10.3-7.5-18.6-7.7-20.6s6.5-17.4 8.9-23.8-5.2-6.35-9.2-6.7'
        />
        <path
          className='fill-f8f3ef group-[.svg-selected]_fill-BE9983 lg_group-hover_fill-BE9983'
          d='M35.223 52.494s-3.6-9.5-5.9-11.4l-1-2.1 1.8-5.9s2.6-6.1 3.2-8.2c0-.2.1-.3.1-.4h-.2c-.21-.92-.47-1.82-.8-2.7-1.6-5.1.2-12.6.2-12.6l-3.1-.5-1.4 8.1h-18l-1.7-8.13-3.1.5c.79 2.69 1.13 5.5 1 8.3-.2 1.9-1.3 5.7-1.6 7h-.1l.1.2c-.01.07-.05.14-.1.2h.2l2.6 6.8 2.4 7.3s-4 6.6-4.9 8.5-3.3 6.7-2.8 12.1h34.1c-.1.1 0-4.37-1-7.07'
        />
        <path
          fill='#80583F'
          d='M36.12 59.947H1.92a.215.215 0 0 1-.2-.2c-.5-7.5 3.6-13.9 6.1-17.7.7-.9 1.24-1.92 1.6-3 .1-1.3-3.2-9.5-5.8-16-1.2-3-2.3-5.8-3.1-7.7a4.97 4.97 0 0 1 0-4.2c1.5-2.1 5.4-2.4 8.2-2.6.4 0 .8-.1 1.1-.1 3.8-.4 5-8.1 5-8.2.01-.11.09-.19.2-.2h8c.1.01.19.1.2.2 0 .1 1.2 7.8 5 8.2.4 0 .7.1 1.1.1 2.8.2 6.7.6 8.1 2.8.7 1.31.7 2.89 0 4.2-.7 1.9-1.9 4.7-3.1 7.7-2.6 6.5-5.9 14.7-5.8 16 .35 1.09.89 2.1 1.6 3 2.5 3.8 6.6 10.2 6.1 17.7 0-.15 0 0-.1 0m-34-.4h33.7c.5-7.3-3.6-13.5-6-17.3-1-1.5-1.7-2.5-1.7-3.1-.1-1.4 3-9.3 5.8-16.2 1.2-3 2.3-5.8 3.1-7.7.6-1.6.6-2.9 0-3.8-1.3-2-5.1-2.3-7.8-2.6-.4 0-.8-.1-1.1-.1-3.8-.4-5.1-7.1-5.3-8.3h-7.6c-.2 1.2-1.5 7.9-5.3 8.3-.4 0-.7.1-1.1.1-2.8.3-6.6.6-7.8 2.6-.6.9-.6 2.2 0 3.8.7 2 1.8 4.8 3 7.7 2.8 6.9 6 14.7 5.8 16.2-.4 1.12-.97 2.17-1.7 3.1-2.4 3.75-6.5 10-6 17.25z'
        />
        <path
          fill='#80583F'
          d='M4.622 24.67c-.2-.1-.2-.2-.2-.3 1.95-4.81 2.2-10.13.7-15.1-.02-.11.02-.22.1-.3.11-.01.22.03.3.1a23.13 23.13 0 0 1-.7 15.5c-.1.1-.1.1-.2.1M33.42 24.67c-.1 0-.2 0-.2-.1a23.07 23.07 0 0 1-.7-15.5c.08-.07.19-.11.3-.1.1.03.16.13.13.23v.06999999999999999c-1.5 5-1.26 10.36.7 15.2 0 0 0 .1-.2.2zM28.12 16.999h-18a.215.215 0 0 1-.2-.2l-1.6-8.13c0-.1 0-.2.2-.2s.2 0 .2.2l1.6 8h17.66l1.4-8c-.01-.1.07-.19.18-.2h.02c.11 0 .2.09.2.2l-1.4 8.1c-.06.23-.16.23-.26.23'
        />
      </svg>
    ),
    'V-Neck': (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='38'
        height='60'
        fill='none'
        viewBox='0 0 38 60'
      >
        <path
          fill='#fff'
          d='M28.103 8.628c-4-.4-5.1-8.3-5.1-8.3h-8s-1.2 7.9-5.1 8.3-11.5.4-9.1 6.7 9 21.8 8.9 23.8-8.5 10.3-7.7 20.6h34c.8-10.3-7.5-18.6-7.7-20.6s6.5-17.4 8.9-23.8-5.1-6.3-9.1-6.7'
        />
        <path
          className='fill-f8f3ef group-[.svg-selected]_fill-BE9983 lg_group-hover_fill-BE9983'
          d='M35.201 52.525s-3.6-9.5-5.9-11.4l-1-2.1 1.8-5.9s2.6-6.1 3.2-8.2c0-.2.1-.3.1-.4h-.2c-.21-.92-.47-1.82-.8-2.7-1.6-5 .2-12.6.2-12.6l-4.6-.6-9 15.9-9.1-15.9-4.6.6c.79 2.69 1.13 5.5 1 8.3-.2 1.9-1.3 5.7-1.6 7h-.1l.1.2c-.01.07-.05.14-.1.2h.2l2.6 6.8 2.3 7.3s-4 6.5-4.9 8.5-3.3 6.7-2.8 12.2h34s.2-4.5-.8-7.2'
        />
        <path
          fill='#80583F'
          d='M36.002 59.947h-34a.215.215 0 0 1-.2-.2c-.6-7.5 3.6-13.9 6.1-17.7.9-1.4 1.6-2.4 1.6-2.9.1-1.3-3.2-9.4-5.8-15.9-1.2-3-2.3-5.8-3.1-7.7-.64-1.34-.6-2.9.1-4.2 1.3-2.3 5.2-2.6 8.1-2.9.4 0 .8-.1 1.1-.1 3.7-.4 4.9-8 4.9-8.1.01-.11.09-.19.2-.2h8c.1.01.19.1.2.2 0 .1 1.2 7.7 4.9 8.1.3 0 .7.1 1.1.1 2.8.2 6.7.6 8.1 2.8.69 1.31.73 2.86.1 4.2-.7 1.9-1.9 4.7-3.1 7.7-2.6 6.5-5.9 14.6-5.8 15.9.34 1.06.88 2.05 1.6 2.9 2.5 3.8 6.6 10.2 6.1 17.7.1.18 0 .3-.2.3m-33.8-.5h33.6c.5-7.2-3.6-13.4-6-17.2-1-1.5-1.7-2.5-1.7-3.2-.1-1.3 2.3-7.5 5.8-16.2 1.2-3 2.3-5.8 3.1-7.7.6-1.2.6-2.6 0-3.8-1.3-2-5-2.3-7.8-2.5-.4 0-.8-.1-1.1-.1-3.8-.4-5.1-7-5.3-8.3h-7.5c-.2 1.3-1.6 7.9-5.3 8.3-.38.17-.79.23-1.2.2-2.7.2-6.5.5-7.8 2.5-.6.9-.6 2.2 0 3.8.7 1.9 1.9 4.7 3.1 7.7 3.5 8.6 6 14.8 5.8 16.2-.41 1.14-.98 2.22-1.7 3.2-2.4 3.68-6.4 9.88-6 17.08z'
        />
        <path
          fill='#80583F'
          d='M4.603 24.825c-.2-.1-.3-.2-.2-.3 1.95-4.81 2.2-10.13.7-15.1 0-.1 0-.3.1-.3s.3 0 .3.1a23.13 23.13 0 0 1-.7 15.5c-.05.05-.12.09-.2.1M33.4 24.826a.215.215 0 0 1-.2-.2 23.07 23.07 0 0 1-.7-15.5c.05-.11.18-.16.29-.1h.01c.11.06.15.19.1.3-1.51 4.96-1.26 10.3.7 15.1.1.1 0 .3-.2.4M19.002 24.823c-.1 0-.2 0-.2-.1l-9.1-16a.185.185 0 0 1-.01-.25l.01-.01h.07c.1 0 .3 0 .3.1l8.9 15.5 8.9-15.5c.1-.1.2-.2.3-.1s.2.2.1.3l-9.1 15.9c-.02.08-.08.14-.17.16'
        />
      </svg>
    ),
    'Straight Neckline': (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='38'
        height='60'
        fill='none'
        viewBox='0 0 38 60'
      >
        <path
          fill='#fff'
          d='M28.12 8.597c-4-.4-5.2-8.3-5.2-8.3h-8s-1.2 7.9-5.2 8.3-11.5.4-9.1 6.7 9.1 21.8 8.9 23.8-8.5 10.3-7.7 20.6h34.3c.8-10.3-7.5-18.6-7.7-20.6s6.5-17.4 8.9-23.8-5.2-6.35-9.2-6.7'
        />
        <path
          className='fill-f8f3ef group-[.svg-selected]_fill-BE9983 lg_group-hover_fill-BE9983'
          d='M35.22 52.5s-3.6-9.5-5.9-11.4l-1-2.1 1.8-5.9s2.6-6.1 3.2-8.2c0-.2 1.8-4.4 1.8-4.4l-3.6-1.5-3.4-.6s-8.8 0-11.7-.1c-3.25-.11-6.5.13-9.7.7l-3.9 1.5 1.8 4.4 2.7 6.8 2.4 7.3s-4 6.6-4.9 8.5-3.3 6.7-2.8 12.1h34.1c.05-2.4-.25-4.79-.9-7.1'
        />
        <path
          fill='#80583F'
          d='M36.12 59.947H1.92a.215.215 0 0 1-.2-.2c-.5-7.5 3.6-13.9 6.1-17.7.9-1.4 1.6-2.4 1.6-3 .1-1.3-3.2-9.5-5.8-16-1.2-3-2.3-5.8-3.1-7.7a4.97 4.97 0 0 1 0-4.2c1.5-2.1 5.4-2.4 8.2-2.6.4 0 .8-.1 1.1-.1 3.8-.4 5-8.1 5-8.2.01-.11.09-.19.2-.2h8c.1.01.19.1.2.2 0 .1 1.2 7.8 5 8.2.4 0 .7.1 1.1.1 2.8.2 6.7.6 8.1 2.8.7 1.31.7 2.89 0 4.2-.7 1.9-1.9 4.7-3.1 7.7-2.6 6.5-5.9 14.7-5.8 16 .35 1.09.89 2.1 1.6 3 2.5 3.8 6.6 10.2 6.1 17.7 0-.15 0 0-.1 0m-34-.4h33.7c.5-7.3-3.6-13.5-6-17.3-.73-.93-1.31-1.98-1.7-3.1-.1-1.4 3-9.3 5.8-16.2 1.2-3 2.3-5.8 3.1-7.7.6-1.6.6-2.9 0-3.8-1.3-2-5.1-2.3-7.8-2.6-.4 0-.8-.1-1.1-.1-3.8-.4-5.1-7.1-5.3-8.3h-7.6c-.2 1.2-1.5 7.9-5.3 8.3-.4 0-.7.1-1.1.1-2.8.3-6.6.6-7.8 2.6-.6.9-.6 2.2 0 3.8.7 2 1.8 4.8 3 7.7 2.8 6.9 6 14.7 5.8 16.2-.4 1.12-.97 2.17-1.7 3.1-2.43 3.75-6.5 10-6 17.25z'
        />
        <path
          fill='#80583F'
          d='M2.822 20.664c-.1 0-.2 0-.2-.1-.02-.11.02-.22.1-.3l3.9-1.5c2.23-.48 4.51-.71 6.8-.7h11.2c2.32-.02 4.63.22 6.9.7l3.4 1.4c.1 0 .1.2.1.3-.03.1-.14.16-.24.14h-.06l-3.4-1.4a31 31 0 0 0-6.8-.7h-11.1c-2.25-.01-4.5.22-6.7.7z'
        />
      </svg>
    ),
    'Bateau Neckline': (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='38'
        height='60'
        fill='none'
        viewBox='0 0 38 60'
      >
        <path
          fill='#fff'
          d='M28.12 8.503c-4-.4-5.2-8.3-5.2-8.3h-8s-1.2 7.9-5.2 8.3-11.5.4-9.1 6.8 9.1 21.9 8.9 23.8-8.5 10.3-7.7 20.7h34.3c.8-10.3-7.5-18.7-7.7-20.7s6.5-17.5 8.9-23.8-5.2-6.35-9.2-6.8'
        />
        <path
          className='fill-f8f3ef group-[.svg-selected]_fill-BE9983 lg_group-hover_fill-BE9983'
          d='m5.12 26.103 4.6 13s-4 6.6-4.9 8.5-3.3 6.7-2.8 12.1h34.19c.05-2.42-.22-4.85-.8-7.2 0 0-3.6-9.5-5.9-11.4l-1.1-2.1 1.9-6 2.7-7.1c-.68-2.45-1.12-4.96-1.3-7.5-.27-3.16-.37-6.33-.3-9.5l-4.3-.7c-.99.68-2.02 1.28-3.1 1.8-1.7.58-3.5.81-5.3.7-1.4 0-5.7-.1-7.8-2.5 0 0-3.3.4-4.3.6-.1 0 .1 3-.3 9.3-.25 2.69-.65 5.36-1.19 8'
        />
        <path
          fill='#80583F'
          d='M36.12 60H1.92a.215.215 0 0 1-.2-.2c-.6-7.5 3.6-13.9 6.1-17.8.7-.9 1.24-1.92 1.6-3 .1-1.3-3.2-9.5-5.8-16-1.2-3-2.3-5.8-3.1-7.7a4.97 4.97 0 0 1 0-4.2c1.5-2.1 5.4-2.4 8.2-2.7.4 0 .8-.1 1.1-.1 3.8-.4 5-8.1 5-8.2s.1-.1.2-.1h8c.1.01.19.1.2.2 0 .1 1.2 7.8 5 8.2.4 0 .7.1 1.1.1 2.8.2 6.7.6 8.1 2.8.7 1.31.7 2.89 0 4.2-.7 1.9-1.9 4.8-3.1 7.7-2.6 6.5-5.9 14.7-5.8 16 .35 1.09.89 2.1 1.6 3 2.5 3.8 6.7 10.2 6.1 17.7.1.05-.01.1-.1.1m-34-.4h33.8c.5-7.3-3.6-13.6-6-17.3-1-1.5-1.7-2.5-1.7-3.1-.1-1.4 3.1-9.3 5.9-16.2 1.2-3 2.3-5.8 3.1-7.7.6-1.6.6-2.9 0-3.8-1.3-2-5.1-2.3-7.8-2.6-.4 0-.8-.1-1.1-.1-3.8-.4-5.1-7.1-5.3-8.3h-7.6c-.2 1.2-1.5 8-5.3 8.3-.4 0-.7.1-1.1.1-3 .1-6.8.5-8.1 2.5-.6.9-.6 2.2 0 3.8.7 1.9 1.9 4.8 3.1 7.7 2.8 6.9 6 14.8 5.9 16.2-.4 1.12-.97 2.17-1.7 3.1-2.5 3.8-6.6 10.1-6.1 17.4'
        />
        <path
          fill='#80583F'
          d='M19.02 10.998c-3.8 0-6.5-.8-8.2-2.6a.185.185 0 0 1-.01-.25l.01-.01c0-.1.1-.1.2 0 1.7 1.7 4.3 2.5 8 2.5s6.3-.8 8-2.5c.08-.08.22-.08.3 0s.08.22 0 .3c-1.8 1.66-4.5 2.56-8.3 2.56M5.122 26.298a.215.215 0 0 1-.2-.2c2.3-11 1.5-17 1.5-17-.01-.1.07-.19.18-.2h.02c.11 0 .2.09.2.2 0 .1.8 6-1.5 17.1-.05.05-.12.09-.2.1M32.92 26.297a.215.215 0 0 1-.2-.2c-2.3-11.1-1.5-17.1-1.5-17.1-.01-.1.07-.19.18-.2h.01c.11 0 .2.09.21.2 0 .1-.8 6 1.5 17 0 .2 0 .3-.2.3'
        />
      </svg>
    ),
    Turtleneck: (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='38'
        height='60'
        fill='none'
        viewBox='0 0 38 60'
      >
        <g clipPath='url(#clip0_13409_28757)'>
          <path
            fill='#fff'
            d='M28.124 8.597c-4-.4-5.2-8.3-5.2-8.3h-8s-1.2 7.9-5.2 8.3-11.5.4-9.1 6.7 9.1 21.8 8.9 23.8-8.5 10.3-7.7 20.6h34.3c.8-10.3-7.5-18.6-7.7-20.6s6.5-17.4 8.9-23.8-5.2-6.35-9.2-6.7'
          />
          <path
            className='fill-f8f3ef group-[.svg-selected]_fill-BE9983 lg_group-hover_fill-BE9983'
            d='M35.255 52.394s-3.21-7.5-6.04-11.21l-.86-2.29 1.8-5.9 2.7-7c.82-1.37 1.45-2.86 1.87-4.41h.1l-.09-.03c.01-.02.01-.04.02-.06h-.16c-1.1-.49-5.97-3.23-5.97-12.91l-1.54-.39-1.4-.96-1.05-2.57s-.92.53-2.09.79c-1.41.32-3.13.4-3.52.4-1.99-.09-3.77-.68-5.7-1.19-.23.68-.57 1.33-1 1.9-.5.6-1.6 2.1-3.1 2-.38 2.56-.88 5.29-1.96 7.64-1.05 1.98-2.55 3.58-3.99 5.29h-.02l.01.02c-.01.02-.03.03-.04.05h.06l2.07 5.24 4.3 12.2s-4 6.6-4.9 8.5-3.3 6.7-2.8 12.1h34.1c.05-2.43-.22-4.86-.8-7.21'
          />
          <path
            fill='#80583F'
            d='M34.525 21.738c-6.31-3.09-6.31-11.66-6.31-12.95a5.16 5.16 0 0 1-3.85-3.69c-1.65.6-3.38.91-5.13.9-1.91.02-3.81-.28-5.62-.9-.3.8-1.48 3.69-4.14 3.79-.28 4.89-2.39 9.5-5.92 12.85-.08.08-.22.08-.3 0a.22.22 0 0 1 0-.3c5.93-5.88 5.83-12.66 5.73-12.76s0-.1.1-.1l.1-.1c2.86 0 3.94-3.69 4.04-3.69 0-.06.04-.1.1-.1h.2c1.84.62 3.78.92 5.72.9 1.77 0 3.55-.31 5.23-.9h.2l.1.1a4.8 4.8 0 0 0 3.75 3.69c.1.01.19.1.2.2 0 .1-.49 9.57 6.02 12.76.1 0 .1.2.1.3-.22-.13-.32 0-.32 0'
          />
          <path
            fill='#80583F'
            d='M36.124 59.947h-34.2a.215.215 0 0 1-.2-.2c-.5-7.5 3.6-13.9 6.1-17.7.9-1.4 1.6-2.4 1.6-3 .1-1.3-3.2-9.5-5.8-16-1.2-3-2.3-5.8-3.1-7.7a4.97 4.97 0 0 1 0-4.2c1.5-2.1 5.4-2.4 8.2-2.6.4 0 .8-.1 1.1-.1 3.8-.4 5-8.1 5-8.2.01-.11.09-.19.2-.2h8c.1.01.19.1.2.2 0 .1 1.2 7.8 5 8.2.4 0 .7.1 1.1.1 2.8.2 6.7.6 8.1 2.8.7 1.31.7 2.89 0 4.2-.7 1.9-1.9 4.7-3.1 7.7-2.6 6.5-5.9 14.7-5.8 16 .35 1.09.89 2.1 1.6 3 2.5 3.8 6.6 10.2 6.1 17.7.03-.15.03 0-.1 0m-34-.4h33.7c.5-7.3-3.6-13.5-6-17.3-.73-.93-1.31-1.98-1.7-3.1-.1-1.4 3-9.3 5.8-16.2 1.2-3 2.3-5.8 3.1-7.7.6-1.6.6-2.9 0-3.8-1.3-2-5.1-2.3-7.8-2.6-.4 0-.8-.1-1.1-.1-3.8-.4-5.1-7.1-5.3-8.3h-7.57c-.2 1.2-1.5 7.9-5.3 8.3-.4 0-.7.1-1.1.1-2.8.3-6.6.6-7.8 2.6-.6.9-.6 2.2 0 3.8.7 2 1.8 4.8 3 7.7 2.8 6.9 6 14.8 5.8 16.2-.4 1.12-.97 2.17-1.7 3.1-2.44 3.75-6.54 10-6 17.25z'
          />
        </g>
        <defs>
          <clipPath id='clip0_13409_28757'>
            <path
              fill='#fff'
              d='M.055.047h37.89v59.9H.055z'
            />
          </clipPath>
        </defs>
      </svg>
    ),
    'Halter Neckline': (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='38'
        height='60'
        fill='none'
        viewBox='0 0 38 60'
      >
        <path
          fill='#fff'
          d='M28.12 8.503c-4-.4-5.2-8.3-5.2-8.3h-8s-1.2 7.9-5.2 8.3-11.5.4-9.1 6.8 9.1 21.9 8.9 23.8-8.5 10.3-7.7 20.7h34.3c.8-10.3-7.5-18.7-7.7-20.7s6.5-17.5 8.9-23.8-5.2-6.35-9.2-6.8'
        />
        <path
          className='fill-f8f3ef group-[.svg-selected]_fill-BE9983 lg_group-hover_fill-BE9983'
          d='m5.12 26.098 4.6 13s-4 6.6-4.9 8.5-3.3 6.7-2.8 12.1h34.2c.05-2.42-.22-4.85-.8-7.2 0 0-3.6-9.5-5.9-11.4l-1.1-2.1 1.9-6 2.7-7.1s0-2.2-1.8-7.5c-.6-1.25-1.31-2.46-2.1-3.6a19.6 19.6 0 0 0-5-4.9c-1.7.58-3.5.81-5.3.7-1.63.07-3.26-.17-4.8-.7a19.6 19.6 0 0 0-7.3 8.9c-.93 2.33-1.47 4.8-1.6 7.3'
        />
        <path
          fill='#80583F'
          d='M36.12 60H1.92a.215.215 0 0 1-.2-.2c-.6-7.5 3.6-13.9 6.1-17.8.7-.9 1.24-1.92 1.6-3 .1-1.3-3.2-9.5-5.8-16-1.2-3-2.3-5.8-3.1-7.7a4.97 4.97 0 0 1 0-4.2c1.5-2.1 5.4-2.4 8.2-2.7.4 0 .8-.1 1.1-.1 3.8-.4 5-8.1 5-8.2s.1-.1.2-.1h8c.1.01.19.1.2.2 0 .1 1.2 7.8 5 8.2.4 0 .7.1 1.1.1 2.8.2 6.7.6 8.1 2.8.7 1.31.7 2.89 0 4.2-.7 1.9-1.9 4.8-3.1 7.7-2.6 6.5-5.9 14.7-5.8 16 .35 1.09.89 2.1 1.6 3 2.5 3.8 6.7 10.2 6.1 17.7.1.05 0 .1-.1.1m-34-.4h33.8c.5-7.3-3.6-13.6-6-17.3-1-1.5-1.7-2.5-1.7-3.1-.1-1.4 3.1-9.3 5.9-16.2 1.2-3 2.3-5.8 3.1-7.7.6-1.6.6-2.9 0-3.8-1.3-2-5.1-2.3-7.8-2.6-.4 0-.8-.1-1.1-.1-3.8-.4-5.1-7.1-5.3-8.3h-7.6c-.2 1.2-1.5 8-5.3 8.3-.4 0-.7.1-1.1.1-3 .1-6.8.5-8.1 2.5-.6.9-.6 2.2 0 3.8.7 1.9 1.9 4.7 3.1 7.7 2.8 6.9 6 14.8 5.9 16.2-.4 1.12-.97 2.17-1.7 3.1-2.5 3.8-6.6 10.1-6.1 17.4'
        />
        <path
          fill='#80583F'
          d='M19.02 10.998c-3.8 0-6.5-.8-8.2-2.6a.185.185 0 0 1-.01-.25l.01-.01c0-.1.1-.1.2 0 1.7 1.7 4.3 2.5 8 2.5s6.3-.8 8-2.5c.08-.08.22-.08.3 0s.08.22 0 .3c-1.8 1.66-4.54 2.56-8.3 2.56'
        />
        <path
          fill='#80583F'
          d='M5.02 26.302a.215.215 0 0 1-.2-.2c.3-6.48 3.64-12.45 9-16.1.1-.1.2 0 .3.1s0 .2-.1.3a20 20 0 0 0-8.8 15.8c-.05.05-.12.09-.2.1M33.02 26.3a.215.215 0 0 1-.2-.2c-.8-11-8.7-15.8-8.8-15.8a.34.34 0 0 1-.1-.3c.08-.07.19-.11.3-.1 5.36 3.66 8.69 9.62 9 16.1 0 .2-.1.3-.2.3'
        />
      </svg>
    ),
    'Mandarin Neckline': (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='38'
        height='60'
        fill='none'
        viewBox='0 0 38 60'
      >
        <path
          fill='#fff'
          d='M28.12 8.597c-4-.4-5.2-8.3-5.2-8.3h-8s-1.2 7.9-5.2 8.3-11.5.4-9.1 6.7 9.1 21.8 8.9 23.8-8.5 10.3-7.7 20.6h34.3c.8-10.3-7.5-18.6-7.7-20.6s6.5-17.4 8.9-23.8-5.2-6.35-9.2-6.7'
        />
        <path
          className='fill-f8f3ef group-[.svg-selected]_fill-BE9983 lg_group-hover_fill-BE9983'
          d='M7.422 8.8s1.9-.1 2.5-.2c.81-.18 1.57-.56 2.2-1.1 0 0 3.9.7 4.8 1.6 1.06.79 1.75 1.99 1.9 3.3.2 1.7.3 2.8.3 2.8l-5.2.6-5.2-2.9z'
        />
        <path
          className='fill-f8f3ef group-[.svg-selected]_fill-BE9983 lg_group-hover_fill-BE9983'
          d='M30.622 8.8s-1.9-.1-2.5-.2c-.81-.18-1.57-.56-2.2-1.1 0 0-3.9.7-4.8 1.6a4.72 4.72 0 0 0-1.9 3.3c-.2 1.8-.3 2.8-.3 2.8l5.2.6 5.2-2.9z'
        />
        <path
          className='fill-f8f3ef group-[.svg-selected]_fill-BE9983 lg_group-hover_fill-BE9983'
          d='m5.12 26.095 3 7.7 1.6 5.3s-4 6.8-4.9 8.5c-1 1.9-3.3 6.7-2.8 12.1h34.1c.05-2.42-.22-4.85-.8-7.2 0 0-3.6-9.5-5.9-11.4l-1.1-2.1 1.8-5.9s1.9-4.9 3.9-10c0 0-2.7-5.6-1.7-14.1l-2.7-.3s-1.8 4.8-10.4 4.8c-10.9 0-10.9-4.8-10.9-4.8s-2.2.2-2.8.3c.29 2.22.33 4.47.1 6.7-.33 2.47-.93 4.9-1.79 7.25 0 0 1.29 3 1.29 3.15'
        />
        <path
          fill='#80583F'
          d='M36.12 59.947H1.92a.215.215 0 0 1-.2-.2c-.5-7.5 3.6-13.9 6.1-17.7.9-1.4 1.6-2.4 1.6-3 .1-1.3-3.2-9.5-5.8-16-1.2-3-2.3-5.8-3.1-7.7a4.97 4.97 0 0 1 0-4.2c1.5-2.1 5.4-2.4 8.2-2.6.4 0 .8-.1 1.1-.1 3.8-.4 5-8.1 5-8.2.01-.11.09-.19.2-.2h8c.1.01.19.1.2.2 0 .1 1.2 7.8 5 8.2.4 0 .7.1 1.1.1 2.8.2 6.7.6 8.1 2.8.7 1.31.7 2.89 0 4.2-.7 1.9-1.9 4.7-3.1 7.7-2.6 6.5-5.9 14.7-5.8 16 .35 1.09.89 2.1 1.6 3 2.5 3.8 6.6 10.2 6.1 17.7 0-.15 0 0-.1 0m-34-.4h33.71c.5-7.3-3.6-13.5-6-17.3-.73-.93-1.31-1.98-1.7-3.1-.1-1.4 3-9.3 5.8-16.2 1.2-3 2.3-5.8 3.1-7.7.6-1.6.6-2.9 0-3.8-1.3-2-5.1-2.3-7.8-2.6-.4 0-.8-.1-1.1-.1-3.8-.4-5.1-7.1-5.3-8.3h-7.6c-.2 1.2-1.5 7.9-5.3 8.3-.4 0-.7.1-1.1.1-2.8.3-6.6.6-7.8 2.6-.6.9-.6 2.2 0 3.8.7 2 1.8 4.8 3 7.7 2.8 6.9 6 14.7 5.8 16.2-.4 1.12-.97 2.17-1.7 3.1-2.42 3.75-6.52 10-6 17.25z'
        />
        <path
          fill='#80583F'
          d='M19.02 14.948a.215.215 0 0 1-.2-.2l-.4-2.3a4.86 4.86 0 0 0-2.9-3.7l-3.4-1.4c-.1.1-.1-.1-.1-.2.08-.07.19-.11.3-.1l3.3 1.4a5.43 5.43 0 0 1 3.1 4l.4 2.3c.1.1 0 .2-.1.2'
        />
        <path
          fill='#80583F'
          d='M19.02 14.948a.215.215 0 0 1-.2-.2l.5-2.3a5.39 5.39 0 0 1 3.1-4l3.3-1.4c.11-.01.22.03.3.1.01.11-.03.22-.1.3l-3.3 1.4a4.9 4.9 0 0 0-2.9 3.7l-.5 2.3c-.1.1-.1.1-.2.1'
        />
        <path
          fill='#80583F'
          d='M19.02 14.95c-5.7 0-10.4-2.5-10.9-5.9-.1 0 0-.1.1-.2.1.01.19.1.2.2.5 3.2 5.1 5.5 10.5 5.5s10.1-2.5 10.6-5.6c0-.11.09-.2.2-.2.11.01.19.09.2.2-.5 3.45-5.2 6-10.9 6M3.833 23.097c-.2-.1-.2-.2-.2-.3 2.8-6 1.7-13.7 1.7-13.8-.01-.1.07-.19.18-.2h.01c.11 0 .2.09.21.2 0 .1 1.1 7.9-1.7 14-.11.1-.11.1-.2.1M34.12 23.097c-.1 0-.1 0-.2-.1-2.8-6.1-1.7-13.9-1.7-14-.01-.1.07-.19.18-.2h.01c.11 0 .2.09.21.2 0 .1-1.1 7.7 1.7 13.8 0 .1 0 .2-.2.3'
        />
      </svg>
    ),
    'Sweetheart Neckline': (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='38'
        height='60'
        fill='none'
        viewBox='0 0 38 60'
      >
        <path
          fill='#fff'
          d='M28.12 8.667c-4-.4-5.2-8.3-5.2-8.3h-8s-1.2 7.9-5.2 8.3-11.5.4-9.1 6.7 9.1 21.8 8.9 23.8-8.5 10.3-7.7 20.6h34.3c.8-10.3-7.5-18.6-7.7-20.6s6.5-17.4 8.9-23.8-5.2-6.42-9.2-6.7'
        />
        <path
          className='fill-f8f3ef group-[.svg-selected]_fill-BE9983 lg_group-hover_fill-BE9983'
          d='m4.02 23.544 1.35 3.4 4.3 12.2s-4 6.6-4.9 8.5-3.3 6.7-2.8 12.1h34.1c.05-2.42-.22-4.85-.8-7.2 0 0-3.6-9.5-5.9-11.4l-1-2.1 1.8-5.9 2.7-7c.56-.56.88-1.31.9-2.1 0 0-.8-8.8-10.5-4.1-1.47.62-2.88 1.39-4.2 2.3 0 0-2.2-1.3-4.2-2.3-1.85-.96-3.93-1.37-6-1.2-3.95.7-4.85 4.8-4.85 4.8'
        />
        <path
          fill='#80583F'
          d='M36.12 59.947H1.92a.215.215 0 0 1-.2-.2c-.5-7.5 3.6-13.9 6.1-17.7.7-.9 1.24-1.92 1.6-3 .1-1.3-3.2-9.5-5.8-16-1.2-3-2.3-5.8-3.1-7.7a4.97 4.97 0 0 1 0-4.2c1.5-2.1 5.4-2.4 8.2-2.6.4 0 .8-.1 1.1-.1 3.8-.4 5-8.1 5-8.2.01-.11.09-.19.2-.2h8c.1.01.19.1.2.2 0 .1 1.2 7.8 5 8.2.4 0 .7.1 1.1.1 2.8.2 6.7.6 8.1 2.8.7 1.31.7 2.89 0 4.2-.7 1.9-1.9 4.7-3.1 7.7-2.6 6.5-5.9 14.7-5.8 16 .35 1.09.89 2.1 1.6 3 2.5 3.8 6.6 10.2 6.1 17.7 0-.1 0 0-.1 0m-34-.4h33.7c.5-7.3-3.6-13.5-6-17.3-.73-.93-1.31-1.98-1.7-3.1-.1-1.4 3-9.3 5.8-16.2 1.2-3 2.3-5.8 3.1-7.7.6-1.6.6-2.9 0-3.8-1.3-2-5.1-2.3-7.8-2.6-.4 0-.8-.1-1.1-.1-3.8-.4-5.1-7.1-5.3-8.3h-7.6c-.2 1.2-1.5 7.9-5.3 8.3-.4 0-.7.1-1.1.1-2.8.3-6.6.6-7.8 2.6-.6.9-.6 2.2 0 3.8.7 2 1.8 4.8 3 7.7 2.8 6.9 6 14.7 5.8 16.2-.4 1.12-.97 2.17-1.7 3.1-2.4 3.8-6.5 10-6 17.3'
        />
        <path
          className='fill-f8f3ef group-[.svg-selected]_fill-BE9983 lg_group-hover_fill-BE9983'
          d='M34.12 23.246c-.5-6.4-7.6-6.5-15.1-1-7.5-5.4-14.4-5.1-15 1.3'
        />
        <path
          fill='#80583F'
          d='M4.02 23.746a.215.215 0 0 1-.2-.2c0-2.02 1.17-3.85 3-4.7 3-1.4 7.5-.1 12.2 3.3 4.7-3.4 9.5-4.7 12.4-3.4 1.74.88 2.86 2.65 2.9 4.6-.01.11-.1.2-.21.2a.19.19 0 0 1-.19-.19v-.01a4.82 4.82 0 0 0-2.7-4.3c-2.8-1.3-7.5 0-12.1 3.4h-.3c-4.5-3.3-9.1-4.6-11.9-3.3-1.6.7-2.5 2.3-2.7 4.4-.01.11-.09.19-.2.2'
        />
      </svg>
    ),
    'Scoop/U-Neck': (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='38'
        height='60'
        fill='none'
        viewBox='0 0 38 60'
      >
        <path
          fill='#fff'
          d='M28.12 8.597c-4-.4-5.2-8.3-5.2-8.3h-8s-1.2 7.9-5.2 8.3-11.5.4-9.1 6.7 9.1 21.8 8.9 23.8-8.5 10.3-7.7 20.6h34.3c.8-10.3-7.5-18.6-7.7-20.6s6.5-17.4 8.9-23.8-5.2-6.35-9.2-6.7'
        />
        <path
          className='fill-f8f3ef group-[.svg-selected]_fill-BE9983 lg_group-hover_fill-BE9983'
          d='M35.219 52.494s-3.6-9.5-5.9-11.4l-1-2.1 1.8-5.9s2.6-6.1 3.2-8.2c0-.2.1-.3.1-.4h-.2c-.21-.92-.47-1.82-.8-2.7-1.6-5.1.2-12.6.2-12.6l-3.1-.5c-.12 1.52-.49 3.01-1.1 4.4a9.32 9.32 0 0 1-4 4.1c-1.8.9-6.3.7-10.9 0-4.12-.6-5.12-8.53-5.12-8.53l-3.1.5c.79 2.69 1.13 5.5 1 8.3-.2 1.9-1.3 5.7-1.6 7h-.1l.1.2c-.01.07-.05.14-.1.2h.2l2.6 6.8 2.4 7.3s-4 6.6-4.9 8.5-3.3 6.7-2.8 12.1h34.1c-.08.1.02-4.37-.98-7.07'
        />
        <path
          fill='#80583F'
          d='M36.12 59.947H1.92a.215.215 0 0 1-.2-.2c-.5-7.5 3.6-13.9 6.1-17.7.9-1.4 1.6-2.4 1.6-3 .1-1.3-3.2-9.5-5.8-16-1.2-3-2.3-5.8-3.1-7.7a4.97 4.97 0 0 1 0-4.2c1.5-2.1 5.4-2.4 8.2-2.6.4 0 .8-.1 1.1-.1 3.8-.4 5-8.1 5-8.2.01-.11.09-.19.2-.2h8c.1.01.19.1.2.2 0 .1 1.2 7.8 5 8.2.4 0 .7.1 1.1.1 2.8.2 6.7.6 8.1 2.8.7 1.31.7 2.89 0 4.2-.7 1.9-1.9 4.8-3.1 7.7-2.6 6.5-5.9 14.6-5.8 16 .35 1.09.89 2.1 1.6 3 2.5 3.8 6.6 10.2 6.1 17.7 0-.15 0 0-.1 0m-34-.4h33.7c.5-7.3-3.6-13.5-6-17.3-1-1.5-1.7-2.5-1.7-3.1-.1-1.4 3-9.3 5.8-16.2 1.2-3 2.3-5.8 3.1-7.7.6-1.6.6-2.9 0-3.8-1.3-2-5.1-2.3-7.8-2.6-.4 0-.8-.1-1.1-.1-3.8-.4-5.1-7.1-5.3-8.3h-7.6c-.2 1.2-1.5 7.9-5.3 8.3-.4 0-.7.1-1.1.1-2.8.3-6.6.6-7.8 2.6-.6.9-.6 2.2 0 3.8.7 2 1.8 4.8 3 7.7 2.8 6.9 6 14.7 5.8 16.2-.4 1.12-.97 2.17-1.7 3.1-2.4 3.75-6.5 10-6 17.25z'
        />
        <path
          fill='#80583F'
          d='M22.22 17.669h-6.4c-4.3 0-6.7-2.9-7.6-9-.01-.1.07-.19.18-.2h.01c.11 0 .2.09.21.2.8 5.9 3.1 8.6 7.2 8.6h6.4c4.1 0 6.4-2.7 7.2-8.6-.01-.1.07-.19.18-.2h.01c.11 0 .2.09.21.2-.9 6.28-3.3 9-7.6 9M4.622 24.67c-.2-.1-.2-.2-.2-.3 1.95-4.81 2.2-10.13.7-15.1-.02-.11.02-.22.1-.3.11-.01.22.03.3.1a23.13 23.13 0 0 1-.7 15.5c-.1.1-.1.1-.2.1M33.4 24.67c-.1 0-.2 0-.2-.1a23.07 23.07 0 0 1-.7-15.5c.08-.07.19-.11.3-.1.1.03.16.13.13.23v.06999999999999999c-1.5 5-1.26 10.36.7 15.2 0 0 0 .1-.2.2z'
        />
      </svg>
    ),
    'Cowl Neckline': (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='38'
        height='60'
        fill='none'
        viewBox='0 0 38 60'
      >
        <g clipPath='url(#clip0_12360_26918)'>
          <path
            fill='#fff'
            d='M28.151 8.54c-4-.4-5.2-8.29-5.2-8.29h-8s-1.2 7.89-5.2 8.29-11.5.4-9.1 6.79 9.1 21.86 8.9 23.76c-.2 2-8.5 10.28-7.7 20.66h34.3c.8-10.28-7.5-18.67-7.7-20.66-.2-2 6.5-17.47 8.9-23.76s-5.2-6.39-9.2-6.79'
          />
          <path
            fill='#fff'
            d='M28.151 8.55c-4-.4-5.2-8.3-5.2-8.3h-8s-1.2 7.9-5.2 8.3-11.5.4-9.1 6.7 9.1 21.8 8.9 23.8-8.5 10.3-7.7 20.6h34.3c.8-10.3-7.5-18.6-7.7-20.6s6.5-17.4 8.9-23.8c2.3-6.4-5.2-6.4-9.2-6.7'
          />
          <path
            className='fill-f8f3ef group-[.svg-selected]_fill-BE9983 lg_group-hover_fill-BE9983'
            d='m5.252 10.742.2 16.07 4.3 12.28s-4 6.59-4.9 8.48c-.9 2-3.3 6.69-2.8 12.08h34.2s.1-4.49-.8-7.19c0 0-3.6-9.48-5.9-11.38l-1-2.1 1.8-5.99 2.7-7.09s-.5-10.58-.3-12.18.1-3.49.1-4.69c0-.1-.94.04-.94.04-.01.56-.01 1.33-.33 1.81-1.88 2.8-8.57 3.93-12.33 3.93-5.8 0-13.21-2.6-12.93-5.75.02-.12-.77-.11-1.17.09z'
          />
          <path
            fill='#80583F'
            d='M5.16 25.87c-.1 0-.2-.1-.2-.19.6-6.78 0-16.15 0-16.24 0-.1.1-.19.2-.19s.2.1.2.19c0 .1.6 9.56 0 16.34 0-.01-.1.09-.2.09M32.963 25.868c-.1 0-.2-.1-.2-.19-.6-6.78 0-16.24 0-16.34s.1-.19.2-.19.2.1.2.19c0 .1-.6 9.46 0 16.24 0 .2-.1.29-.2.29'
          />
          <path
            stroke='#80583F'
            strokeWidth='0.4'
            d='m28.13 8.748.9.06c.79.05 1.67.11 2.55.21 1.22.14 2.44.37 3.46.78s1.82.99 2.23 1.84c.41.84.46 1.98-.1 3.55-.74 1.96-1.87 4.77-3.1 7.78-.77 1.9-1.57 3.87-2.33 5.77-.98 2.45-1.87 4.77-2.51 6.6-.32.91-.58 1.71-.75 2.34-.09.32-.15.59-.19.83-.04.23-.06.43-.04.58.03.3.2.68.45 1.11.25.45.59.98.99 1.59.09.13.18.27.27.41.75 1.15 1.66 2.55 2.55 4.15 1.97 3.55 3.8 8.1 3.46 13.12H2.04c-.34-5.02 1.5-9.56 3.46-13.12.89-1.6 1.8-3 2.55-4.15.09-.14.18-.28.27-.41.4-.61.74-1.14.99-1.59.24-.44.42-.82.45-1.11.02-.15 0-.35-.04-.58s-.1-.51-.19-.83c-.17-.63-.43-1.43-.75-2.35-.64-1.84-1.53-4.16-2.51-6.62-.84-2.12-1.75-4.34-2.6-6.44-1.12-2.75-2.15-5.28-2.83-7.07-.59-1.54-.56-2.66-.16-3.49s1.18-1.41 2.2-1.82c1.01-.43 2.23-.66 3.45-.81.79-.1 1.57-.17 2.29-.23.4-.03.79-.07 1.15-.1 1.07-.11 1.94-.71 2.63-1.54.69-.82 1.23-1.87 1.63-2.89a21 21 0 0 0 1.05-3.7q.015-.105.03-.18h7.66q.015.075.03.18a20.564 20.564 0 0 0 1.05 3.7c.4 1.02.94 2.07 1.63 2.89.72.82 1.59 1.43 2.65 1.54Z'
          />
          <path
            stroke='#80583F'
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth='0.4'
            d='M6.62 11.148c.39 2.2 4.42 7.08 12.79 9.06m12.78-9.06c-.19 3.16-3.71 10.21-13.94 11.53m-12.2-11.53c.19 4.25 4.17 13.34 16.27 14M6.602 10.719c2.51 2.45 7.23 4.11 12.66 4.11s10.15-1.66 12.66-4.11'
          />
          <path
            stroke='#80583F'
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth='0.4'
            d='M31.91 9.094s-.03 1.59-.09 1.72M6.41 9.094s.03 1.59.09 1.72'
          />
        </g>
        <defs>
          <clipPath id='clip0_12360_26918'>
            <path
              fill='#fff'
              d='M.191.25h37.62v59.5H.191z'
            />
          </clipPath>
        </defs>
      </svg>
    ),
  },
  'Back Detail': {
    Buttons: (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='38'
        height='60'
        fill='none'
        viewBox='0 0 38 60'
      >
        <path
          fill='#fff'
          d='M28.1 8.597c-4-.4-5.2-8.3-5.2-8.3h-8s-1.2 7.9-5.2 8.3-11.3.4-9 6.7 9.1 21.8 8.9 23.8-8.5 10.3-7.7 20.6h34.2c.8-10.3-7.5-18.7-7.7-20.6s6.5-17.5 8.9-23.8-5.2-6.3-9.2-6.7'
        />
        <path
          fill='#80583F'
          d='M36.1 60H1.9a.215.215 0 0 1-.2-.2C1.1 52.3 5.3 45.9 7.8 42c.9-1.4 1.6-2.4 1.6-2.9.1-1.3-3.2-9.5-5.8-16-1.2-3-2.3-5.8-3.1-7.7-.63-1.34-.6-2.89.1-4.2C2 9 5.9 8.7 8.7 8.4c.4 0 .8-.1 1.1-.1 3.8-.4 5-8.1 5-8.1.01-.11.09-.19.2-.2h8c.11.01.19.09.2.2 0 .1 1.2 7.8 5 8.1.4 0 .7.1 1.1.1 2.8.2 6.8.6 8.1 2.8.7 1.31.73 2.86.1 4.2-.7 1.9-1.9 4.8-3.1 7.7-2.6 6.5-5.9 14.7-5.8 16 .34 1.06.88 2.05 1.6 2.9 2.5 3.8 6.7 10.2 6.1 17.7-.04.2-.1.3-.2.3m-34-.5h33.7c.5-7.3-3.6-13.5-6-17.2-1-1.5-1.7-2.5-1.7-3.1C28 37.8 31 30.3 34 23c1.2-3 2.3-5.8 3.1-7.7.61-1.19.61-2.61 0-3.8-1.3-2-5-2.3-7.8-2.6-.4 0-.8-.1-1.1-.1-3.8-.4-5.1-7.1-5.3-8.3h-7.64c-.2 1.3-1.5 8-5.3 8.3-.4 0-.7.1-1.1.1-2.9.2-6.7.5-7.9 2.5-.6.9-.6 2.2 0 3.8.7 2 1.8 4.8 3 7.7 2.9 7.3 6 14.8 5.9 16.2-.4 1.12-.97 2.17-1.7 3.1C5.7 46 1.7 52.3 2.1 59.5'
        />
        <path
          className='fill-f8f3ef group-[.svg-selected]_fill-BE9983 lg_group-hover_fill-BE9983'
          d='M28.4 39.1c.09-.75.26-1.48.5-2.2l4.8-12.7c-1-4.9-.3-10 .3-14.9L32.4 9 19.5 19.9 5.2 9.1l-1.7.4c.5 3.5 1.4 10.4.8 14.7l4.9 12.7c.24.75.41 1.52.5 2.3-.2 2-8.5 10.3-7.7 20.6h34.1c.8-10.4-7.6-18.7-7.7-20.7'
        />
        <path
          fill='#80583F'
          d='M36.099 59.997h-34.2a.215.215 0 0 1-.2-.2c-.6-7.5 3.6-13.9 6.1-17.8.9-1.4 1.6-2.4 1.6-2.9-.1-.75-.27-1.48-.5-2.2l-4.8-12.6v-.1c.7-4.7-.4-12.4-.8-14.6 0-.1.1-.2.2-.3l1.7-.4h.2l14.1 10.7 12.7-10.7c.1 0 .1-.1.2-.1l1.6.3c.1 0 .2.1.2.3-.6 4.6-1.3 9.9-.3 14.8v.1l-4.8 12.7c-.23.68-.4 1.39-.5 2.1.34 1.06.88 2.05 1.6 2.9 2.5 3.8 6.7 10.2 6.1 17.7-.04.2-.1.3-.2.3m-33.9-.5h33.7c.5-7.3-3.6-13.5-6-17.2-1-1.5-1.7-2.5-1.7-3.2.05-.79.22-1.56.5-2.3l4.8-12.6c-.9-4.9-.3-10 .3-14.6l-1.3-.2-12.9 10.7a.19.19 0 0 1-.27.03l-.03-.03-14.1-10.8-1.4.4c.5 3 1.4 10 .8 14.5l4.8 12.6c.34.75.51 1.57.5 2.4-.41 1.14-.98 2.22-1.7 3.2-2.5 3.6-6.5 9.9-6 17.1'
        />
        <path
          fill='#F8F3EF'
          d='M29.1 36.398c-12.1 2.8-20.1 0-20.1 0z'
        />
        <path
          fill='#80583F'
          d='M18.5 37.898c-3.25.07-6.49-.36-9.6-1.3-.11-.06-.16-.19-.1-.3s.19-.16.3-.1c.1 0 8.1 2.8 19.9 0 .1 0 .3 0 .3.2s0 .3-.2.3c-3.49.74-7.04 1.14-10.6 1.2'
        />
        <path
          fill='#80583F'
          d='M19.263 37.603a.215.215 0 0 1-.2-.2l.1-17.5c.01-.11.09-.19.2-.2.11.01.19.09.2.2l-.1 17.5c-.01.11-.09.19-.2.2'
        />
        <path
          fill='#F8F3EF'
          d='M19.898 20.797a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1'
        />
        <path
          fill='#80583F'
          d='M19.9 21.002c-.39 0-.7-.31-.7-.7s.31-.7.7-.7.7.31.7.7c.01.38-.28.69-.66.7zm0-1.1c-.22 0-.4.18-.4.4s.18.4.4.4.4-.18.4-.4a.44.44 0 0 0-.4-.4'
        />
        <path
          fill='#F8F3EF'
          d='M19.898 24.102a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1'
        />
        <path
          fill='#80583F'
          d='M19.9 24.298c-.39 0-.7-.31-.7-.7s.31-.7.7-.7.7.31.7.7c.01.38-.28.69-.66.7zm0-1c-.22 0-.4.18-.4.4s.18.4.4.4.4-.18.4-.4a.37.37 0 0 0-.34-.4z'
        />
        <path
          fill='#F8F3EF'
          d='M19.898 27.5a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1'
        />
        <path
          fill='#80583F'
          d='M19.9 27.697c-.39 0-.7-.31-.7-.7s.31-.7.7-.7.7.31.7.7c.01.38-.28.69-.66.7zm0-1.1c-.22 0-.4.18-.4.4s.18.4.4.4.4-.18.4-.4a.44.44 0 0 0-.4-.4'
        />
        <path
          fill='#F8F3EF'
          d='M19.898 30.797a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1'
        />
        <path
          fill='#80583F'
          d='M19.9 31.002c-.39 0-.7-.31-.7-.7s.31-.7.7-.7.7.31.7.7c.01.38-.28.69-.66.7zm0-1.1c-.22 0-.4.18-.4.4s.18.4.4.4.4-.18.4-.4a.44.44 0 0 0-.4-.4'
        />
        <path
          fill='#F8F3EF'
          d='M19.898 34.102a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1'
        />
        <path
          fill='#80583F'
          d='M19.9 34.298c-.39 0-.7-.31-.7-.7s.31-.7.7-.7.7.31.7.7c.01.38-.28.69-.66.7zm0-1c-.22 0-.4.18-.4.4s.18.4.4.4.4-.18.4-.4a.37.37 0 0 0-.34-.4z'
        />
        <path
          fill='#F8F3EF'
          d='M19.898 37.5a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1'
        />
        <path
          fill='#80583F'
          d='M19.9 37.697c-.39 0-.7-.31-.7-.7s.31-.7.7-.7.7.31.7.7c-.03.38-.32.68-.7.7m0-1.1c-.22 0-.4.18-.4.4s.18.4.4.4.4-.18.4-.4a.44.44 0 0 0-.4-.4'
        />
      </svg>
    ),
    Backless: (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='38'
        height='60'
        fill='none'
        viewBox='0 0 38 60'
      >
        <path
          fill='#fff'
          d='M28.063 8.597c-4-.4-5.2-8.3-5.2-8.3h-8s-1.2 7.9-5.2 8.3-11.3.4-8.9 6.7 9.1 21.8 8.9 23.8-8.5 10.3-7.7 20.6h34.1c.8-10.3-7.5-18.6-7.7-20.6s6.5-17.5 8.9-23.8-5.2-6.3-9.2-6.7'
        />
        <path
          className='fill-f8f3ef group-[.svg-selected]_fill-BE9983 lg_group-hover_fill-BE9983'
          d='M8.702 35.602c.49 1.11.79 2.29.9 3.5-.2 2-8.5 10.3-7.7 20.6h34.16c.8-10.3-7.5-18.6-7.7-20.6.15-1.2.45-2.38.9-3.5-11.9 5.8-20.6 0-20.6 0'
        />
        <path
          fill='#80583F'
          d='M18.7 38.398c-3.53.05-7.02-.84-10.1-2.57l.1-.2-.2-.2.1-.1.2.1c.1.1 8.7 5.6 20.3 0l.2.4a22.85 22.85 0 0 1-10.6 2.57'
        />
        <path
          fill='#80583F'
          d='M36.06 60H1.9a.215.215 0 0 1-.2-.2C1.1 52.2 5.3 45.9 7.8 42c.9-1.4 1.6-2.4 1.6-2.9.1-1.3-3.2-9.4-5.8-16-1.2-3-2.3-5.8-3.1-7.7-.63-1.34-.6-2.89.1-4.2C2 9 5.9 8.7 8.7 8.4c.4 0 .8-.1 1.1-.1 3.7-.4 4.9-8 4.9-8.1s.2-.2.3-.2h8c.11.01.19.09.2.2 0 .1 1.2 7.7 4.9 8.1.4 0 .7.1 1.1.1 2.9.2 6.8.6 8.2 2.8.7 1.31.73 2.86.1 4.2-.7 1.9-1.9 4.8-3.1 7.7-2.6 6.5-5.9 14.6-5.8 16 .34 1.06.88 2.05 1.6 2.9 2.5 3.8 6.7 10.2 6.1 17.7 0 .2-.1.3-.24.3m-33.9-.5h33.7c.5-7.3-3.6-13.5-6-17.2-1-1.5-1.7-2.5-1.7-3.2-.2-1.3 2.3-7.5 5.8-16.1 1.2-3 2.3-5.8 3.1-7.7.61-1.19.61-2.61 0-3.8-1.3-2-5-2.3-7.8-2.5-.4 0-.8-.1-1.1-.1-3.8-.4-5.1-7-5.3-8.3h-7.5c-.2 1.3-1.6 8-5.3 8.3-.4 0-.7.1-1.1.1-3 .2-6.7.5-8 2.5-.6.9-.6 2.2 0 3.8.7 1.9 1.8 4.7 3 7.7 3.5 8.6 6 14.9 5.8 16.2-.41 1.14-.98 2.22-1.7 3.2C5.8 46 1.7 52.2 2.2 59.5z'
        />
        <path
          fill='#80583F'
          d='M28.062 8.83h-.1c-8.1-4-16.5-.6-16.5-.6-.11.06-.24.01-.3-.1s-.01-.24.1-.3c.1 0 8.6-3.5 17 .6.1.1.2.2.1.3a.37.37 0 0 1-.3.1'
        />
        <path
          fill='#80583F'
          d='M14.3 13.9c-.21.02-.41-.02-.6-.1-.5-.2-.7-.5-.7-.8-.3-1.9 5.5-6.1 6.1-6.6a.19.19 0 0 1 .3 0c.08.08.11.19.1.3-.1.2-2 6-4.4 7.1-.26.09-.53.12-.8.1m4.5-6.6c-2.4 1.8-5.4 4.5-5.3 5.7 0 .1.1.3.4.4.33.12.7.08 1-.1 1.7-.8 3.3-4.4 3.9-6'
        />
        <path
          fill='#80583F'
          d='M22.802 13.502c-.32-.01-.64-.11-.9-.3-1.9-1.2-2.8-6.4-2.9-6.6a.37.37 0 0 1 .1-.3c.1-.1.2 0 .3 0 .5.4 5.1 4.3 4.7 6.2-.14.46-.53.81-1 .9a.37.37 0 0 1-.3.1m-3.1-6.2c.3 1.4 1.2 4.7 2.5 5.5.24.15.52.22.8.2.5-.1.6-.4.6-.5.3-1.2-2.4-3.9-3.9-5.2'
        />
        <path
          fill='#80583F'
          d='M19.302 28.498c-.2 0-.3-.2-.2-.3 1.2-6 .4-16.7.1-20.3-.8 2.8-2.7 9.9-2.6 15.6 0 .1-.1.2-.2.3a.215.215 0 0 1-.2-.2c-.1-7.4 3-16.9 3-17s.1-.2.3-.2c.11.01.19.09.2.2 0 .1 1.4 14.4 0 21.7-.2.1-.3.2-.4.2'
        />
      </svg>
    ),
    'Lace-up': (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='38'
        height='61'
        fill='none'
        viewBox='0 0 38 61'
      >
        <path
          fill='#fff'
          d='M28.102 9.097c-4-.4-5.2-8.3-5.2-8.3h-8s-1.2 7.9-5.2 8.3-11.3.4-8.9 6.7 9.1 21.8 8.9 23.8-8.5 10.3-7.7 20.6h34.1c.8-10.3-7.5-18.6-7.7-20.6s6.5-17.5 8.9-23.8-5.2-6.3-9.2-6.7'
        />
        <path
          className='fill-f8f3ef group-[.svg-selected]_fill-BE9983 lg_group-hover_fill-BE9983'
          d='m34 24.398-5.1 13-.9 3.1c1.9 2.29 3.61 4.73 5.1 7.3a7.07 7.07 0 0 1-.2 7.9l-13.9 2.4-13.8-2.4a7.07 7.07 0 0 1-.2-7.9c1.5-2.57 3.21-5.01 5.1-7.3l-.9-3.1-4.8-12.4s11.7 2.2 15.7 2.2c4.77-.05 9.48-1 13.9-2.8'
        />
        <path
          className='fill-f8f3ef group-[.svg-selected]_fill-BE9983 lg_group-hover_fill-BE9983'
          d='M8.702 36.102c.49 1.11.79 2.29.9 3.5-.2 2-8.5 10.3-7.7 20.6h34.2c.8-10.3-7.5-18.6-7.7-20.6.15-1.2.45-2.38.9-3.5-11.9 5.8-20.6 0-20.6 0M15.9 27.098c-3.92-.4-7.8-1.14-11.6-2.2z'
        />
        <path
          fill='#80583F'
          d='M15.9 27.302c-3.96-.35-7.88-1.09-11.7-2.2-.1 0-.2-.2-.2-.3s.2-.2.3-.2a75 75 0 0 0 11.6 2.2c.1 0 .2.1.2.3s0 .2-.2.2M22.2 27.398a.215.215 0 0 1-.2-.2c0-.1.1-.3.2-.3a34.2 34.2 0 0 0 11.6-2.7c.11-.06.24-.01.3.1s.01.24-.1.3c-4.3 2.1-9.7 2.6-11.8 2.8'
        />
        <path
          fill='#80583F'
          d='M36.1 60.5H1.9a.215.215 0 0 1-.2-.2c-.6-7.6 3.6-13.9 6.1-17.8.9-1.4 1.6-2.4 1.6-2.9.1-1.3-3.2-9.4-5.8-16-1.2-3-2.3-5.8-3.1-7.7-.63-1.34-.6-2.89.1-4.2C2 9.5 5.9 9.2 8.7 8.9c.4 0 .8-.1 1.1-.1 3.7-.4 4.9-8 4.9-8.1s.2-.2.3-.2h8c.11.01.19.09.2.2 0 .1 1.2 7.7 4.9 8.1.4 0 .7.1 1.1.1 2.9.2 6.8.6 8.2 2.8.7 1.31.73 2.86.1 4.2-.7 1.9-1.9 4.8-3.1 7.7-2.6 6.5-5.9 14.6-5.8 16 .34 1.06.88 2.05 1.6 2.9 2.5 3.8 6.7 10.2 6.1 17.7 0 .2-.1.3-.2.3M2.2 60h33.7c.5-7.3-3.6-13.5-6-17.2-1-1.5-1.7-2.5-1.7-3.2-.2-1.3 2.3-7.5 5.8-16.1 1.2-3 2.3-5.8 3.1-7.7.61-1.19.61-2.61 0-3.8-1.3-2-5-2.3-7.8-2.5-.4 0-.8-.1-1.1-.1-3.8-.4-5.1-7-5.3-8.3h-7.5c-.2 1.3-1.6 8-5.3 8.3-.4 0-.7.1-1.1.1-3 .2-6.7.5-8 2.5-.6.9-.6 2.2 0 3.8.7 1.9 1.8 4.7 3 7.7 3.5 8.6 6 14.9 5.8 16.2-.41 1.14-.98 2.22-1.7 3.2-2.3 3.6-6.4 9.8-5.9 17.1'
        />
        <path
          fill='#F8F3EF'
          d='m23.2 27.102-4.1 14.13-4.1-14.1c2.6.1 5.3.1 8.2-.03'
        />
        <path
          fill='#80583F'
          d='M19.1 41.497a.215.215 0 0 1-.2-.2l-4.1-14.2v-.2c.1-.1.1-.1.2-.1q4.05.225 8.1 0c.1 0 .2 0 .2.1s.1.1 0 .2l-4.1 14.13c.1.17 0 .27-.1.27m-3.7-14.2 3.7 13 3.7-13c-2.46.2-4.94.2-7.4 0'
        />
        <path
          fill='#80583F'
          d='m22.9 29.603-7.7-1.9c-.1 0-.2-.2-.2-.3s.2-.2.3-.2l7.7 1.9c.1 0 .2.2.2.3s-.2.2-.3.2'
        />
        <path
          fill='#80583F'
          d='M15.302 29.603a.215.215 0 0 1-.2-.2c0-.1 0-.3.2-.3l7.7-1.9c.1 0 .3 0 .3.2s0 .3-.2.3z'
        />
        <path
          fill='#80583F'
          d='m22.9 31.502-7.7-1.9c-.1 0-.2-.2-.2-.3s.2-.2.3-.2l7.7 1.9c.1 0 .2.2.2.3s-.2.2-.3.2'
        />
        <path
          fill='#80583F'
          d='M15.302 31.502a.215.215 0 0 1-.2-.2c0-.1 0-.3.2-.3l7.7-1.9c.1 0 .3 0 .3.2s0 .3-.2.3z'
        />
        <path
          fill='#80583F'
          d='m22.9 33.4-7.7-1.9c-.1 0-.2-.2-.2-.3s.2-.2.3-.2l7.7 1.9c.1 0 .2.2.2.3s-.2.2-.3.2'
        />
        <path
          fill='#80583F'
          d='M15.302 33.4a.215.215 0 0 1-.2-.2c0-.1 0-.3.2-.3l7.7-1.9c.1 0 .3 0 .3.2s0 .3-.2.3z'
        />
        <path
          fill='#80583F'
          d='m22.9 35.306-7.7-1.9c-.1 0-.2-.2-.2-.3s.2-.2.3-.2l7.7 1.9c.1 0 .2.2.2.3s-.2.2-.3.2'
        />
        <path
          fill='#80583F'
          d='M15.302 35.306a.215.215 0 0 1-.2-.2c0-.1 0-.3.2-.3l7.7-1.9c.1 0 .3 0 .3.2s0 .3-.2.3z'
        />
        <path
          fill='#80583F'
          d='m22.9 37.236-7.7-1.9c-.1 0-.2-.2-.2-.3s.2-.2.3-.2l7.7 1.9c.1 0 .2.2.2.3s-.2.2-.3.2'
        />
        <path
          fill='#80583F'
          d='M15.302 37.236a.215.215 0 0 1-.2-.2c0-.1 0-.3.2-.3l7.7-1.9c.1 0 .3 0 .3.2s0 .3-.2.3z'
        />
        <path
          fill='#80583F'
          d='m22.9 39.104-7.7-1.87c-.1 0-.2-.2-.2-.3s.2-.2.3-.2l7.7 1.9c.1 0 .2.2.2.3s-.2.17-.3.17'
        />
        <path
          fill='#80583F'
          d='M15.302 39.103a.215.215 0 0 1-.2-.2c0-.1 0-.3.2-.3l7.7-1.9c.1 0 .3 0 .3.2s0 .3-.2.3z'
        />
        <path
          fill='#80583F'
          d='m22.9 41.002-7.7-1.9c-.1 0-.2-.2-.2-.3s.2-.2.3-.2l7.7 1.9c.1 0 .2.2.2.3s-.2.2-.3.2'
        />
        <path
          fill='#80583F'
          d='M15.302 41.002a.215.215 0 0 1-.2-.2c0-.1 0-.3.2-.3l7.7-1.9c.1 0 .3 0 .3.2s0 .3-.2.3z'
        />
        <path
          fill='#80583F'
          d='M22.9 42.9 15.2 41c-.1 0-.2-.2-.2-.3s.2-.2.3-.2l7.7 2c.1 0 .2.2.2.3a.37.37 0 0 1-.3.1'
        />
        <path
          fill='#80583F'
          d='M15.302 42.9a.215.215 0 0 1-.2-.2c0-.1 0-.3.2-.3l7.7-1.9c.1 0 .3 0 .3.2s0 .3-.2.3z'
        />
        <path
          fill='#80583F'
          d='m22.9 44.806-7.7-1.9c-.1 0-.2-.2-.2-.3s.2-.2.3-.2l7.7 1.9c.1 0 .2.2.2.3s-.2.2-.3.2'
        />
        <path
          fill='#80583F'
          d='M15.302 44.806a.215.215 0 0 1-.2-.2c0-.1 0-.3.2-.3l7.7-1.9c.1 0 .3 0 .3.2s0 .3-.2.3z'
        />
        <path
          fill='#80583F'
          d='m22.9 46.806-7.7-1.9c-.1 0-.2-.2-.2-.3s.2-.2.3-.2l7.7 1.9c.1 0 .2.2.2.3s-.2.2-.3.2'
        />
        <path
          fill='#80583F'
          d='M15.302 46.806a.215.215 0 0 1-.2-.2c0-.1 0-.3.2-.3l7.7-1.9c.1 0 .3 0 .3.2s0 .3-.2.3zM15.002 27.298a.35.35 0 0 1-.2-.1l-7.6-17.5c-.1-.1 0-.2.1-.3.08-.06.2-.05.27.03.02.02.03.04.03.07l7.6 17.5c.1.1.1.3-.2.3M23.2 27.4c-.2-.1-.3-.2-.2-.3l7.4-17.6c.1-.1.2-.2.3-.1s.2.2.1.3l-7.4 17.53c-.1.07-.1.17-.2.17'
        />
      </svg>
    ),
    'X-Back': (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='38'
        height='60'
        fill='none'
        viewBox='0 0 38 60'
      >
        <g clipPath='url(#clip0_12360_10409)'>
          <path
            fill='#fff'
            d='M28.102 8.597c-4-.4-5.2-8.3-5.2-8.3h-8s-1.2 7.9-5.2 8.3-11.3.4-8.9 6.7 9.1 21.8 8.9 23.8-8.5 10.3-7.7 20.6h34.1c.8-10.3-7.5-18.6-7.7-20.6s6.5-17.5 8.9-23.8c2.3-6.3-5.2-6.3-9.2-6.7'
          />
          <path
            className='fill-f8f3ef group-[.svg-selected]_fill-BE9983 lg_group-hover_fill-BE9983'
            d='M8.702 35.602c.6 1.8 1 3 .9 3.5-.2 2-8.5 10.3-7.7 20.6h34.2c.8-10.3-7.5-18.6-7.7-20.6 0-.5.3-1.8.9-3.5-11.9 5.8-20.6 0-20.6 0'
          />
          <path
            fill='#80583F'
            d='M18.702 38.398c-6.1 0-10.1-2.6-10.1-2.6l.3-.4c.1.1 8.7 5.6 20.3 0l.2.4c-4 2-7.6 2.6-10.7 2.6'
          />
          <path
            fill='#80583F'
            d='M36.1 60H1.9c-.1 0-.2-.1-.2-.2-.6-7.6 3.6-13.9 6.1-17.8.9-1.4 1.6-2.4 1.6-2.9.1-1.3-3.2-9.4-5.8-16-1.2-3-2.3-5.8-3.1-7.7-.6-1.7-.6-3.1.1-4.2C2 9 5.9 8.7 8.7 8.4c.4 0 .8-.1 1.1-.1 3.7-.4 4.9-8 4.9-8.1.1-.1.2-.2.3-.2h8c.1 0 .2.1.2.2s1.2 7.7 4.9 8.1c.4 0 .7.1 1.1.1 2.9.2 6.8.6 8.2 2.8.7 1.1.7 2.5.1 4.2-.7 1.9-1.9 4.8-3.1 7.7-2.6 6.5-5.9 14.6-5.8 16 0 .5.7 1.6 1.6 2.9 2.5 3.8 6.7 10.2 6.1 17.7 0 .2-.1.3-.2.3m-33.9-.5h33.7c.5-7.3-3.6-13.5-6-17.2-1-1.5-1.7-2.5-1.7-3.2-.2-1.3 2.3-7.5 5.8-16.1 1.2-3 2.3-5.8 3.1-7.7.6-1.6.6-2.8 0-3.8-1.3-2-5-2.3-7.8-2.5-.4 0-.8-.1-1.1-.1-3.8-.4-5.1-7-5.3-8.3h-7.5c-.2 1.3-1.6 8-5.3 8.3-.4 0-.7.1-1.1.1-3 .2-6.7.5-8 2.5-.6.9-.6 2.2 0 3.8.7 1.9 1.8 4.7 3 7.6 3.5 8.6 6 14.9 5.8 16.2-.1.6-.7 1.6-1.7 3.2-2.3 3.7-6.4 9.9-5.9 17.2'
          />
          <path
            className='fill-f8f3ef group-[.svg-selected]_fill-BE9983 lg_group-hover_fill-BE9983'
            d='m8.102 8.798 9.4 11.1 1.5-.9-7.6-10.6z'
          />
          <path
            fill='#80583F'
            d='M17.5 20.202c-.1 0-.1 0-.2-.1L8 8.902q-.15-.15 0-.3s0-.1.1-.1l3.3-.4c.1 0 .2 0 .2.1l7.6 10.7c0 .1.1.1 0 .2 0 .1-.1.1-.1.2l-1.5.9zm-8.9-11.2 9 10.7 1.1-.7-7.4-10.4z'
          />
          <path
            className='fill-f8f3ef group-[.svg-selected]_fill-BE9983 lg_group-hover_fill-BE9983'
            d='M4.8 25.6s10.7-1.5 12.7-5.7L19 19v3.3c-1.3 4.1-5.2 11.1-10.3 13.2z'
          />
          <path
            fill='#80583F'
            d='M8.702 35.797c-.2 0-.2-.1-.2-.2l-3.9-10v-.2c0-.1.1-.1.2-.1s10.6-1.5 12.5-5.5l.1-.1 1.5-.9h.2c.1 0 .1.1.1.2v3.4c-1.3 4.2-5.2 11.2-10.5 13.4m-3.5-10 3.7 9.5c4.9-2.3 8.6-8.9 9.9-13v-2.8l-1.1.6c-1.9 3.8-10.7 5.4-12.5 5.7'
          />
          <path
            className='fill-f8f3ef group-[.svg-selected]_fill-BE9983 lg_group-hover_fill-BE9983'
            d='M33.2 25.6s-10.7-1.5-12.7-5.7L19 19v3.3c1.3 4.1 5.2 11.1 10.3 13.2z'
          />
          <path
            fill='#80583F'
            d='M29.4 35.797q-.15 0 0 0c-5.3-2.2-9.2-9.2-10.6-13.4v-3.4c0-.1 0-.2.1-.2h.2l1.5.9.1.1c1.9 4 12.4 5.5 12.5 5.5s.1.1.2.1v.2l-3.9 10c.1.2 0 .2-.1.2m-10.1-13.5c1.3 4 5 10.7 9.9 13l3.7-9.5c-1.8-.3-10.6-1.8-12.5-5.7l-1.1-.6z'
          />
          <path
            className='fill-f8f3ef group-[.svg-selected]_fill-BE9983 lg_group-hover_fill-BE9983'
            d='m29.904 8.798-9.4 11.1-1.5-.9 7.7-10.6z'
          />
          <path
            fill='#80583F'
            d='M20.502 20.202h-.1l-1.4-.9c-.1 0-.1-.1-.1-.2v-.2l7.6-10.7c.1-.1.1-.1.2-.1l3.3.4c.1 0 .2.1.2.2v.3l-9.5 11.2zm-1.1-1.2 1 .6 9-10.7-2.7-.3z'
          />
        </g>
        <defs>
          <clipPath id='clip0_12360_10409'>
            <path
              fill='#fff'
              d='M0 0h38v60H0z'
            />
          </clipPath>
        </defs>
      </svg>
    ),
    'Self Tie': (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='38'
        height='60'
        fill='none'
        viewBox='0 0 38 60'
      >
        <path
          fill='#fff'
          d='M28.102 8.597c-4-.4-5.2-8.3-5.2-8.3h-8s-1.2 7.9-5.2 8.3-11.3.4-8.9 6.7 9.1 21.8 8.9 23.8-8.5 10.3-7.7 20.6h34.1c.8-10.3-7.5-18.6-7.7-20.6s6.5-17.5 8.9-23.8-5.2-6.3-9.2-6.7'
        />
        <path
          className='fill-f8f3ef group-[.svg-selected]_fill-BE9983 lg_group-hover_fill-BE9983'
          d='M8.659 35.602c.49 1.11.79 2.29.9 3.5-.2 2-8.5 10.3-7.7 20.6h34.2c.8-10.3-7.5-18.6-7.7-20.6.15-1.2.45-2.38.9-3.5-11.9 5.8-20.6 0-20.6 0'
        />
        <path
          fill='#80583F'
          d='M18.663 38.398c-3.54.04-7.02-.86-10.1-2.6l.3-.4c.1.1 8.7 5.6 20.3 0l.2.4c-3.31 1.7-6.98 2.59-10.7 2.6'
        />
        <path
          fill='#80583F'
          d='M36.1 60H1.9a.215.215 0 0 1-.2-.2C1.1 52.2 5.3 45.9 7.8 42c.9-1.4 1.6-2.4 1.6-2.9.1-1.3-3.2-9.4-5.8-16-1.2-3-2.3-5.8-3.1-7.7-.63-1.34-.6-2.89.1-4.2C2 9 5.9 8.7 8.7 8.4c.4 0 .8-.1 1.1-.1 3.7-.4 4.9-8 4.9-8.1s.2-.2.3-.2h8c.11.01.19.09.2.2 0 .1 1.2 7.7 4.9 8.1.3 0 .7.1 1.1.1 2.9.2 6.8.6 8.2 2.8.7 1.31.73 2.86.1 4.2-.7 1.9-1.9 4.8-3.1 7.7-2.6 6.5-5.9 14.6-5.8 16 .34 1.06.88 2.05 1.6 2.9 2.5 3.8 6.7 10.2 6.1 17.7 0 .2-.1.3-.2.3m-33.9-.5h33.6c.5-7.3-3.6-13.5-6-17.2-1-1.5-1.7-2.5-1.7-3.2-.1-1.3 2.4-7.5 5.8-16.2 1.2-3 2.3-5.8 3.1-7.7.61-1.19.61-2.61 0-3.8-1.3-2-5-2.3-7.8-2.5-.4 0-.8-.1-1.1-.1-3.8-.4-5.1-7-5.3-8.3h-7.5c-.3 1.3-1.6 8-5.4 8.3-.4 0-.7.1-1.1.1-2.8.3-6.5.6-7.8 2.6-.6.9-.6 2.2 0 3.8.7 1.9 1.9 4.7 3.1 7.7 3.5 8.6 6 14.9 5.8 16.2-.41 1.14-.98 2.22-1.7 3.2-2.4 3.6-6.54 9.8-6 17.1'
        />
        <path
          className='fill-f8f3ef group-[.svg-selected]_fill-BE9983 lg_group-hover_fill-BE9983'
          d='M8.66 35.6s.701-1.5 6.7-2.3c.9-.1 3.5-.2 3.5-.2v-.8c-1.3-4.1-9.8-6.2-14.9-8.3z'
        />
        <path
          fill='#80583F'
          d='M8.66 35.801a.215.215 0 0 1-.2-.2l-4.6-11.6a.35.35 0 0 1 .29-.4c.04-.01.07-.01.11 0 1.1.5 2.4 1 3.8 1.5 4.7 1.7 10.1 3.7 11.2 7v.9c-.01.11-.09.19-.2.2 0 0-2.7.1-3.5.2-5.7.8-6.5 2.2-6.6 2.2-.06.2-.16.2-.3.2m-4.1-11.5 4.2 10.8c.6-.5 2.2-1.5 6.6-2.1.7-.1 2.7-.2 3.3-.2v-.6c-1-3.1-6.3-5-10.9-6.7-1.07-.34-2.13-.74-3.16-1.2z'
        />
        <path
          className='fill-f8f3ef group-[.svg-selected]_fill-BE9983 lg_group-hover_fill-BE9983'
          d='M29.302 35.6s-.7-1.5-6.7-2.3c-.9-.1-3.5-.2-3.5-.2v-.8c1.3-4.1 9.8-6.2 14.9-8.3z'
        />
        <path
          fill='#80583F'
          d='M29.3 35.901a.35.35 0 0 1-.2-.1s-.9-1.4-6.6-2.2c-.9-.1-3.5-.2-3.5-.2a.215.215 0 0 1-.2-.2v-.9c1.1-3.3 6.4-5.3 11.2-7 1.4-.5 2.7-1 3.8-1.5.19-.03.37.1.4.29.01.04.01.07 0 .11l-4.6 11.7a.19.19 0 0 0-.3 0m-10.1-3.1c.7 0 2.6.1 3.3.2 4.4.6 6 1.6 6.6 2.1l4.2-10.8c-1 .4-2.1.8-3.3 1.3-4.6 1.7-9.8 3.6-10.9 6.7v.5z'
        />
        <path
          className='fill-f8f3ef group-[.svg-selected]_fill-BE9983 lg_group-hover_fill-BE9983'
          d='M19.1 33.097c-.2.2-3.8 4.3-2.8 8.7.6 2.3 1.7 5.3 1.7 5.3l1-11.5.7 16.3s4.1-3.8 1.4-12.7-2-6.1-2-6.1'
        />
        <path
          fill='#80583F'
          d='M19.662 52.199c-.2-.1-.2-.1-.2-.2l-.6-12.4-.6 7.4c-.01.11-.09.19-.2.2-.1 0-.2 0-.3-.2 0 0-1.1-3-1.7-5.3-1-4 2-8 2.7-8.8-.1-.3-.1-.5.1-.5s.3-.1 2.4 6.7c2.7 8.9-1.3 12.8-1.5 12.9.14.1.04.2-.1.2m-.7-16.8c.11.01.19.09.2.2l.8 15.7c.9-1.3 3-5.1.9-12-1-3.4-1.5-5-1.8-5.8-.8.9-3.4 4.5-2.5 8.2.34 1.43.78 2.83 1.3 4.2l.8-10.3c.14-.1.24-.2.34-.2z'
        />
        <path
          className='fill-f8f3ef group-[.svg-selected]_fill-BE9983 lg_group-hover_fill-BE9983'
          d='M19.003 30.598s-9.5-2.4-8.1.4 1.7 4.2 2.9 3.6a39 39 0 0 1 5.2-1.5z'
        />
        <path
          fill='#80583F'
          d='M13.3 34.997a.37.37 0 0 1-.3-.1c-.7-.2-1.1-1.1-1.7-2.6l-.6-1.2c-.21-.37-.21-.83 0-1.2 1.2-1.4 7.6.2 8.4.4.11.01.19.09.2.2v2.4c-.01.11-.09.19-.2.2-1.75.37-3.46.9-5.1 1.6-.17.21-.43.32-.7.3m-.5-5.1c-.8 0-1.5.1-1.7.4 0 .1-.2.2 0 .6l.6 1.2c.6 1.3 1 2.2 1.4 2.3.21.06.42.02.6-.1 1.65-.67 3.36-1.21 5.1-1.6v-2.02c-1.98-.4-3.99-.66-6-.78'
        />
        <path
          className='fill-f8f3ef group-[.svg-selected]_fill-BE9983 lg_group-hover_fill-BE9983'
          d='M19 30.598s9.5-2.4 8.1.4-1.7 4.2-2.9 3.6a39 39 0 0 0-5.2-1.5z'
        />
        <path
          fill='#80583F'
          d='M24.66 34.999c-.24-.03-.48-.1-.7-.2a44 44 0 0 0-5.1-1.6.215.215 0 0 1-.2-.2v-2.4c.01-.11.09-.19.2-.2.7-.2 7.2-1.8 8.4-.4.2.2.3.6 0 1.2l-.6 1.2c-.7 1.5-1.1 2.4-1.7 2.6zm-5.5-2.1c1.74.39 3.45.93 5.1 1.6.18.12.39.16.6.1.4-.2.8-1 1.4-2.3l.6-1.2c.2-.4.1-.6 0-.6-.7-.9-5-.2-7.7.5z'
        />
        <path
          fill='#80583F'
          d='M24.66 34.196a.215.215 0 0 1-.2-.2c-.4-1.5-4.1-1-5.4-.7h-.1c-3.6-1-5.3.8-5.4.8-.11.11-.29.11-.4 0a.284.284 0 0 1 0-.4c.1-.1 2-2 5.8-1 .6-.1 5.3-1 5.9 1.1.1.2 0 .4-.2.4'
        />
      </svg>
    ),
    'Side Zipper': (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='38'
        height='60'
        fill='none'
        viewBox='0 0 38 60'
      >
        <path
          fill='#fff'
          d='M28.14 8.597c-4-.4-5.2-8.3-5.2-8.3h-8s-1.2 7.9-5.2 8.3-11.3.4-8.9 6.7 9.1 21.8 8.9 23.8-8.5 10.3-7.7 20.6h34.1c.8-10.3-7.5-18.6-7.7-20.6s6.5-17.5 8.9-23.8-5.2-6.3-9.2-6.7'
        />
        <path
          className='fill-f8f3ef group-[.svg-selected]_fill-BE9983 lg_group-hover_fill-BE9983'
          d='M33.842 23.903c-2.1 5.1-4.3 10.6-5.1 13.5-3.21.53-6.45.79-9.7.8-3.25 0-6.49-.27-9.7-.8-.7-2.2-2.2-6.1-3.8-10.1l-2.8-17.5c1.84-.62 3.76-.99 5.7-1.1.1.03 4.2 15.2 25.4 15.2'
        />
        <path
          fill='#80583F'
          d='M19.04 38.4c-3.28-.01-6.56-.28-9.8-.8a.215.215 0 0 1-.2-.2c-.7-2.2-2.2-6.2-3.8-10.1v-.1L2.54 9.9a.37.37 0 0 1 .1-.3 20.1 20.1 0 0 1 5.8-1.1c.1 0 .2.1.3.2s4.3 15 25.1 15c.1 0 .2 0 .2.1s.1.2 0 .2c-2 5-4.3 10.6-5.1 13.5-.01.11-.09.19-.2.2a77 77 0 0 1-9.7.7m-9.5-1.3c3.14.53 6.32.79 9.5.8 3.18-.01 6.36-.27 9.5-.8.9-2.8 3-8.2 5-13C13.94 24 8.94 10.8 8.34 9c-1.81.05-3.6.39-5.3 1l2.7 17.2c1.6 3.8 3.1 7.7 3.8 9.9'
        />
        <path
          className='fill-f8f3ef group-[.svg-selected]_fill-BE9983 lg_group-hover_fill-BE9983'
          d='M36.141 59.797H1.901c-.7-10.4 7.6-18.7 7.7-20.7-.05-.62-.18-1.22-.4-1.8 3.21.53 6.45.79 9.7.8 3.25-.01 6.49-.27 9.7-.8-.17.59-.3 1.19-.4 1.8.34 2 8.64 10.3 7.94 20.7'
        />
        <path
          fill='#80583F'
          d='M36.139 60.002H1.899a.215.215 0 0 1-.2-.2c-.6-7.5 3.6-13.9 6.1-17.8.9-1.4 1.6-2.4 1.6-2.9 0-.3-.2-1.1-.3-1.7 0-.1 0-.2.1-.2s.1-.1.2-.1c3.21.53 6.45.79 9.7.8 3.25-.01 6.49-.28 9.7-.8.1 0 .2 0 .2.1.06.05.09.12.1.2-.22.54-.32 1.12-.3 1.7.34 1.06.88 2.05 1.6 2.9 2.5 3.8 6.6 10.2 6.1 17.7-.16.2-.26.3-.36.3m-33.9-.5h33.66c.5-7.2-3.6-13.5-6-17.2-1-1.5-1.7-2.6-1.7-3.2 0-.52.1-1.03.3-1.5-6.19 1-12.51 1-18.7 0q.225.735.3 1.5c0 .6-.7 1.6-1.7 3.2-2.56 3.7-6.66 10-6.16 17.2'
        />
        <path
          fill='#80583F'
          d='M28.74 37.598c-.2-.1-.3-.2-.2-.3l5.1-13.5c.06-.11.19-.16.3-.1s.16.19.1.3l-5.14 13.5c-.04.05-.1.08-.16.1'
        />
        <path
          fill='#80583F'
          d='M33.64 24.5v3l.3-.8V24z'
        />
        <path
          fill='#80583F'
          d='M33.641 27.8c-.2 0-.2-.1-.2-.2v-3.1l.3-.6c.1-.1.2-.2.3-.1.11.01.19.09.2.2v2.9l-.3.8c-.1.03-.2.1-.3.1'
        />
        <path
          fill='#80583F'
          d='M36.14 60H1.9a.215.215 0 0 1-.2-.2C1.1 52.2 5.3 45.9 7.8 42c.9-1.4 1.6-2.4 1.6-2.9.1-1.3-3.2-9.4-5.8-16-1.2-3-2.3-5.8-3.1-7.7-.63-1.34-.6-2.89.1-4.2C2 9 5.9 8.7 8.7 8.4c.4 0 .8-.1 1.1-.1 3.7-.4 4.9-8 4.9-8.1s.2-.2.3-.2h8c.11.01.19.09.2.2 0 .1 1.2 7.7 4.9 8.1.3 0 .7.1 1.1.1 2.9.2 6.8.6 8.2 2.8.7 1.31.73 2.86.1 4.2-.7 1.9-1.9 4.7-3.1 7.7-2.6 6.5-5.9 14.7-5.8 16 .34 1.06.88 2.05 1.6 2.9 2.5 3.8 6.7 10.2 6.1 17.7.04.2-.06.3-.16.3m-33.9-.5h33.6c.5-7.3-3.6-13.5-6-17.2-1-1.5-1.7-2.5-1.7-3.2-.1-1.3 2.4-7.6 5.9-16.2 1.2-3 2.3-5.8 3.1-7.7.61-1.19.61-2.61 0-3.8-1.3-2-5-2.3-7.8-2.5-.4 0-.8-.1-1.1-.1-3.8-.4-5.1-7-5.3-8.3h-7.5c-.4 1.3-1.7 8-5.5 8.3-.4 0-.7.1-1.1.1-2.8.3-6.5.6-7.8 2.6-.6.9-.6 2.2 0 3.8.7 1.9 1.8 4.7 3.1 7.7 3.5 8.6 6 14.9 5.8 16.2-.41 1.14-.98 2.22-1.7 3.2-2.4 3.6-6.5 9.8-6 17.1'
        />
      </svg>
    ),
    Zipper: (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='38'
        height='60'
        fill='none'
        viewBox='0 0 38 60'
      >
        <path
          fill='#fff'
          d='M28.102 8.597c-4-.4-5.2-8.3-5.2-8.3h-8s-1.2 7.9-5.2 8.3-11.3.4-8.9 6.7 9.1 21.8 8.9 23.8-8.5 10.3-7.7 20.6h34.1c.8-10.3-7.5-18.6-7.7-20.6s6.5-17.5 8.9-23.8-5.2-6.3-9.2-6.7'
        />
        <path
          className='fill-f8f3ef group-[.svg-selected]_fill-BE9983 lg_group-hover_fill-BE9983'
          d='m34 23.898-5.1 13-.9 3.1c1.9 2.29 3.61 4.73 5.1 7.3a7.07 7.07 0 0 1-.2 7.9l-13.9 2.4-13.8-2.4a7.07 7.07 0 0 1-.2-7.9c1.5-2.57 3.21-5.01 5.1-7.3l-.9-3.1-4.8-12.43c4.75 1.39 9.66 2.12 14.6 2.2 5.11-.13 10.18-1.06 15-2.77'
        />
        <path
          fill='#80583F'
          d='M18.998 26.9c-4.87-.07-9.71-.78-14.4-2.1-.1 0-.2-.2-.2-.3s.2-.2.3-.2c4.66 1.32 9.46 2.02 14.3 2.1 5.01-.08 9.97-.95 14.7-2.6.11-.06.24-.01.3.1s.01.24-.1.3a43.5 43.5 0 0 1-14.9 2.7'
        />
        <path
          className='fill-f8f3ef group-[.svg-selected]_fill-BE9983 lg_group-hover_fill-BE9983'
          d='M8.702 35.602c.49 1.11.79 2.29.9 3.5-.2 2-8.5 10.3-7.7 20.6h34.2c.8-10.3-7.5-18.6-7.7-20.6.15-1.2.45-2.38.9-3.5-11.9 5.8-20.6 0-20.6 0'
        />
        <path
          fill='#80583F'
          d='M36.1 60H1.9a.215.215 0 0 1-.2-.2C1.1 52.3 5.3 45.9 7.8 42c.9-1.4 1.6-2.4 1.6-2.9.1-1.3-3.2-9.4-5.8-16-1.2-3-2.3-5.8-3.1-7.7-.63-1.34-.6-2.89.1-4.2C2 9 5.9 8.7 8.7 8.4c.4 0 .8-.1 1.1-.1 3.7-.4 4.9-8 4.9-8.1s.2-.2.3-.2h8c.11.01.19.09.2.2 0 .1 1.2 7.7 4.9 8.1.3 0 .7.1 1.1.1 2.9.2 6.8.6 8.2 2.8.7 1.31.73 2.86.1 4.2-.7 1.9-1.9 4.8-3.1 7.7-2.6 6.5-5.9 14.6-5.8 16 .34 1.06.88 2.05 1.6 2.9 2.5 3.8 6.7 10.2 6.1 17.7 0 .2-.1.3-.2.3m-33.9-.5h33.64c.5-7.3-3.6-13.5-6-17.2-1-1.5-1.7-2.5-1.7-3.2-.1-1.3 2.4-7.6 5.8-16.2 1.2-3 2.3-5.8 3.1-7.7.61-1.19.61-2.61 0-3.8-1.3-2-5-2.3-7.8-2.5-.4 0-.8-.1-1.1-.1-3.8-.4-5.1-7-5.3-8.3h-7.5c-.3 1.3-1.6 8-5.4 8.3-.4 0-.7.1-1.1.1-2.8.3-6.5.6-7.8 2.6-.6.9-.6 2.2 0 3.8.7 1.9 1.8 4.7 3.1 7.7 3.5 8.6 6 14.9 5.8 16.2-.41 1.14-.98 2.22-1.7 3.2C5.84 46 1.7 52.2 2.2 59.47z'
        />
        <path
          fill='#80583F'
          d='M19 40.998a.215.215 0 0 1-.2-.2v-14.2c0-.11.09-.2.2-.2s.2.09.2.2v14.1c0 .2-.1.3-.2.3'
        />
        <path
          fill='#80583F'
          d='m19.3 28.502-.6.3v-1.9l.6-.3z'
        />
        <path
          fill='#80583F'
          d='M18.7 29h-.1c-.1 0-.1-.1-.1-.2V27c.01-.08.05-.15.1-.2l.6-.3h.2c.1 0 .1.1.1.2v1.77c-.01.08-.05.15-.1.2l-.7.3zm.2-1.9v1.3l.1-.1V27z'
        />
      </svg>
    ),
  },
  // -------------------------------- Straps Sleeves ---------------------------------
  'Straps Sleeves': {
    // Long Sleeves
    'Long Sleeves': (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='46'
        height='60'
        fill='none'
        viewBox='0 0 46 60'
      >
        <mask
          id='mask0_13163_56467'
          width='46'
          height='60'
          x='0'
          y='0'
          maskUnits='userSpaceOnUse'
          style={{
            maskType: 'luminance',
          }}
        >
          <path
            fill='#fff'
            d='M45.5 0H.5v60h45z'
          />
        </mask>
        <g mask='url(#mask0_13163_56467)'>
          <path
            fill='#fff'
            d='M5.291 57.466s.66 1.74-.17 1.05c-.97-.8-1.3-2.04-1.75-3.19-.54-1.38-.59-2.63-.54-4.11.12-3.38.95-6.7 1.91-9.92.45-1.5.86-3.03 1.5-4.46.66-1.46 1.49-2.85 2.03-4.37.2-.55.29-1.12.29-1.71 0-8.56-2.08-17.14.67-19.24 1.58-1.2 5.08-1.65 6.82-2.63s1.2-6.73 1.2-6.73h6.3s-.54 6.48 2.72 6.6 5.91-.32 7.22 1.76c1.3 2.08 9.3 10.59 9.84 11.69s.33 1.83-.65 3.3-8.56 11.04-9.93 11.5c-1.11.37-1.22-.31-2.11-.06-.58.16-1.23.48-2.08.82 0 0-1.46.76-1.69.28l3.33-2.2s-1.65-.03-2.39.49c-.58.41-1.26.69-1.76.44 0 0 .44-.34 1.85-1.45.48-.38 1.15-.58 2-.57 0 0-2.01.03-2.47.38-.54.4-1.22 1.07-1.55.77 0 0 .6-.78 1.1-1.29 0 0 .72-.93 2.35-.71l-1.03-.34s-.55.1-.78.26c-.95.63-1.42.13-1.42.13l1.55-1.11c.07-.05.44-.1.52-.09l4.34.52 5.33-9.11-8.27-7.56-8.16 7.25-6.25 3.71-2.9 7.19-3.65 5.83-1.37 5.23-1.27 7.61c.18 3.93.09 3.33-.68 4.04'
          />
          <path
            className='fill-f8f3ef group-[.svg-selected]_fill-BE9983 lg_group-hover_fill-BE9983'
            d='M43.85 22.955c-.04-.43-8.38-10.2-9.76-12.05-1.5-2.02-4.58-2.21-4.35-2.22 0 0-3.06-.13-3.53-.2-.42-.06-3.64 6.54-4.66 7.61-.19.2-.71 1.04-.92.85-.91-.8-2.6-2.32-4.26-4.47-.88-1.14-1.38-1.78-1.74-3.12 0 0-1.59.52-2.48.83l-.48.16-.54.26s-2.48.33-3.05 3.66.72 16.11.72 16.11-.12 3.07-2.32 6.41c-2.21 3.33-3.38 13.72-3.38 13.72l2.88.31c-1 3.33-2.24 6.84-3.82 11.04 0 0 3.97 4.71 17.92 5.48 10 .55 13.68-2.82 13.68-2.82-2.03-9.56-.89-11.04-.63-18.55.16-4.78-.22-7.8-1.78-12.38-.81-2.38-3.09-6.43-2.57-7.76.71-1.81 1.61-5.25 1.21-7.46-.16-.88-.31-.75-.36-1.64 0-.06 6.56 5.4 8.32 7.25.05.05-5.33 8.46-4.84 9.16.82 1.16 1.39 2.73 2.28 1.67 8.8-10.53 8.46-11.77 8.46-11.85'
          />
          <path
            fill='#80583F'
            d='M4.779 55.589c.22-.6.48-1.19.66-1.81l-.15-1.32a.35.35 0 0 0-.28-.3c-.15-.03-.17.2-.24.33-.12.24-.55 1.64-.07 3.06.02.02.06.02.08.04'
          />
          <path
            stroke='#80583F'
            strokeMiterlimit='10'
            strokeWidth='0.4'
            d='m32.93 32.883 2.05 2.32M3.008 50.46l3.28.42'
          />
          <path
            stroke='#80583F'
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeMiterlimit='10'
            strokeWidth='0.4'
            d='M4.119 57.313c-.3-.6-.52-1.27-.77-1.91-.55-1.39-.59-2.64-.54-4.13.12-3.39.95-6.72 1.92-9.96.45-1.5.86-3.04 1.51-4.47.66-1.47 1.5-2.86 2.04-4.38.2-.55.29-1.12.29-1.71 0-8.59-2.09-17.2.67-19.3 1.59-1.21 5.1-1.65 6.85-2.64 1.74-.98 1.2-6.75 1.2-6.75h6.32s-.55 6.5 2.73 6.63c3.27.12 5.93-.32 7.24 1.77s9.33 10.63 9.87 11.73c.55 1.1.33 1.84-.65 3.31s-8.59 11.08-9.97 11.54c-1.11.37-1.22-.31-2.12-.06-.58.16-1.23.48-2.09.83 0 0-1.47.76-1.7.28l3.34-2.21s-1.65-.03-2.39.49c-.58.41-1.27.69-1.77.44 0 0 .44-.34 1.86-1.46.48-.38 1.15-.58 2.01-.58 0 0-2.02.03-2.48.38-.54.4-1.22 1.08-1.56.77 0 0 .6-.78 1.11-1.3 0 0 .72-.93 2.36-.72l-1.03-.34s-.55.11-.79.26c-.95.63-1.42.13-1.42.13l1.56-1.11c.07-.05.44-.1.53-.09l4.35.52 5.35-9.14-8.3-7.58'
          />
          <path
            stroke='#80583F'
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeMiterlimit='10'
            strokeWidth='0.4'
            d='M12.26 15.762c-.12 4.56.63 7 1.18 8.78.21.68.39 1.26.46 1.81.19 1.38-.69 3.08-1.8 5.22-1.27 2.45-2.86 5.49-3.51 9.29-1.25 7.27-3.16 13.12-6.23 21.31.82.79 5.37 4.6 17.75 5.29 8.73.49 12.66-2.05 13.45-2.64-1.47-7-1.25-9.65-.92-13.64.12-1.41.25-3 .32-5.01.17-4.93-.26-7.95-1.78-12.4-.25-.74-.65-1.65-1.07-2.61-1-2.28-1.94-4.43-1.53-5.48.7-1.79 1.59-5.22 1.2-7.36-.12-.7-.16-1.41-.18-2.09m.42-7.44c-.84-.04-3.07-.14-3.62-.19-.32.36-1.22 1.98-1.95 3.3-1.01 1.83-2.06 3.72-2.6 4.29-.04.05-.1.13-.17.22-.34.45-.58.75-.89.78a.5.5 0 0 1-.35-.11c-1.2-1.05-2.78-2.52-4.36-4.58-.82-1.06-1.34-1.74-1.72-2.93-.63.21-2.05.55-2.59.74m18.13-1.52q-.015 0 0 0'
          />
          <mask
            id='mask1_13163_56467'
            width='13'
            height='13'
            x='24'
            y='29'
            maskUnits='userSpaceOnUse'
            style={{
              maskType: 'luminance',
            }}
          >
            <path
              fill='#fff'
              d='M35.992 35.55c3.11-.6-2.57 5.74-5.74 5.74a5.74 5.74 0 0 1-5.74-5.74c0-3.17 3.35-7.83 5.74-5.74 2.3 2.01 2.27 6.41 5.74 5.74'
            />
          </mask>
          <g mask='url(#mask1_13163_56467)'>
            <path
              fill='#fff'
              d='M5.531 57.352s.66 1.73-.17 1.04c-.96-.8-1.29-2.03-1.74-3.18-.54-1.37-.59-2.62-.54-4.09.12-3.36.95-6.66 1.9-9.87.44-1.49.85-3.01 1.49-4.43.66-1.46 1.48-2.83 2.02-4.34.2-.55.29-1.11.29-1.7 0-8.51-2.07-17.05.67-19.13 1.57-1.2 5.06-1.64 6.79-2.61s1.19-6.69 1.19-6.69h6.27s-.54 6.45 2.7 6.57 5.88-.32 7.18 1.75 9.25 10.53 9.79 11.63c.54 1.09.32 1.82-.65 3.28s-8.51 10.98-9.88 11.43c-1.1.36-1.21-.3-2.1-.06-.57.16-1.22.47-2.07.82 0 0-1.45.75-1.69.28l3.31-2.19s-1.64-.03-2.37.49c-.57.4-1.26.68-1.75.44 0 0 .43-.33 1.84-1.44.48-.37 1.14-.58 1.99-.57 0 0-2 .03-2.46.38-.53.4-1.21 1.07-1.54.76 0 0 .59-.77 1.1-1.29 0 0 .72-.92 2.34-.71l-1.02-.33s-.55.1-.78.26c-.94.63-1.41.13-1.41.13l1.55-1.1c.07-.05.44-.1.52-.09l4.31.52 5.3-9.06-8.22-7.51-8.11 7.21-6.22 3.69-2.88 7.15-3.63 5.79-1.36 5.2-1.26 7.57c.15 3.89.06 3.3-.7 4'
            />
          </g>
          <mask
            id='mask2_13163_56467'
            width='13'
            height='13'
            x='24'
            y='29'
            maskUnits='userSpaceOnUse'
            style={{
              maskType: 'luminance',
            }}
          >
            <path
              fill='#fff'
              d='M35.662 36.123c2.8 1.49-2.24 5.17-5.41 5.17a5.74 5.74 0 0 1-5.74-5.74c0-3.17 3.96-8.37 5.74-5.74 2.35 3.48 3.67 5.38 5.41 6.31'
            />
          </mask>
          <g mask='url(#mask2_13163_56467)'>
            <path
              stroke='#80583F'
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeMiterlimit='10'
              strokeWidth='0.4'
              d='M5.531 57.352s.66 1.73-.17 1.04c-.96-.8-1.29-2.03-1.74-3.18-.54-1.37-.59-2.62-.54-4.09.12-3.36.95-6.66 1.9-9.87.44-1.49.85-3.01 1.49-4.43.66-1.46 1.48-2.83 2.02-4.34.2-.55.29-1.11.29-1.7 0-8.51-2.07-17.05.67-19.13 1.57-1.2 5.06-1.64 6.79-2.61s1.19-6.69 1.19-6.69h6.27s-.54 6.45 2.7 6.57 5.88-.32 7.18 1.75 9.25 10.53 9.79 11.63c.54 1.09.32 1.82-.65 3.28s-8.51 10.98-9.88 11.43c-1.1.36-1.21-.3-2.1-.06-.57.16-1.22.47-2.07.82 0 0-1.45.75-1.69.28l3.31-2.19s-1.64-.03-2.37.49c-.57.4-1.26.68-1.75.44 0 0 .43-.33 1.84-1.44.48-.37 1.14-.58 1.99-.57 0 0-2 .03-2.46.38-.53.4-1.21 1.07-1.54.76 0 0 .59-.77 1.1-1.29 0 0 .72-.92 2.34-.71l-1.02-.33s-.55.1-.78.26c-.94.63-1.41.13-1.41.13l1.55-1.1c.07-.05.44-.1.52-.09l4.31.52 5.3-9.06-8.22-7.51-8.11 7.21-6.22 3.69-2.88 7.15-3.63 5.79-1.36 5.2-1.26 7.57c.15 3.89.06 3.3-.7 4'
            />
          </g>
        </g>
      </svg>
    ),
    '3/4 Sleeves': (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='46'
        height='60'
        fill='none'
        viewBox='0 0 46 60'
      >
        <mask
          id='mask0_13163_56694'
          width='46'
          height='60'
          x='0'
          y='0'
          maskUnits='userSpaceOnUse'
          style={{
            maskType: 'luminance',
          }}
        >
          <path
            fill='#fff'
            d='M45.5 0H.5v60h45z'
          />
        </mask>
        <g mask='url(#mask0_13163_56694)'>
          <path
            fill='#fff'
            d='M5.069 58.373s.66 1.74-.17 1.05c-.97-.8-1.3-2.04-1.75-3.19-.54-1.38-.59-2.63-.54-4.11.12-3.38.95-6.7 1.91-9.92.45-1.5.86-3.03 1.5-4.46.66-1.46 1.49-2.85 2.03-4.37.2-.55.29-1.12.29-1.71 0-8.56-2.08-17.14.67-19.24 1.58-1.2 5.08-1.65 6.82-2.63s1.2-6.73 1.2-6.73h6.3s-.54 6.48 2.72 6.6 5.91-.32 7.22 1.76c1.3 2.08 9.3 10.59 9.84 11.69s.33 1.83-.65 3.3-8.56 11.04-9.93 11.5c-1.11.37-1.22-.31-2.11-.06-.58.16-1.23.48-2.08.82 0 0-1.46.76-1.69.28l3.33-2.2s-1.65-.03-2.39.49c-.58.41-1.26.69-1.76.44 0 0 .44-.34 1.85-1.45.48-.38 1.15-.58 2-.57 0 0-2.01.03-2.47.38-.54.4-1.22 1.07-1.55.77 0 0 .6-.78 1.1-1.29 0 0 .72-.93 2.35-.71l-1.03-.34s-.55.1-.78.26c-.95.63-1.42.13-1.42.13l1.55-1.11c.07-.05.44-.1.52-.09l4.35.52 5.33-9.11-8.27-7.56-8.16 7.25-6.25 3.71-2.9 7.19-3.65 5.83-1.37 5.23-1.27 7.61c.17 3.93.08 3.33-.69 4.04'
          />
          <path
            className='fill-f8f3ef group-[.svg-selected]_fill-BE9983 lg_group-hover_fill-BE9983'
            d='M43.4 23.908c-.04-.43-8.38-10.2-9.76-12.05-1.5-2.02-4.58-2.21-4.35-2.22 0 0-3.06-.13-3.53-.2-.42-.06-3.64 6.54-4.66 7.61-.19.2-.71 1.04-.92.85-.91-.8-2.6-2.32-4.26-4.47-.88-1.14-1.38-1.78-1.74-3.12 0 0-1.59.52-2.48.83v-.26l-1.02.69s-2.29.32-2.85 3.64.52 16.13.52 16.13-.12 3.07-2.32 6.41c-.79 1.19-1.45 3.29-1.97 5.49.46.04.91.12 1.36.19.39.06.73.32 1.13.34 1.94.08.79 2.12.37 3.19-.01.04-.03.07-.04.11-.06.33-.14.65-.23.98-.14.52-.27 1.04-.42 1.55-.14.46-.34.9-.46 1.37-.07.28-.16.55-.28.81h.03c-1 3.33-2.24 6.84-3.82 11.04 0 0 3.97 4.71 17.92 5.48 10 .55 13.68-2.82 13.68-2.82-2.03-9.56-.89-11.04-.63-18.55.16-4.78-.22-7.8-1.78-12.38-.81-2.38-3.09-6.43-2.57-7.76.71-1.81 1.61-5.25 1.21-7.46-.16-.88-.31-.75-.36-1.64 0-.06 6.56 5.4 8.32 7.25.02.02-1.04 1.72-2.18 3.64 1.26.69 2.64 1.19 3.8 2.02 4.52-5.81 4.3-6.63 4.29-6.69'
          />
          <path
            fill='#80583F'
            d='M4.48 56.526c.22-.6.48-1.19.66-1.81l-.15-1.32a.35.35 0 0 0-.28-.3c-.15-.03-.25.26-.32.39-.18.38-.65 1.49.01 3 .02.02.06.02.08.04'
          />
          <path
            stroke='#80583F'
            strokeMiterlimit='10'
            strokeWidth='0.4'
            d='m4.246 43.281 3.38.68M35.887 28.492l3.18 2.31'
          />
          <path
            stroke='#80583F'
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeMiterlimit='10'
            strokeWidth='0.4'
            d='M11.818 16.717c-.12 4.56.63 7 1.18 8.78.21.68.39 1.26.46 1.81.19 1.38-.69 3.08-1.8 5.22-1.27 2.45-2.86 5.49-3.51 9.29-1.25 7.27-3.16 13.12-6.23 21.31.82.79 5.37 4.6 17.75 5.29 8.73.49 12.66-2.05 13.45-2.64-1.47-7-1.25-9.65-.92-13.64.12-1.41.25-3 .32-5.01.17-4.93-.26-7.95-1.78-12.4-.25-.74-.65-1.65-1.07-2.61-1-2.28-1.94-4.43-1.53-5.48.7-1.79 1.59-5.22 1.2-7.36-.12-.7-.16-1.41-.18-2.09m.42-7.52c-.84-.04-3.07-.07-3.62-.12-.32.36-1.22 1.98-1.95 3.3-1.01 1.83-2.06 3.72-2.6 4.29-.04.05-.1.13-.17.22-.34.45-.58.75-.89.78a.5.5 0 0 1-.35-.11c-1.2-1.05-2.78-2.52-4.36-4.58-.82-1.06-1.34-1.74-1.72-2.93-.63.21-2.04.56-2.58.76'
          />
          <path
            stroke='#80583F'
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeMiterlimit='10'
            strokeWidth='0.4'
            d='M3.77 58.049c-.3-.6-.43-1.1-.69-1.74-.55-1.39-.59-2.64-.54-4.13.12-3.39.95-6.72 1.92-9.96.45-1.5.86-3.04 1.51-4.47.66-1.47 1.5-2.86 2.04-4.38.2-.55.29-1.12.29-1.71 0-8.59-2.09-17.2.67-19.3 1.59-1.21 5.1-1.65 6.85-2.64s1.2-6.75 1.2-6.75h6.32s-.55 6.5 2.73 6.63c3.27.12 5.93-.32 7.24 1.77s9.33 10.63 9.87 11.73c.55 1.1.33 1.84-.65 3.31s-8.59 11.08-9.97 11.54c-1.11.37-1.22-.31-2.12-.06-.58.16-1.23.48-2.09.83 0 0-1.47.76-1.7.28l3.34-2.21s-1.65-.03-2.39.49c-.58.41-1.27.69-1.77.44 0 0 .44-.34 1.86-1.46.48-.38 1.15-.58 2.01-.58 0 0-2.02.03-2.48.38-.54.4-1.22 1.08-1.56.77 0 0 .6-.78 1.11-1.3 0 0 .72-.93 2.36-.72l-1.03-.34s-.55.11-.79.26c-.95.63-1.42.13-1.42.13l1.56-1.11c.07-.05.44-.1.53-.09l4.35.52 5.35-9.14-8.3-7.58'
          />
          <mask
            id='mask1_13163_56694'
            width='13'
            height='13'
            x='24'
            y='30'
            maskUnits='userSpaceOnUse'
            style={{
              maskType: 'luminance',
            }}
          >
            <path
              fill='#fff'
              d='M35.73 36.457c3.11-.6-2.57 5.74-5.74 5.74a5.74 5.74 0 0 1-5.74-5.74c0-3.17 3.35-7.83 5.74-5.74 2.3 2.01 2.27 6.41 5.74 5.74'
            />
          </mask>
          <g mask='url(#mask1_13163_56694)'>
            <path
              fill='#fff'
              d='M5.27 58.258s.66 1.73-.17 1.04c-.96-.8-1.29-2.03-1.74-3.18-.54-1.37-.59-2.62-.54-4.09.12-3.36.95-6.66 1.9-9.87.44-1.49.85-3.01 1.49-4.43.66-1.46 1.48-2.83 2.02-4.34.2-.55.29-1.11.29-1.7 0-8.51-2.07-17.05.67-19.13 1.57-1.2 5.06-1.64 6.79-2.61s1.19-6.69 1.19-6.69h6.27s-.54 6.45 2.7 6.57 5.88-.32 7.18 1.75 9.25 10.53 9.79 11.63c.54 1.09.32 1.82-.65 3.28s-8.51 10.98-9.88 11.43c-1.1.36-1.21-.3-2.1-.06-.57.16-1.22.47-2.07.82 0 0-1.45.75-1.69.28l3.31-2.19s-1.64-.03-2.37.49c-.57.4-1.26.68-1.75.44 0 0 .43-.33 1.84-1.44.48-.37 1.14-.58 1.99-.57 0 0-2 .03-2.46.38-.53.4-1.21 1.07-1.54.76 0 0 .59-.77 1.1-1.29 0 0 .72-.92 2.34-.71l-1.02-.33s-.55.1-.78.26c-.94.63-1.41.13-1.41.13l1.55-1.1c.07-.05.44-.1.52-.09l4.31.52 5.3-9.06-8.22-7.51-8.11 7.21-6.22 3.69-2.88 7.15-3.63 5.79-1.36 5.2-1.26 7.57c.15 3.89.06 3.3-.7 4'
            />
          </g>
          <mask
            id='mask2_13163_56694'
            width='13'
            height='13'
            x='24'
            y='30'
            maskUnits='userSpaceOnUse'
            style={{
              maskType: 'luminance',
            }}
          >
            <path
              fill='#fff'
              d='M35.4 37.029c2.8 1.49-2.24 5.17-5.41 5.17a5.74 5.74 0 0 1-5.74-5.74c0-3.17 3.96-8.37 5.74-5.74 2.36 3.48 3.67 5.38 5.41 6.31'
            />
          </mask>
          <g mask='url(#mask2_13163_56694)'>
            <path
              stroke='#80583F'
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeMiterlimit='10'
              strokeWidth='0.4'
              d='M5.27 58.258s.66 1.73-.17 1.04c-.96-.8-1.29-2.03-1.74-3.18-.54-1.37-.59-2.62-.54-4.09.12-3.36.95-6.66 1.9-9.87.44-1.49.85-3.01 1.49-4.43.66-1.46 1.48-2.83 2.02-4.34.2-.55.29-1.11.29-1.7 0-8.51-2.07-17.05.67-19.13 1.57-1.2 5.06-1.64 6.79-2.61s1.19-6.69 1.19-6.69h6.27s-.54 6.45 2.7 6.57 5.88-.32 7.18 1.75 9.25 10.53 9.79 11.63c.54 1.09.32 1.82-.65 3.28s-8.51 10.98-9.88 11.43c-1.1.36-1.21-.3-2.1-.06-.57.16-1.22.47-2.07.82 0 0-1.45.75-1.69.28l3.31-2.19s-1.64-.03-2.37.49c-.57.4-1.26.68-1.75.44 0 0 .43-.33 1.84-1.44.48-.37 1.14-.58 1.99-.57 0 0-2 .03-2.46.38-.53.4-1.21 1.07-1.54.76 0 0 .59-.77 1.1-1.29 0 0 .72-.92 2.34-.71l-1.02-.33s-.55.1-.78.26c-.94.63-1.41.13-1.41.13l1.55-1.1c.07-.05.44-.1.52-.09l4.31.52 5.3-9.06-8.22-7.51-8.11 7.21-6.22 3.69-2.88 7.15-3.63 5.79-1.36 5.2-1.26 7.57c.15 3.89.06 3.3-.7 4'
            />
          </g>
        </g>
      </svg>
    ),
    'Short Sleeves': (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='46'
        height='60'
        fill='none'
        viewBox='0 0 46 60'
      >
        <mask
          id='mask0_13163_56876'
          width='46'
          height='60'
          x='0'
          y='0'
          maskUnits='userSpaceOnUse'
          style={{
            maskType: 'luminance',
          }}
        >
          <path
            fill='#fff'
            d='M45.5 0H.5v60h45z'
          />
        </mask>
        <g mask='url(#mask0_13163_56876)'>
          <path
            fill='#fff'
            d='M5.069 58.326s.66 1.74-.17 1.05c-.97-.8-1.3-2.04-1.75-3.19-.54-1.38-.59-2.63-.54-4.11.12-3.38.95-6.7 1.91-9.92.45-1.5.86-3.03 1.5-4.46.66-1.46 1.49-2.85 2.03-4.37.2-.55.29-1.12.29-1.71 0-8.56-2.08-17.14.67-19.24 1.58-1.2 5.08-1.65 6.82-2.63s1.19-6.73 1.19-6.73h6.3s-.54 6.48 2.72 6.6 5.91-.32 7.22 1.76c1.3 2.08 9.3 10.59 9.84 11.69s.33 1.83-.65 3.3-8.56 11.04-9.93 11.5c-1.11.37-1.22-.31-2.11-.06-.58.16-1.23.48-2.08.82 0 0-1.46.76-1.69.28l3.33-2.2s-1.65-.03-2.39.49c-.58.41-1.26.69-1.76.44 0 0 .44-.34 1.85-1.45.48-.38 1.15-.58 2-.57 0 0-2.01.03-2.47.38-.54.4-1.22 1.07-1.55.77 0 0 .6-.78 1.1-1.29 0 0 .72-.93 2.35-.71l-1.03-.34s-.55.1-.78.26c-.95.63-1.42.13-1.42.13l1.55-1.11c.07-.05.44-.1.52-.09l4.34.52 5.33-9.11-8.27-7.56-8.16 7.25-6.25 3.71-2.9 7.19-3.65 5.83-1.37 5.23-1.27 7.61c.19 3.93.1 3.33-.67 4.04'
          />
          <path
            className='fill-f8f3ef group-[.svg-selected]_fill-BE9983 lg_group-hover_fill-BE9983'
            d='M33.649 11.819c-1.5-2.02-4.58-2.21-4.35-2.22 0 0-3.06-.13-3.53-.2-.42-.06-3.64 6.54-4.66 7.61-.19.2-.71 1.04-.92.85-.91-.8-2.6-2.32-4.26-4.47-.88-1.14-1.38-1.78-1.74-3.12 0 0-1.59.52-2.48.83v-.26l-1.02.69s-2.29.32-2.85 3.64c-.41 2.44.06 9.96.34 13.81 1.19.01 2.37.04 3.56.04.63 0 1.1.52 1.13 1.13.02.39-.21.75-.53.95-.61 1.21-1.36 2.34-1.84 3.61-.5 1.34-1.08 2.65-1.53 4.01-.45 1.35-.8 2.73-1.13 4.12-.17.69-.31 1.37-.49 2.05.09.66-.16 1.4-.41 2.04-.01.04-.03.07-.04.11-.06.33-.14.65-.23.98-.14.52-.27 1.04-.42 1.55-.14.46-.34.9-.46 1.37-.07.28-.16.55-.28.81h.03c-1 3.33-2.24 6.84-3.82 11.04 0 0 3.97 4.71 17.92 5.48 10 .55 13.68-2.82 13.68-2.82-2.03-9.56-.89-11.04-.63-18.55.16-4.78-.22-7.8-1.78-12.38-.81-2.38-3.09-6.43-2.57-7.76.71-1.81 1.61-5.25 1.21-7.46-.16-.88-.31-.75-.36-1.64 0-.05 4.96 4.07 7.41 6.36.14-.14.27-.29.42-.41 1.12-1.2 2.31-2.33 3.74-3.14-2.59-3.15-6.24-7.48-7.11-8.65'
          />
          <path
            fill='#80583F'
            d='M4.34 56.502c.22-.6.48-1.19.66-1.81l-.15-1.32a.35.35 0 0 0-.28-.3c-.15-.03-.21.41-.29.54-.05.09-.24 1.49-.02 2.84.01.04.06.04.08.05'
          />
          <path
            stroke='#80583F'
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeMiterlimit='10'
            strokeWidth='0.4'
            d='M11.818 16.676c-.12 4.56.63 7 1.18 8.78.21.68.39 1.26.46 1.81.19 1.38-.69 3.08-1.8 5.22-1.27 2.45-2.86 5.49-3.51 9.29-1.25 7.27-3.16 13.12-6.23 21.31.82.79 5.37 4.6 17.75 5.29 8.73.49 12.66-2.05 13.45-2.64-1.47-7-1.25-9.65-.92-13.64.12-1.41.25-3 .32-5.01.17-4.93-.26-7.95-1.78-12.4-.25-.74-.65-1.65-1.07-2.61-1-2.28-1.94-4.43-1.53-5.48.7-1.79 1.59-5.22 1.2-7.36-.12-.7-.16-1.41-.18-2.09m.42-7.44c-.84-.04-3.07-.14-3.62-.19-.32.36-1.22 1.98-1.95 3.3-1.01 1.83-2.06 3.72-2.6 4.29-.04.05-.1.13-.17.22-.34.45-.58.75-.89.78a.5.5 0 0 1-.35-.11c-1.2-1.05-2.78-2.52-4.36-4.58-.82-1.06-1.34-1.74-1.72-2.93-.63.21-1.97.65-2.51.84m18.05-1.62q-.015 0 0 0M36.54 23.912l4.01-3.81M8.34 29.125h4.87'
          />
          <path
            stroke='#80583F'
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeMiterlimit='10'
            strokeWidth='0.4'
            d='M3.76 58.039c-.3-.6-.43-1.09-.68-1.73-.55-1.39-.59-2.64-.54-4.13.12-3.39.95-6.72 1.92-9.96.45-1.5.86-3.04 1.51-4.47.66-1.47 1.5-2.86 2.04-4.38.2-.55.29-1.12.29-1.71 0-8.59-2.09-17.2.67-19.3 1.59-1.21 5.1-1.65 6.85-2.64 1.74-.98 1.2-6.75 1.2-6.75h6.32s-.55 6.5 2.73 6.63c3.27.12 5.93-.32 7.24 1.77s9.33 10.63 9.87 11.73c.55 1.1.33 1.84-.65 3.31s-8.59 11.08-9.97 11.54c-1.11.37-1.22-.31-2.12-.06-.58.16-1.23.48-2.09.83 0 0-1.47.76-1.7.28l3.34-2.21s-1.65-.03-2.39.49c-.58.41-1.27.69-1.77.44 0 0 .44-.34 1.86-1.46.48-.38 1.15-.58 2.01-.58 0 0-2.02.03-2.48.38-.54.4-1.22 1.08-1.56.77 0 0 .6-.78 1.11-1.3 0 0 .72-.93 2.36-.72l-1.03-.34s-.55.11-.79.26c-.95.63-1.42.13-1.42.13l1.56-1.11c.07-.05.44-.1.53-.09l4.35.52 5.35-9.14-8.3-7.58'
          />
          <mask
            id='mask1_13163_56876'
            width='13'
            height='13'
            x='24'
            y='30'
            maskUnits='userSpaceOnUse'
            style={{
              maskType: 'luminance',
            }}
          >
            <path
              fill='#fff'
              d='M35.73 36.457c3.11-.6-2.57 5.74-5.74 5.74a5.74 5.74 0 0 1-5.74-5.74c0-3.17 3.35-7.83 5.74-5.74 2.3 2.01 2.27 6.41 5.74 5.74'
            />
          </mask>
          <g mask='url(#mask1_13163_56876)'>
            <path
              fill='#fff'
              d='M5.27 58.258s.66 1.73-.17 1.04c-.96-.8-1.29-2.03-1.74-3.18-.54-1.37-.59-2.62-.54-4.09.12-3.36.95-6.66 1.9-9.87.44-1.49.85-3.01 1.49-4.43.66-1.46 1.48-2.83 2.02-4.34.2-.55.29-1.11.29-1.7 0-8.51-2.07-17.05.67-19.13 1.57-1.2 5.06-1.64 6.79-2.61s1.19-6.69 1.19-6.69h6.27s-.54 6.45 2.7 6.57 5.88-.32 7.18 1.75 9.25 10.53 9.79 11.63c.54 1.09.32 1.82-.65 3.28s-8.51 10.98-9.88 11.43c-1.1.36-1.21-.3-2.1-.06-.57.16-1.22.47-2.07.82 0 0-1.45.75-1.69.28l3.31-2.19s-1.64-.03-2.37.49c-.57.4-1.26.68-1.75.44 0 0 .43-.33 1.84-1.44.48-.37 1.14-.58 1.99-.57 0 0-2 .03-2.46.38-.53.4-1.21 1.07-1.54.76 0 0 .59-.77 1.1-1.29 0 0 .72-.92 2.34-.71l-1.02-.33s-.55.1-.78.26c-.94.63-1.41.13-1.41.13l1.55-1.1c.07-.05.44-.1.52-.09l4.31.52 5.3-9.06-8.22-7.51-8.11 7.21-6.22 3.69-2.88 7.15-3.63 5.79-1.36 5.2-1.26 7.57c.15 3.89.06 3.3-.7 4'
            />
          </g>
          <mask
            id='mask2_13163_56876'
            width='13'
            height='13'
            x='24'
            y='30'
            maskUnits='userSpaceOnUse'
            style={{
              maskType: 'luminance',
            }}
          >
            <path
              fill='#fff'
              d='M35.4 37.029c2.8 1.49-2.24 5.17-5.41 5.17a5.74 5.74 0 0 1-5.74-5.74c0-3.17 3.96-8.37 5.74-5.74 2.35 3.48 3.67 5.38 5.41 6.31'
            />
          </mask>
          <g mask='url(#mask2_13163_56876)'>
            <path
              stroke='#80583F'
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeMiterlimit='10'
              strokeWidth='0.4'
              d='M5.27 58.258s.66 1.73-.17 1.04c-.96-.8-1.29-2.03-1.74-3.18-.54-1.37-.59-2.62-.54-4.09.12-3.36.95-6.66 1.9-9.87.44-1.49.85-3.01 1.49-4.43.66-1.46 1.48-2.83 2.02-4.34.2-.55.29-1.11.29-1.7 0-8.51-2.07-17.05.67-19.13 1.57-1.2 5.06-1.64 6.79-2.61s1.19-6.69 1.19-6.69h6.27s-.54 6.45 2.7 6.57 5.88-.32 7.18 1.75 9.25 10.53 9.79 11.63c.54 1.09.32 1.82-.65 3.28s-8.51 10.98-9.88 11.43c-1.1.36-1.21-.3-2.1-.06-.57.16-1.22.47-2.07.82 0 0-1.45.75-1.69.28l3.31-2.19s-1.64-.03-2.37.49c-.57.4-1.26.68-1.75.44 0 0 .43-.33 1.84-1.44.48-.37 1.14-.58 1.99-.57 0 0-2 .03-2.46.38-.53.4-1.21 1.07-1.54.76 0 0 .59-.77 1.1-1.29 0 0 .72-.92 2.34-.71l-1.02-.33s-.55.1-.78.26c-.94.63-1.41.13-1.41.13l1.55-1.1c.07-.05.44-.1.52-.09l4.31.52 5.3-9.06-8.22-7.51-8.11 7.21-6.22 3.69-2.88 7.15-3.63 5.79-1.36 5.2-1.26 7.57c.15 3.89.06 3.3-.7 4'
            />
          </g>
        </g>
      </svg>
    ),
    'Puff Sleeves': (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='46'
        height='60'
        fill='none'
        viewBox='0 0 46 60'
      >
        <mask
          id='mask0_13163_57046'
          width='46'
          height='60'
          x='0'
          y='0'
          maskUnits='userSpaceOnUse'
          style={{
            maskType: 'luminance',
          }}
        >
          <path
            fill='#fff'
            d='M45.5 0H.5v60h45z'
          />
        </mask>
        <g mask='url(#mask0_13163_57046)'>
          <path
            fill='#fff'
            d='M6.377 58.279s.66 1.74-.17 1.05c-.97-.8-1.3-2.04-1.75-3.19-.54-1.38-.59-2.63-.54-4.11.12-3.38.95-6.7 1.91-9.92.45-1.5.86-3.03 1.5-4.46.66-1.46 1.49-2.85 2.03-4.37.2-.55.29-1.12.29-1.71 0-8.56-2.08-17.14.67-19.24 1.58-1.2 5.08-1.65 6.82-2.63s1.19-6.73 1.19-6.73h6.3s-.54 6.48 2.72 6.6 5.91-.32 7.22 1.76c1.3 2.08 9.3 10.59 9.84 11.69s.33 1.83-.65 3.3-8.56 11.04-9.93 11.5c-1.11.37-1.22-.31-2.11-.06-.58.16-1.23.48-2.08.82 0 0-1.46.76-1.69.28l3.33-2.2s-1.65-.03-2.39.49c-.58.41-1.26.69-1.76.44 0 0 .44-.34 1.85-1.45.48-.38 1.15-.58 2-.57 0 0-2.01.03-2.47.38-.54.4-1.22 1.07-1.55.77 0 0 .6-.78 1.1-1.29 0 0 .72-.93 2.35-.71l-1.03-.34s-.55.1-.78.26c-.95.63-1.42.13-1.42.13l1.55-1.11c.07-.05.44-.1.52-.09l4.34.52 5.33-9.11-8.27-7.56-8.16 7.25-6.25 3.71-2.9 7.19-3.65 5.83-1.37 5.23-1.27 7.61c.19 3.93.09 3.33-.67 4.04'
          />
          <path
            className='fill-f8f3ef group-[.svg-selected]_fill-BE9983 lg_group-hover_fill-BE9983'
            d='M34.718 11.327c-1.86-1.46-4.07-1.72-4.14-1.78-3.16 1.44-7.16 6.34-8.17 7.4-.19.2-.71 1.04-.92.85-.91-.8-3.13-1.8-4.8-3.96-.88-1.14-3.33-1.47-3.69-2.81l-.34.17-.68.26s-2.29.32-2.85 3.64.17 16.13.17 16.13-.52 1.6-2.73 4.93c-.47.7-.94 2.07-.42 4.01.43.04.87.12 1.28.24.63.19 1.37.47 1.71 1.08.4.7.07 1.42-.14 2.12-.17.57-.22 1.16-.37 1.72-.05.51-.23 1.04-.42 1.51-.01.04-.03.07-.04.11-.06.33-.14.65-.23.98-.14.52-.27 1.04-.42 1.55-.14.46-.34.9-.46 1.37-.07.28-.16.55-.28.81h.03c-1 3.33-2.24 6.84-3.82 11.04 0 0 3.97 4.71 17.92 5.48 10 .55 13.68-2.82 13.68-2.82-2.03-9.56-.89-11.04-.63-18.55.16-4.78-.22-7.8-1.78-12.38-.81-2.38-3.09-6.43-2.57-7.76.71-1.81 1.61-5.25 1.21-7.46-.16-.88-.25-1-.3-1.89 0-.06 4.34 3.12 6.33 6.37.02.03-.94 2.65-.04 4.31 1.26.69 2.44 1.65 3.6 2.48 4.81-1.85 4.29-6.64 4.29-6.7-.03-.4-4.83-8.42-9.98-12.45'
          />
          <path
            fill='#80583F'
            d='M5.647 56.449c.22-.6.48-1.19.66-1.81l-.15-1.32a.35.35 0 0 0-.28-.3c-.15-.03-.15.17-.23.3-.05.09-.62 1.54-.08 3.09.01.03.05.03.08.04'
          />
          <path
            stroke='#80583F'
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeMiterlimit='10'
            strokeWidth='0.4'
            d='M40.569 30.635c-2.58 3.2-5.84 7.02-6.7 7.31-1.11.37-1.22-.31-2.12-.06-.58.16-1.23.48-2.09.83 0 0-1.47.76-1.7.28l3.34-2.21s-1.65-.03-2.39.49c-.58.41-1.27.69-1.77.44 0 0 .44-.34 1.86-1.46.48-.38 1.15-.58 2.01-.58 0 0-2.02.03-2.48.38-.54.4-1.22 1.08-1.56.77 0 0 .6-.78 1.11-1.3 0 0 .72-.93 2.36-.72l-1.03-.34s-.55.11-.79.26c-.95.63-1.42.13-1.42.13l1.56-1.11c.07-.05.44-.1.53-.09l4.35.52 3.4-5.8M37.188 28.398l3.19 2.32M30.758 9.648s-2.61 3.02-.3 7.43'
          />
          <path
            stroke='#80583F'
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeMiterlimit='10'
            strokeWidth='0.4'
            d='M12.299 11.368s.81 3.08.82 5.26c.02 4.56.63 7 1.18 8.78.21.68.39 1.26.46 1.81.19 1.38-.69 3.08-1.8 5.22-1.27 2.45-2.86 5.49-3.51 9.29-1.25 7.27-3.16 13.12-6.23 21.31.82.79 5.37 4.6 17.75 5.29 8.73.49 12.66-2.05 13.45-2.64-1.47-7-1.25-9.65-.92-13.64.12-1.41.25-3 .32-5.01.17-4.93-.26-7.95-1.78-12.4-.25-.74-.65-1.65-1.07-2.61-1-2.28-1.94-4.43-1.53-5.48.7-1.79 1.59-5.22 1.2-7.36-.12-.7-.16-1.41-.18-2.09 0 0 5.52 4.54 6.24 6.46.46 1.23-.73 2.13-.25 3.74.23.78.75 1.11.75 1.11m3.22 2.1c4.29-1.41 4.53-5.01 4.17-6.99s-4.67-7.05-7.72-10.14c-2.95-2.99-3.66-3.21-3.66-3.21-1.15-.46-2.22-.47-2.77-.52-.32.36-2.85 1.87-4.23 3.19-1.51 1.44-2.95 3.64-3.49 4.2-.04.05-.1.13-.17.22-.34.45-.58.75-.89.78a.5.5 0 0 1-.35-.11c-1.2-1.05-2.17-2.18-4.6-4.08-1.06-.83-2.51-2.01-4.04-2.64-.63.21-1.8.71-2.33 1.13 0 0-1.4.29-1.42 5.71s.42 12.29.4 13.52c-.02 2.1-5.12 5.43-2.91 8.6l3.02 1.05'
          />
          <path
            stroke='#80583F'
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeMiterlimit='10'
            strokeWidth='0.4'
            d='M12.54 11.299c1.61-.54 3.47-.95 4.59-1.58 1.74-.98 1.2-6.75 1.2-6.75h6.32s-.55 6.5 2.73 6.63c1.13.04 2.19.02 3.14.05M5.028 58.1c-.3-.6-.39-1.15-.64-1.79-.55-1.39-.59-2.64-.54-4.13.12-3.39.95-6.72 1.92-9.96.21-.72.42-1.44.64-2.15'
          />
          <mask
            id='mask1_13163_57046'
            width='13'
            height='13'
            x='25'
            y='30'
            maskUnits='userSpaceOnUse'
            style={{
              maskType: 'luminance',
            }}
          >
            <path
              fill='#fff'
              d='M36.988 36.527c3.11-.6-2.57 5.74-5.74 5.74a5.74 5.74 0 0 1-5.74-5.74c0-3.17 3.35-7.83 5.74-5.74 2.3 2.01 2.27 6.41 5.74 5.74'
            />
          </mask>
          <g mask='url(#mask1_13163_57046)'>
            <path
              fill='#fff'
              d='M6.528 58.328s.66 1.73-.17 1.04c-.96-.8-1.29-2.03-1.74-3.18-.54-1.37-.59-2.62-.54-4.09.12-3.36.95-6.66 1.9-9.87.44-1.49.85-3.01 1.49-4.43.66-1.46 1.48-2.83 2.02-4.34.2-.55.29-1.11.29-1.7 0-8.51-2.07-17.05.67-19.13 1.57-1.2 5.06-1.64 6.79-2.61s1.19-6.69 1.19-6.69h6.27s-.54 6.45 2.7 6.57 5.88-.32 7.18 1.75 9.25 10.53 9.79 11.63c.54 1.09.32 1.82-.65 3.28s-8.51 10.98-9.88 11.43c-1.1.36-1.21-.3-2.1-.06-.57.16-1.22.47-2.07.82 0 0-1.45.75-1.69.28l3.31-2.19s-1.64-.03-2.37.49c-.57.4-1.26.68-1.75.44 0 0 .43-.33 1.84-1.44.48-.37 1.14-.58 1.99-.57 0 0-2 .03-2.46.38-.53.4-1.21 1.07-1.54.76 0 0 .59-.77 1.1-1.29 0 0 .72-.92 2.34-.71l-1.02-.33s-.55.1-.78.26c-.94.63-1.41.13-1.41.13l1.55-1.1c.07-.05.44-.1.52-.09l4.31.52 5.3-9.06-8.22-7.51-8.11 7.21-6.22 3.69-2.88 7.15-3.63 5.79-1.36 5.2-1.26 7.57c.15 3.89.06 3.29-.7 4'
            />
          </g>
          <mask
            id='mask2_13163_57046'
            width='13'
            height='13'
            x='25'
            y='30'
            maskUnits='userSpaceOnUse'
            style={{
              maskType: 'luminance',
            }}
          >
            <path
              fill='#fff'
              d='M36.658 37.091c2.8 1.49-2.24 5.17-5.41 5.17a5.74 5.74 0 0 1-5.74-5.74c0-3.17 3.96-8.37 5.74-5.74 2.36 3.49 3.67 5.39 5.41 6.31'
            />
          </mask>
          <g mask='url(#mask2_13163_57046)'>
            <path
              stroke='#80583F'
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeMiterlimit='10'
              strokeWidth='0.4'
              d='M6.528 58.328s.66 1.73-.17 1.04c-.96-.8-1.29-2.03-1.74-3.18-.54-1.37-.59-2.62-.54-4.09.12-3.36.95-6.66 1.9-9.87.44-1.49.85-3.01 1.49-4.43.66-1.46 1.48-2.83 2.02-4.34.2-.55.29-1.11.29-1.7 0-8.51-2.07-17.05.67-19.13 1.57-1.2 5.06-1.64 6.79-2.61s1.19-6.69 1.19-6.69h6.27s-.54 6.45 2.7 6.57 5.88-.32 7.18 1.75 9.25 10.53 9.79 11.63c.54 1.09.32 1.82-.65 3.28s-8.51 10.98-9.88 11.43c-1.1.36-1.21-.3-2.1-.06-.57.16-1.22.47-2.07.82 0 0-1.45.75-1.69.28l3.31-2.19s-1.64-.03-2.37.49c-.57.4-1.26.68-1.75.44 0 0 .43-.33 1.84-1.44.48-.37 1.14-.58 1.99-.57 0 0-2 .03-2.46.38-.53.4-1.21 1.07-1.54.76 0 0 .59-.77 1.1-1.29 0 0 .72-.92 2.34-.71l-1.02-.33s-.55.1-.78.26c-.94.63-1.41.13-1.41.13l1.55-1.1c.07-.05.44-.1.52-.09l4.31.52 5.3-9.06-8.22-7.51-8.11 7.21-6.22 3.69-2.88 7.15-3.63 5.79-1.36 5.2-1.26 7.57c.15 3.89.06 3.29-.7 4'
            />
          </g>
        </g>
      </svg>
    ),
    'Cap Sleeves': (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='46'
        height='60'
        fill='none'
        viewBox='0 0 46 60'
      >
        <mask
          id='mask0_13163_57405'
          width='46'
          height='60'
          x='0'
          y='0'
          maskUnits='userSpaceOnUse'
          style={{
            maskType: 'luminance',
          }}
        >
          <path
            fill='#fff'
            d='M45.5 0H.5v60h45z'
          />
        </mask>
        <g mask='url(#mask0_13163_57405)'>
          <path
            fill='#fff'
            d='M6.291 58.24s.66 1.74-.17 1.05c-.97-.8-1.3-2.04-1.75-3.19-.54-1.38-.59-2.63-.54-4.11.12-3.38.95-6.7 1.91-9.92.45-1.5.86-3.03 1.5-4.46.66-1.46 1.49-2.85 2.03-4.37.2-.55.29-1.12.29-1.71 0-8.56-2.08-17.14.67-19.24 1.58-1.2 5.08-1.65 6.82-2.63s1.2-6.73 1.2-6.73h6.3s-.54 6.48 2.72 6.6 5.91-.32 7.22 1.76c1.3 2.08 9.3 10.59 9.84 11.69s.33 1.83-.65 3.3-8.56 11.04-9.93 11.5c-1.11.37-1.22-.31-2.11-.06-.58.16-1.23.48-2.08.82 0 0-1.46.76-1.69.28l3.33-2.2s-1.65-.03-2.39.49c-.58.41-1.26.69-1.76.44 0 0 .44-.34 1.85-1.45.48-.38 1.15-.58 2-.57 0 0-2.01.03-2.47.38-.54.4-1.22 1.07-1.55.77 0 0 .6-.78 1.1-1.29 0 0 .72-.93 2.35-.71l-1.03-.34s-.55.1-.78.26c-.95.63-1.42.13-1.42.13l1.55-1.11c.07-.05.44-.1.52-.09l4.34.52 5.33-9.11-8.27-7.56-8.16 7.25-6.25 3.71-2.9 7.19-3.65 5.83-1.37 5.23-1.27 7.61c.18 3.93.09 3.33-.68 4.04'
          />
          <path
            className='fill-f8f3ef group-[.svg-selected]_fill-BE9983 lg_group-hover_fill-BE9983'
            d='M33.9 46.787c.16-4.78-.22-7.8-1.78-12.38-.81-2.38-3.09-6.43-2.57-7.76.71-1.81 1.61-5.25 1.21-7.46-.16-.88-.31-.75-.36-1.64 0-.01.06.04.15.11.02-1.05 1.05-1.94 1.85-2.64 1.08-.95 2.17-1.82 3.49-2.36-.16-.2.1-.43-.03-.6-.18-.22-.45-.24-.55-.38-.24-.33-.83-.87-1.14-1.1-1.58-1.24-3.85-1.08-3.65-1.09 0 0-2.92-.01-3.38-.08-.42-.06-3.64 6.54-4.66 7.61-.19.2-.85.92-1.06.74-.91-.8-2.6-2.32-4.26-4.47-.88-1.14-1.71-1.67-2.08-3.01 0 0-1.29.24-2.18.54l-1.28.32-1.45.79s-2.08 1.78-2.08 4.18c0 .11 0 .23.01.35.6.02 1.21.05 1.81.07s1.2-.02 1.8 0c.69.03 1.3.33 1.48 1.05.13.52.03 1.1-.01 1.63-.1 1.38-.04 2.67.3 4.01.24.96.63 1.87.84 2.83.28 1.22.46 2.87-.24 3.99.01.39-.21.73-.53.93-.61 1.21-1.36 2.34-1.84 3.61-.5 1.34-1.08 2.65-1.53 4.01-.45 1.35-.8 2.73-1.13 4.12-.17.69-.31 1.37-.49 2.05.09.66-.16 1.4-.41 2.04-.01.04-.03.07-.04.11-.06.33-.14.65-.23.98-.14.52-.27 1.04-.42 1.55-.14.46-.34.9-.46 1.37-.07.28-.16.55-.28.81h.03c-1 3.33-2.24 6.84-3.82 11.04 0 0 3.97 4.71 17.92 5.48 10 .55 13.68-2.82 13.68-2.82-2.04-9.54-.89-11.03-.63-18.53'
          />
          <path
            fill='#80583F'
            d='M5.56 56.408c.22-.6.48-1.19.66-1.81l-.15-1.32a.35.35 0 0 0-.28-.3c-.15-.03-.24.44-.32.58-.05.09-.38 1.44.01 2.81.01.02.05.02.08.04'
          />
          <path
            stroke='#80583F'
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeMiterlimit='10'
            strokeWidth='0.4'
            d='M13.04 16.584c-.12 4.56.63 7 1.18 8.78.21.68.39 1.26.46 1.81.19 1.38-.69 3.08-1.8 5.22-1.27 2.45-2.86 5.49-3.51 9.29-1.25 7.27-3.16 13.12-6.23 21.31.82.79 5.37 4.6 17.75 5.29 8.73.49 12.66-2.05 13.45-2.64-1.47-7-1.25-9.65-.92-13.64.12-1.41.25-3 .32-5.01.17-4.93-.26-7.95-1.78-12.4-.25-.74-.65-1.65-1.07-2.61-1-2.28-1.94-4.43-1.53-5.48.7-1.79 1.59-5.22 1.2-7.36-.12-.7-.16-1.41-.18-2.09m5.69-4.71c-2.06-2.85-5.28-2.74-5.28-2.74-.84-.04-3.07-.14-3.62-.19-.32.36-1.22 1.98-1.95 3.3-1.01 1.83-2.06 3.72-2.6 4.29-.04.05-.1.13-.17.22-.34.45-.58.75-.89.78a.5.5 0 0 1-.35-.11c-1.2-1.05-2.78-2.52-4.36-4.58-.82-1.06-1.34-1.74-1.72-2.93-.63.21-2.52.44-3.06.63M30.371 17.04l2.29-2.46c.3-.33.64-.62 1.02-.86 1.08-.69 2.39-1.29 2.39-1.29'
          />
          <path
            stroke='#80583F'
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeMiterlimit='10'
            strokeWidth='0.4'
            d='M12.072 11.008s-3.84.64-3.99 5.57l.45-.08c.68-.12 1.37-.15 2.06-.1l2.46.18M35.45 12.79c2.59 3.18 8.35 9.38 8.8 10.31.55 1.1.33 1.84-.65 3.31s-8.59 11.08-9.97 11.54c-1.11.37-1.22-.31-2.12-.06-.58.16-1.23.48-2.09.83 0 0-1.47.76-1.7.28l3.34-2.21s-1.65-.03-2.39.49c-.58.41-1.27.69-1.77.44 0 0 .44-.34 1.86-1.46.48-.38 1.15-.58 2.01-.58 0 0-2.02.03-2.48.38-.54.4-1.22 1.08-1.56.77 0 0 .6-.78 1.11-1.3 0 0 .72-.93 2.36-.72l-1.03-.34s-.55.11-.79.26c-.95.63-1.42.13-1.42.13l1.56-1.11c.07-.05.44-.1.53-.09l4.35.52 5.35-9.14-8.3-7.58M14.98 10.487c.73-.23 1.4-.47 1.91-.76 1.74-.98 1.2-6.75 1.2-6.75h6.32s-.54 6.43 2.67 6.62'
          />
          <path
            stroke='#80583F'
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeMiterlimit='10'
            strokeWidth='0.4'
            d='M4.92 58.214c-.3-.6-.52-1.27-.77-1.91-.55-1.39-.59-2.64-.54-4.13.12-3.39.95-6.72 1.92-9.96.45-1.5.86-3.04 1.51-4.47.66-1.47 1.5-2.86 2.04-4.38.2-.55.29-1.12.29-1.71 0-5.73-.93-11.36-.73-15.17'
          />
          <mask
            id='mask1_13163_57405'
            width='13'
            height='13'
            x='25'
            y='30'
            maskUnits='userSpaceOnUse'
            style={{
              maskType: 'luminance',
            }}
          >
            <path
              fill='#fff'
              d='M36.73 36.527c3.11-.6-2.57 5.74-5.74 5.74a5.74 5.74 0 0 1-5.74-5.74c0-3.17 3.35-7.83 5.74-5.74 2.31 2.01 2.28 6.41 5.74 5.74'
            />
          </mask>
          <g mask='url(#mask1_13163_57405)'>
            <path
              fill='#fff'
              d='M6.27 58.328s.66 1.73-.17 1.04c-.96-.8-1.29-2.03-1.74-3.18-.54-1.37-.59-2.62-.54-4.09.12-3.36.95-6.66 1.9-9.87.44-1.49.85-3.01 1.49-4.43.66-1.46 1.48-2.83 2.02-4.34.2-.55.29-1.11.29-1.7 0-8.51-2.07-17.05.67-19.13 1.57-1.2 5.06-1.64 6.79-2.61s1.19-6.69 1.19-6.69h6.27s-.54 6.45 2.7 6.57 5.88-.32 7.18 1.75 9.25 10.53 9.79 11.63c.54 1.09.32 1.82-.65 3.28s-8.51 10.98-9.88 11.43c-1.1.36-1.21-.3-2.1-.06-.57.16-1.22.47-2.07.82 0 0-1.45.75-1.69.28l3.31-2.19s-1.64-.03-2.37.49c-.57.4-1.26.68-1.75.44 0 0 .43-.33 1.84-1.44.48-.37 1.14-.58 1.99-.57 0 0-2 .03-2.46.38-.53.4-1.21 1.07-1.54.76 0 0 .59-.77 1.1-1.29 0 0 .72-.92 2.34-.71l-1.02-.33s-.55.1-.78.26c-.94.63-1.41.13-1.41.13l1.55-1.1c.07-.05.44-.1.52-.09l4.31.52 5.3-9.06-8.22-7.51-8.11 7.21-6.22 3.69-2.88 7.15-3.63 5.79-1.36 5.2-1.26 7.57c.15 3.89.06 3.29-.7 4'
            />
          </g>
          <mask
            id='mask2_13163_57405'
            width='13'
            height='13'
            x='25'
            y='30'
            maskUnits='userSpaceOnUse'
            style={{
              maskType: 'luminance',
            }}
          >
            <path
              fill='#fff'
              d='M36.4 37.091c2.8 1.49-2.24 5.17-5.41 5.17a5.74 5.74 0 0 1-5.74-5.74c0-3.17 3.96-8.37 5.74-5.74 2.36 3.49 3.67 5.39 5.41 6.31'
            />
          </mask>
          <g mask='url(#mask2_13163_57405)'>
            <path
              stroke='#80583F'
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeMiterlimit='10'
              strokeWidth='0.4'
              d='M6.27 58.328s.66 1.73-.17 1.04c-.96-.8-1.29-2.03-1.74-3.18-.54-1.37-.59-2.62-.54-4.09.12-3.36.95-6.66 1.9-9.87.44-1.49.85-3.01 1.49-4.43.66-1.46 1.48-2.83 2.02-4.34.2-.55.29-1.11.29-1.7 0-8.51-2.07-17.05.67-19.13 1.57-1.2 5.06-1.64 6.79-2.61s1.19-6.69 1.19-6.69h6.27s-.54 6.45 2.7 6.57 5.88-.32 7.18 1.75 9.25 10.53 9.79 11.63c.54 1.09.32 1.82-.65 3.28s-8.51 10.98-9.88 11.43c-1.1.36-1.21-.3-2.1-.06-.57.16-1.22.47-2.07.82 0 0-1.45.75-1.69.28l3.31-2.19s-1.64-.03-2.37.49c-.57.4-1.26.68-1.75.44 0 0 .43-.33 1.84-1.44.48-.37 1.14-.58 1.99-.57 0 0-2 .03-2.46.38-.53.4-1.21 1.07-1.54.76 0 0 .59-.77 1.1-1.29 0 0 .72-.92 2.34-.71l-1.02-.33s-.55.1-.78.26c-.94.63-1.41.13-1.41.13l1.55-1.1c.07-.05.44-.1.52-.09l4.31.52 5.3-9.06-8.22-7.51-8.11 7.21-6.22 3.69-2.88 7.15-3.63 5.79-1.36 5.2-1.26 7.57c.15 3.89.06 3.29-.7 4'
            />
          </g>
        </g>
      </svg>
    ),
    'Spaghetti Straps': (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='46'
        height='60'
        fill='none'
        viewBox='0 0 46 60'
      >
        <mask
          id='mask0_13163_57745'
          width='46'
          height='60'
          x='0'
          y='0'
          maskUnits='userSpaceOnUse'
          style={{
            maskType: 'luminance',
          }}
        >
          <path
            fill='#fff'
            d='M45.5 0H.5v60h45z'
          />
        </mask>
        <g mask='url(#mask0_13163_57745)'>
          <path
            fill='#fff'
            d='M5.001 59.773s.67 1.74-.17 1.05c-.97-.8-1.3-2.05-1.76-3.21-.55-1.39-.59-2.64-.54-4.13.12-3.39.95-6.72 1.92-9.96.45-1.5.86-3.04 1.51-4.47.66-1.47 1.5-2.86 2.04-4.38.2-.55.29-1.12.29-1.71 0-8.59-2.09-17.2.67-19.3 1.59-1.21 5.1-1.65 6.85-2.64 1.74-.98 1.2-6.75 1.2-6.75h6.32s-.55 6.5 2.73 6.63c3.27.12 5.93-.32 7.24 1.77s9.33 10.63 9.87 11.73c.55 1.1.33 1.84-.65 3.31s-8.59 11.08-9.97 11.54c-1.11.37-1.22-.31-2.12-.06-.58.16-1.23.48-2.09.83 0 0-1.47.76-1.7.28l3.34-2.21s-1.65-.03-2.39.49c-.58.41-1.27.69-1.77.44 0 0 .44-.34 1.86-1.46.48-.38 1.15-.58 2.01-.58 0 0-2.02.03-2.48.38-.54.4-1.22 1.08-1.56.77 0 0 .6-.78 1.11-1.3 0 0 .72-.93 2.36-.72l-1.03-.34s-.55.11-.79.26c-.95.63-1.42.13-1.42.13l1.56-1.11c.07-.05.44-.1.53-.09l4.35.52 5.35-9.14-8.3-7.58-8.19 7.28-6.27 3.72-2.91 7.21-3.66 5.85-1.37 5.25-1.27 7.64c.17 3.95.07 3.35-.7 4.06'
          />
          <path
            stroke='#80583F'
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeMiterlimit='10'
            strokeWidth='0.4'
            d='M5.001 59.773s.67 1.74-.17 1.05c-.97-.8-1.3-2.05-1.76-3.21-.55-1.39-.59-2.64-.54-4.13.12-3.39.95-6.72 1.92-9.96.45-1.5.86-3.04 1.51-4.47.66-1.47 1.5-2.86 2.04-4.38.2-.55.29-1.12.29-1.71 0-8.59-2.09-17.2.67-19.3 1.59-1.21 5.1-1.65 6.85-2.64 1.74-.98 1.2-6.75 1.2-6.75h6.32s-.55 6.5 2.73 6.63c3.27.12 5.93-.32 7.24 1.77s9.33 10.63 9.87 11.73c.55 1.1.33 1.84-.65 3.31s-8.59 11.08-9.97 11.54c-1.11.37-1.22-.31-2.12-.06-.58.16-1.23.48-2.09.83 0 0-1.47.76-1.7.28l3.34-2.21s-1.65-.03-2.39.49c-.58.41-1.27.69-1.77.44 0 0 .44-.34 1.86-1.46.48-.38 1.15-.58 2.01-.58 0 0-2.02.03-2.48.38-.54.4-1.22 1.08-1.56.77 0 0 .6-.78 1.11-1.3 0 0 .72-.93 2.36-.72l-1.03-.34s-.55.11-.79.26c-.95.63-1.42.13-1.42.13l1.56-1.11c.07-.05.44-.1.53-.09l4.35.52 5.35-9.14-8.3-7.58-8.19 7.28-6.27 3.72-2.91 7.21-3.66 5.85-1.37 5.25-1.27 7.64c.17 3.95.07 3.35-.7 4.06'
          />
          <path
            className='fill-f8f3ef group-[.svg-selected]_fill-BE9983 lg_group-hover_fill-BE9983'
            d='M32.622 48c.16-4.78-.22-7.8-1.78-12.38-.81-2.38-3.09-6.43-2.57-7.76.71-1.81 1.61-5.25 1.21-7.46-.08-.42-.15-.61-.21-.79-.01-.02-.01-.04-.02-.06.08.19-.16-.37-.19-.44-.18-.39.03-.57-.19-.94-.12-.17-.56-.37-.47-.26-.07-.09-3.06-1-4.09-.98-.25.01-.68.32-.69.32-.56.27-1.31.64-1.47.78-.3.26-1.51 1.56-1.72 1.37-.91-.8-1.55-1.73-4.36-2.39-1.4-.33-.73-.11-2.4 0l-.88.31c-.46.52-.46.42-.97.93-.22.22-.18 1.13-.04 3.23.03.44 2 7.18 1.64 7.44-.02.01-.78 2.71-.83 2.75-2.22 4.4-3.64 7.88-3.64 7.88s-.42 2.12-1.74 7.27c-1 3.87-1.4 5.28-1.4 5.28l-.32.73c-1 3.33-2.24 6.84-3.82 11.04 0 0 3.97 4.71 17.92 5.48 10 .55 13.68-2.82 13.68-2.82-2.06-9.54-.91-11.02-.65-18.53'
          />
          <path
            fill='#80583F'
            d='M4.281 57.62c.22-.6.48-1.19.66-1.81l-.15-1.32a.35.35 0 0 0-.28-.3c-.15-.03-.22.18-.29.31-.05.09-.8 1.65-.01 3.08.01.03.04.03.07.04'
          />
          <path
            stroke='#80583F'
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeMiterlimit='10'
            strokeWidth='0.4'
            d='M11.752 18.047c-.12 4.56.64 6.76 1.18 8.54.21.68.39 1.26.46 1.81.19 1.38-.69 3.08-1.8 5.22-1.27 2.45-2.86 5.49-3.51 9.29-1.25 7.27-3.16 13.12-6.23 21.31.82.79 5.37 4.6 17.75 5.29 8.73.49 12.66-2.05 13.45-2.64-1.47-7-1.25-9.65-.92-13.64.12-1.41.25-3 .32-5.01.17-4.93-.26-7.95-1.78-12.4-.25-.74-.65-1.65-1.07-2.61-1-2.28-1.94-4.43-1.53-5.48.7-1.79 1.59-5.22 1.2-7.36-.12-.7-.16-1.32-.19-2.01'
          />
          <path
            stroke='#80583F'
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeMiterlimit='10'
            strokeWidth='0.4'
            d='M11.75 18.047s4.42-3.21 8.62 1.74c0 0 3.81-5.06 8.71-1.44M13.14 12.008l.65 5.18M27.75 10.984l-.77 6.29'
          />
          <mask
            id='mask1_13163_57745'
            width='12'
            height='12'
            x='24'
            y='32'
            maskUnits='userSpaceOnUse'
            style={{
              maskType: 'luminance',
            }}
          >
            <path
              fill='#fff'
              d='M29.982 43.503a5.74 5.74 0 1 0 0-11.48 5.74 5.74 0 0 0 0 11.48'
            />
          </mask>
          <g mask='url(#mask1_13163_57745)'>
            <path
              fill='#fff'
              d='M5.262 59.563s.66 1.73-.17 1.04c-.96-.8-1.29-2.03-1.74-3.18-.54-1.37-.59-2.62-.54-4.09.12-3.36.95-6.66 1.9-9.87.44-1.49.85-3.01 1.49-4.43.66-1.46 1.48-2.83 2.02-4.34.2-.55.29-1.11.29-1.7 0-8.51-2.07-17.05.67-19.13 1.57-1.2 5.06-1.64 6.79-2.61s1.19-6.69 1.19-6.69h6.27s-.54 6.45 2.7 6.57 5.88-.32 7.18 1.75 9.25 10.53 9.79 11.63c.54 1.09.32 1.82-.65 3.28s-8.51 10.98-9.88 11.43c-1.1.36-1.21-.3-2.1-.06-.57.16-1.22.47-2.07.82 0 0-1.45.75-1.69.28l3.31-2.19s-1.64-.03-2.37.49c-.57.4-1.26.68-1.75.44 0 0 .43-.33 1.84-1.44.48-.37 1.14-.58 1.99-.57 0 0-2 .03-2.46.38-.53.4-1.21 1.07-1.54.76 0 0 .59-.77 1.1-1.29 0 0 .72-.92 2.34-.71l-1.02-.33s-.55.1-.78.26c-.94.63-1.41.13-1.41.13l1.55-1.1c.07-.05.44-.1.52-.09l4.31.52 5.3-9.06-8.22-7.51-8.11 7.21-6.22 3.69-2.88 7.15-3.63 5.79-1.36 5.2-1.26 7.57c.15 3.89.06 3.3-.7 4'
            />
          </g>
          <mask
            id='mask2_13163_57745'
            width='12'
            height='12'
            x='24'
            y='32'
            maskUnits='userSpaceOnUse'
            style={{
              maskType: 'luminance',
            }}
          >
            <path
              fill='#fff'
              d='M29.982 43.503a5.74 5.74 0 1 0 0-11.48 5.74 5.74 0 0 0 0 11.48'
            />
          </mask>
          <g mask='url(#mask2_13163_57745)'>
            <path
              stroke='#80583F'
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeMiterlimit='10'
              strokeWidth='0.4'
              d='M5.262 59.563s.66 1.73-.17 1.04c-.96-.8-1.29-2.03-1.74-3.18-.54-1.37-.59-2.62-.54-4.09.12-3.36.95-6.66 1.9-9.87.44-1.49.85-3.01 1.49-4.43.66-1.46 1.48-2.83 2.02-4.34.2-.55.29-1.11.29-1.7 0-8.51-2.07-17.05.67-19.13 1.57-1.2 5.06-1.64 6.79-2.61s1.19-6.69 1.19-6.69h6.27s-.54 6.45 2.7 6.57 5.88-.32 7.18 1.75 9.25 10.53 9.79 11.63c.54 1.09.32 1.82-.65 3.28s-8.51 10.98-9.88 11.43c-1.1.36-1.21-.3-2.1-.06-.57.16-1.22.47-2.07.82 0 0-1.45.75-1.69.28l3.31-2.19s-1.64-.03-2.37.49c-.57.4-1.26.68-1.75.44 0 0 .43-.33 1.84-1.44.48-.37 1.14-.58 1.99-.57 0 0-2 .03-2.46.38-.53.4-1.21 1.07-1.54.76 0 0 .59-.77 1.1-1.29 0 0 .72-.92 2.34-.71l-1.02-.33s-.55.1-.78.26c-.94.63-1.41.13-1.41.13l1.55-1.1c.07-.05.44-.1.52-.09l4.31.52 5.3-9.06-8.22-7.51-8.11 7.21-6.22 3.69-2.88 7.15-3.63 5.79-1.36 5.2-1.26 7.57c.15 3.89.06 3.3-.7 4'
            />
          </g>
        </g>
      </svg>
    ),
    Sleeveless: (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='46'
        height='60'
        fill='none'
        viewBox='0 0 46 60'
      >
        <mask
          id='mask0_13163_57586'
          width='46'
          height='60'
          x='0'
          y='0'
          maskUnits='userSpaceOnUse'
          style={{
            maskType: 'luminance',
          }}
        >
          <path
            fill='#fff'
            d='M45.5 0H.5v60h45z'
          />
        </mask>
        <g mask='url(#mask0_13163_57586)'>
          <path
            fill='#fff'
            d='M5.689 59.773s.67 1.74-.17 1.05c-.97-.8-1.3-2.05-1.76-3.21-.55-1.39-.59-2.64-.54-4.13.12-3.39.95-6.72 1.92-9.96.45-1.5.86-3.04 1.51-4.47.66-1.47 1.5-2.86 2.04-4.38.2-.55.29-1.12.29-1.71 0-8.59-2.09-17.2.67-19.3 1.59-1.21 5.1-1.65 6.85-2.64 1.74-.98 1.2-6.75 1.2-6.75h6.32s-.55 6.5 2.73 6.63c3.27.12 5.93-.32 7.24 1.77s9.33 10.63 9.87 11.73c.55 1.1.33 1.84-.65 3.31s-8.59 11.08-9.97 11.54c-1.11.37-1.22-.31-2.12-.06-.58.16-1.23.48-2.09.83 0 0-1.47.76-1.7.28l3.34-2.21s-1.65-.03-2.39.49c-.58.41-1.27.69-1.77.44 0 0 .44-.34 1.86-1.46.48-.38 1.15-.58 2.01-.58 0 0-2.02.03-2.48.38-.54.4-1.22 1.08-1.56.77 0 0 .6-.78 1.11-1.3 0 0 .72-.93 2.36-.72l-1.03-.34s-.55.11-.79.26c-.95.63-1.42.13-1.42.13l1.56-1.11c.07-.05.44-.1.53-.09l4.35.52 5.35-9.14-8.3-7.58-8.18 7.3-6.27 3.72-2.91 7.21-3.66 5.85-1.37 5.25-1.27 7.64c.15 3.93.05 3.33-.71 4.04'
          />
          <path
            stroke='#80583F'
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeMiterlimit='10'
            strokeWidth='0.4'
            d='m30.069 18.783-8.19 7.28-6.27 3.72-2.91 7.21-3.66 5.85-1.37 5.25-1.27 7.64c.15 3.93.06 3.33-.71 4.04 0 0 .67 1.74-.17 1.05-.97-.8-1.3-2.05-1.76-3.21-.55-1.39-.59-2.64-.54-4.13.12-3.39.95-6.72 1.92-9.96.45-1.5.86-3.04 1.51-4.47.66-1.47 1.5-2.86 2.04-4.38.2-.55.29-1.12.29-1.71 0-8.59-2.09-17.2.67-19.3 1.59-1.21 5.1-1.65 6.85-2.64 1.74-.98 1.2-6.75 1.2-6.75h6.32s-.55 6.5 2.73 6.63c3.27.12 5.93-.32 7.24 1.77s9.33 10.63 9.87 11.73c.55 1.1.33 1.84-.65 3.31s-8.59 11.08-9.97 11.54c-1.11.37-1.22-.31-2.12-.06-.58.16-1.23.48-2.09.83 0 0-1.47.76-1.7.28l3.34-2.21s-1.65-.03-2.39.49c-.58.41-1.27.69-1.77.44 0 0 .44-.34 1.86-1.46.48-.38 1.15-.58 2.01-.58 0 0-2.02.03-2.48.38-.54.4-1.22 1.08-1.56.77 0 0 .6-.78 1.11-1.3 0 0 .72-.93 2.36-.72l-1.03-.34s-.55.11-.79.26c-.95.63-1.42.13-1.42.13l1.56-1.11c.07-.05.44-.1.53-.09l4.35.52 5.35-9.14z'
          />
          <path
            className='fill-f8f3ef group-[.svg-selected]_fill-BE9983 lg_group-hover_fill-BE9983'
            d='M33.33 48.14c.16-4.78-.22-7.8-1.78-12.38-.81-2.38-3.09-6.43-2.57-7.76.71-1.81 1.61-5.25 1.21-7.46-.08-.42-.15-.61-.21-.79-.01-.02-.01-.04-.02-.06.08.19-.16-.37-.19-.44-.18-.39.2-1.35-.02-1.72-.12-.17-.29-6.5-.19-6.39-.07-.09-1.56 0-2.59.02-.25.01-.8 2.58-2.47 5.09-.58.43-2.19.87-2.35 1.01-.3.26-2.16-.04-2.43-.14-1.04-.39-1.79-.58-2.94-2.1-.9-1.19-.55-1.38-1.31-3.3l-2.63.66c-.46.52-.11 4.36-.33 6-.16 1.25-.18 1.13-.04 3.23.03.44 2 7.18 1.64 7.44-.02.01-.78 2.71-.83 2.75-2.22 4.4-3.64 7.88-3.64 7.88s-.42 2.12-1.74 7.27c-1 3.87-1.4 5.28-1.4 5.28l-.32.73c-1 3.33-2.24 6.84-3.82 11.04 0 0 3.97 4.71 17.92 5.48 10 .55 13.68-2.82 13.68-2.82-2.03-9.53-.89-11.02-.63-18.52'
          />
          <path
            fill='#80583F'
            d='M4.999 57.762c.22-.6.48-1.19.66-1.81l-.15-1.32a.35.35 0 0 0-.28-.3c-.15-.03-.27.12-.34.25-.05.09-.61 1.66.04 3.14 0 .02.04.03.07.04'
          />
          <path
            stroke='#80583F'
            strokeMiterlimit='10'
            strokeWidth='0.4'
            d='m12.77 12.195-.3 5.99c-.12 4.56.64 6.76 1.18 8.54.21.68.39 1.26.46 1.81.19 1.38-.69 3.08-1.8 5.22-1.27 2.45-2.86 5.49-3.51 9.29-1.25 7.27-3.16 13.12-6.23 21.31.82.79 5.37 4.6 17.75 5.29 8.73.49 12.66-2.05 13.45-2.64-1.47-7-1.25-9.65-.92-13.64.12-1.41.25-3 .32-5.01.17-4.93-.26-7.95-1.78-12.4-.25-.74-.65-1.65-1.07-2.61-1-2.28-1.94-4.43-1.53-5.48.7-1.79 1.59-5.22 1.2-7.36-.12-.7-.16-1.32-.19-2.01'
          />
          <path
            stroke='#80583F'
            strokeMiterlimit='10'
            strokeWidth='0.4'
            d='M15.57 11.667s.42 5.68 5.75 5.68 5.39-6.37 5.39-6.37M29.34 11.11l.46 7.38'
          />
          <mask
            id='mask1_13163_57586'
            width='13'
            height='12'
            x='24'
            y='32'
            maskUnits='userSpaceOnUse'
            style={{
              maskType: 'luminance',
            }}
          >
            <path
              fill='#fff'
              d='M30.658 43.503a5.74 5.74 0 1 0 0-11.48 5.74 5.74 0 0 0 0 11.48'
            />
          </mask>
          <g mask='url(#mask1_13163_57586)'>
            <path
              fill='#fff'
              d='M5.938 59.563s.66 1.73-.17 1.04c-.96-.8-1.29-2.03-1.74-3.18-.54-1.37-.59-2.62-.54-4.09.12-3.36.95-6.66 1.9-9.87.44-1.49.85-3.01 1.49-4.43.66-1.46 1.48-2.83 2.02-4.34.2-.55.29-1.11.29-1.7 0-8.51-2.07-17.05.67-19.13 1.57-1.2 5.06-1.64 6.79-2.61s1.19-6.69 1.19-6.69h6.27s-.54 6.45 2.7 6.57 5.88-.32 7.18 1.75 9.25 10.53 9.79 11.63c.54 1.09.32 1.82-.65 3.28s-8.51 10.98-9.88 11.43c-1.1.36-1.21-.3-2.1-.06-.57.16-1.22.47-2.07.82 0 0-1.45.75-1.69.28l3.31-2.19s-1.64-.03-2.37.49c-.57.4-1.26.68-1.75.44 0 0 .43-.33 1.84-1.44.48-.37 1.14-.58 1.99-.57 0 0-2 .03-2.46.38-.53.4-1.21 1.07-1.54.76 0 0 .59-.77 1.1-1.29 0 0 .72-.92 2.34-.71l-1.02-.33s-.55.1-.78.26c-.94.63-1.41.13-1.41.13l1.55-1.1c.07-.05.44-.1.52-.09l4.31.52 5.3-9.06-8.22-7.51-8.11 7.21-6.22 3.69-2.88 7.15-3.63 5.79-1.36 5.2-1.26 7.57c.15 3.89.06 3.3-.7 4'
            />
          </g>
          <mask
            id='mask2_13163_57586'
            width='13'
            height='12'
            x='24'
            y='32'
            maskUnits='userSpaceOnUse'
            style={{
              maskType: 'luminance',
            }}
          >
            <path
              fill='#fff'
              d='M30.658 43.503a5.74 5.74 0 1 0 0-11.48 5.74 5.74 0 0 0 0 11.48'
            />
          </mask>
          <g mask='url(#mask2_13163_57586)'>
            <path
              stroke='#80583F'
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeMiterlimit='10'
              strokeWidth='0.4'
              d='M5.938 59.563s.66 1.73-.17 1.04c-.96-.8-1.29-2.03-1.74-3.18-.54-1.37-.59-2.62-.54-4.09.12-3.36.95-6.66 1.9-9.87.44-1.49.85-3.01 1.49-4.43.66-1.46 1.48-2.83 2.02-4.34.2-.55.29-1.11.29-1.7 0-8.51-2.07-17.05.67-19.13 1.57-1.2 5.06-1.64 6.79-2.61s1.19-6.69 1.19-6.69h6.27s-.54 6.45 2.7 6.57 5.88-.32 7.18 1.75 9.25 10.53 9.79 11.63c.54 1.09.32 1.82-.65 3.28s-8.51 10.98-9.88 11.43c-1.1.36-1.21-.3-2.1-.06-.57.16-1.22.47-2.07.82 0 0-1.45.75-1.69.28l3.31-2.19s-1.64-.03-2.37.49c-.57.4-1.26.68-1.75.44 0 0 .43-.33 1.84-1.44.48-.37 1.14-.58 1.99-.57 0 0-2 .03-2.46.38-.53.4-1.21 1.07-1.54.76 0 0 .59-.77 1.1-1.29 0 0 .72-.92 2.34-.71l-1.02-.33s-.55.1-.78.26c-.94.63-1.41.13-1.41.13l1.55-1.1c.07-.05.44-.1.52-.09l4.31.52 5.3-9.06-8.22-7.51-8.11 7.21-6.22 3.69-2.88 7.15-3.63 5.79-1.36 5.2-1.26 7.57c.15 3.89.06 3.3-.7 4'
            />
          </g>
        </g>
      </svg>
    ),
    Strapless: (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='46'
        height='60'
        fill='none'
        viewBox='0 0 46 60'
      >
        <mask
          id='mask0_13163_57908'
          width='46'
          height='60'
          x='0'
          y='0'
          maskUnits='userSpaceOnUse'
          style={{
            maskType: 'luminance',
          }}
        >
          <path
            fill='#fff'
            d='M45.5 0H.5v60h45z'
          />
        </mask>
        <g mask='url(#mask0_13163_57908)'>
          <path
            fill='#fff'
            d='M5.45 59.773s.67 1.74-.17 1.05c-.97-.8-1.3-2.05-1.76-3.21-.55-1.39-.59-2.64-.54-4.13.12-3.39.95-6.72 1.92-9.96.45-1.5.86-3.04 1.51-4.47.66-1.47 1.5-2.86 2.04-4.38.2-.55.29-1.12.29-1.71 0-8.59-2.09-17.2.67-19.3 1.59-1.21 5.1-1.65 6.85-2.64 1.74-.98 1.2-6.75 1.2-6.75h6.32s-.55 6.5 2.73 6.63c3.27.12 5.93-.32 7.24 1.77s9.33 10.63 9.87 11.73c.55 1.1.33 1.84-.65 3.31s-8.59 11.08-9.97 11.54c-1.11.37-1.22-.31-2.12-.06-.58.16-1.23.48-2.09.83 0 0-1.47.76-1.7.28l3.34-2.21s-1.65-.03-2.39.49c-.58.41-1.27.69-1.77.44 0 0 .44-.34 1.86-1.46.48-.38 1.15-.58 2.01-.58 0 0-2.02.03-2.48.38-.54.4-1.22 1.08-1.56.77 0 0 .6-.78 1.11-1.3 0 0 .72-.93 2.36-.72l-1.03-.34s-.55.11-.79.26c-.95.63-1.42.13-1.42.13l1.56-1.11c.07-.05.44-.1.53-.09l4.35.52 5.35-9.14-8.3-7.58-8.19 7.28-6.27 3.72-2.91 7.21-3.66 5.85-1.37 5.25-1.27 7.64c.16 3.95.07 3.35-.7 4.06'
          />
          <path
            stroke='#80583F'
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeMiterlimit='10'
            strokeWidth='0.4'
            d='M5.45 59.773s.67 1.74-.17 1.05c-.97-.8-1.3-2.05-1.76-3.21-.55-1.39-.59-2.64-.54-4.13.12-3.39.95-6.72 1.92-9.96.45-1.5.86-3.04 1.51-4.47.66-1.47 1.5-2.86 2.04-4.38.2-.55.29-1.12.29-1.71 0-8.59-2.09-17.2.67-19.3 1.59-1.21 5.1-1.65 6.85-2.64 1.74-.98 1.2-6.75 1.2-6.75h6.32s-.55 6.5 2.73 6.63c3.27.12 5.93-.32 7.24 1.77s9.33 10.63 9.87 11.73c.55 1.1.33 1.84-.65 3.31s-8.59 11.08-9.97 11.54c-1.11.37-1.22-.31-2.12-.06-.58.16-1.23.48-2.09.83 0 0-1.47.76-1.7.28l3.34-2.21s-1.65-.03-2.39.49c-.58.41-1.27.69-1.77.44 0 0 .44-.34 1.86-1.46.48-.38 1.15-.58 2.01-.58 0 0-2.02.03-2.48.38-.54.4-1.22 1.08-1.56.77 0 0 .6-.78 1.11-1.3 0 0 .72-.93 2.36-.72l-1.03-.34s-.55.11-.79.26c-.95.63-1.42.13-1.42.13l1.56-1.11c.07-.05.44-.1.53-.09l4.35.52 5.35-9.14-8.3-7.58-8.19 7.28-6.27 3.72-2.91 7.21-3.66 5.85-1.37 5.25-1.27 7.64c.16 3.95.07 3.35-.7 4.06'
          />
          <path
            className='fill-f8f3ef group-[.svg-selected]_fill-BE9983 lg_group-hover_fill-BE9983'
            d='M33.06 48.002c.16-4.78-.22-7.8-1.78-12.38-.81-2.38-3.09-6.43-2.57-7.76.71-1.81 1.61-5.25 1.21-7.46-.08-.42-.15-.61-.21-.79-.01-.02-.01-.04-.02-.06.08.19-.16-.37-.19-.44-.18-.39.03-.57-.19-.94-.12-.17-.56-.37-.47-.26-.07-.09-3.08-1.18-4.11-1.16-.25.01-4.04-.29-6.95.21-1.42.25-5.03.77-5.54 1.28-.22.22-.18 1.13-.04 3.23.03.44 2 7.18 1.64 7.44-.02.01-.78 2.71-.83 2.75-2.22 4.4-3.64 7.88-3.64 7.88s-.42 2.12-1.74 7.27c-1 3.87-1.4 5.28-1.4 5.28l-.32.73c-1 3.33-2.24 6.84-3.82 11.04 0 0 3.97 4.71 17.92 5.48 10 .55 13.68-2.82 13.68-2.82-2.03-9.53-.89-11.01-.63-18.52'
          />
          <path
            fill='#80583F'
            d='M4.73 57.62c.22-.6.48-1.19.66-1.81l-.15-1.32a.35.35 0 0 0-.28-.3c-.15-.03-.22.18-.29.31-.05.09-.8 1.65-.01 3.08 0 .03.04.03.07.04'
          />
          <path
            stroke='#80583F'
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeMiterlimit='10'
            strokeWidth='0.4'
            d='M12.2 18.047c-.12 4.56.64 6.76 1.18 8.54.21.68.39 1.26.46 1.81.19 1.38-.69 3.08-1.8 5.22-1.27 2.45-2.86 5.49-3.51 9.29-1.25 7.27-3.16 13.12-6.23 21.31.82.79 5.37 4.6 17.75 5.29 8.73.49 12.66-2.05 13.45-2.64-1.47-7-1.25-9.65-.92-13.64.12-1.41.25-3 .32-5.01.17-4.93-.26-7.95-1.78-12.4-.25-.74-.65-1.65-1.07-2.61-1-2.28-1.94-4.43-1.53-5.48.7-1.79 1.59-5.22 1.2-7.36-.12-.7-.16-1.32-.19-2.01M12.2 18.044s1.75-.84 5.02-1.25c3.91-.49 9.64-.42 12.31 1.55'
          />
          <mask
            id='mask1_13163_57908'
            width='13'
            height='12'
            x='24'
            y='32'
            maskUnits='userSpaceOnUse'
            style={{
              maskType: 'luminance',
            }}
          >
            <path
              fill='#fff'
              d='M30.431 43.503a5.74 5.74 0 1 0 0-11.48 5.74 5.74 0 0 0 0 11.48'
            />
          </mask>
          <g mask='url(#mask1_13163_57908)'>
            <path
              fill='#fff'
              d='M5.703 59.563s.66 1.73-.17 1.04c-.96-.8-1.29-2.03-1.74-3.18-.54-1.37-.59-2.62-.54-4.09.12-3.36.95-6.66 1.9-9.87.44-1.49.85-3.01 1.49-4.43.66-1.46 1.48-2.83 2.02-4.34.2-.55.29-1.11.29-1.7 0-8.51-2.07-17.05.67-19.13 1.57-1.2 5.06-1.64 6.79-2.61s1.19-6.69 1.19-6.69h6.27s-.54 6.45 2.7 6.57 5.88-.32 7.18 1.75 9.25 10.53 9.79 11.63c.54 1.09.32 1.82-.65 3.28s-8.51 10.98-9.88 11.43c-1.1.36-1.21-.3-2.1-.06-.57.16-1.22.47-2.07.82 0 0-1.45.75-1.69.28l3.31-2.19s-1.64-.03-2.37.49c-.57.4-1.26.68-1.75.44 0 0 .43-.33 1.84-1.44.48-.37 1.14-.58 1.99-.57 0 0-2 .03-2.46.38-.53.4-1.21 1.07-1.54.76 0 0 .59-.77 1.1-1.29 0 0 .72-.92 2.34-.71l-1.02-.33s-.55.1-.78.26c-.94.63-1.41.13-1.41.13l1.55-1.1c.07-.05.44-.1.52-.09l4.31.52 5.3-9.06-8.22-7.51-8.11 7.21-6.22 3.69-2.88 7.15-3.63 5.79-1.36 5.2-1.26 7.57c.16 3.89.06 3.3-.7 4'
            />
          </g>
          <mask
            id='mask2_13163_57908'
            width='13'
            height='12'
            x='24'
            y='32'
            maskUnits='userSpaceOnUse'
            style={{
              maskType: 'luminance',
            }}
          >
            <path
              fill='#fff'
              d='M30.431 43.503a5.74 5.74 0 1 0 0-11.48 5.74 5.74 0 0 0 0 11.48'
            />
          </mask>
          <g mask='url(#mask2_13163_57908)'>
            <path
              stroke='#80583F'
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeMiterlimit='10'
              strokeWidth='0.4'
              d='M5.703 59.563s.66 1.73-.17 1.04c-.96-.8-1.29-2.03-1.74-3.18-.54-1.37-.59-2.62-.54-4.09.12-3.36.95-6.66 1.9-9.87.44-1.49.85-3.01 1.49-4.43.66-1.46 1.48-2.83 2.02-4.34.2-.55.29-1.11.29-1.7 0-8.51-2.07-17.05.67-19.13 1.57-1.2 5.06-1.64 6.79-2.61s1.19-6.69 1.19-6.69h6.27s-.54 6.45 2.7 6.57 5.88-.32 7.18 1.75 9.25 10.53 9.79 11.63c.54 1.09.32 1.82-.65 3.28s-8.51 10.98-9.88 11.43c-1.1.36-1.21-.3-2.1-.06-.57.16-1.22.47-2.07.82 0 0-1.45.75-1.69.28l3.31-2.19s-1.64-.03-2.37.49c-.57.4-1.26.68-1.75.44 0 0 .43-.33 1.84-1.44.48-.37 1.14-.58 1.99-.57 0 0-2 .03-2.46.38-.53.4-1.21 1.07-1.54.76 0 0 .59-.77 1.1-1.29 0 0 .72-.92 2.34-.71l-1.02-.33s-.55.1-.78.26c-.94.63-1.41.13-1.41.13l1.55-1.1c.07-.05.44-.1.52-.09l4.31.52 5.3-9.06-8.22-7.51-8.11 7.21-6.22 3.69-2.88 7.15-3.63 5.79-1.36 5.2-1.26 7.57c.16 3.89.06 3.3-.7 4'
            />
          </g>
        </g>
      </svg>
    ),
    'Off the Shoulder': (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='46'
        height='60'
        fill='none'
        viewBox='0 0 46 60'
      >
        <mask
          id='mask0_13163_57243'
          width='46'
          height='60'
          x='0'
          y='0'
          maskUnits='userSpaceOnUse'
          style={{
            maskType: 'luminance',
          }}
        >
          <path
            fill='#fff'
            d='M45.5 0H.5v60h45z'
          />
        </mask>
        <g mask='url(#mask0_13163_57243)'>
          <path
            fill='#fff'
            d='M5.08 58.279s.66 1.74-.17 1.05c-.97-.8-1.3-2.04-1.75-3.19-.54-1.38-.59-2.63-.54-4.11.12-3.38.95-6.7 1.91-9.92.45-1.5.86-3.03 1.5-4.46.66-1.46 1.49-2.85 2.03-4.37.2-.55.29-1.12.29-1.71 0-8.56-2.08-17.14.67-19.24 1.58-1.2 5.08-1.65 6.82-2.63s1.2-6.73 1.2-6.73h6.3s-.54 6.48 2.72 6.6 5.91-.32 7.22 1.76c1.3 2.08 9.3 10.59 9.84 11.69s.33 1.83-.65 3.3-8.56 11.04-9.93 11.5c-1.11.37-1.22-.31-2.11-.06-.58.16-1.23.48-2.08.82 0 0-1.46.76-1.69.28l3.33-2.2s-1.65-.03-2.39.49c-.58.41-1.26.69-1.76.44 0 0 .44-.34 1.85-1.45.48-.38 1.15-.58 2-.57 0 0-2.01.03-2.47.38-.54.4-1.22 1.07-1.55.77 0 0 .6-.78 1.1-1.29 0 0 .72-.93 2.35-.71l-1.03-.34s-.55.1-.78.26c-.95.63-1.42.13-1.42.13l1.55-1.11c.07-.05.44-.1.52-.09l4.34.52 5.33-9.11-8.27-7.56-8.16 7.25-6.25 3.71-2.9 7.19-3.65 5.83-1.37 5.23-1.27 7.61c.17 3.93.08 3.33-.68 4.04'
          />
          <path
            className='fill-f8f3ef group-[.svg-selected]_fill-BE9983 lg_group-hover_fill-BE9983'
            d='M37.281 15.49c-.5-.58-4.5 2.05-5.47 1.83-.46-.1-2.63-.4-3.63-.66-1.77-.47-3.44-1.23-4.97-1.16-.28.01-3.66.03-3.95.05-2.24.13-4.31 1.1-6.87 1.52-1.05.69-2.06 1.38-3.35 1.23-.35-.04-.96-.47-1.39-.57-.01 1.5-1.53 4.34-1.44 6.17.11 2.22 1.55 2.13 4.17 1.9 1.19.01 1.65 1.22 2.84 1.22.63 0-.4 2.45-.38 3.07.02.39-.21.75-.53.95-.61 1.21-1.36 2.34-1.84 3.61-.5 1.34-1.08 2.65-1.53 4.01-.45 1.35-.8 2.73-1.13 4.12-.17.69-.31 1.37-.49 2.05.09.66-.16 1.4-.41 2.04-.01.04-.03.07-.04.11-.06.33-.14.65-.23.98-.14.52-.27 1.04-.42 1.55-.14.46-.34.9-.46 1.37-.07.28-.16.55-.28.81h.03c-1 3.33-2.24 6.84-3.82 11.04 0 0 3.97 4.71 17.92 5.48 10 .55 13.68-2.82 13.68-2.82-2.03-9.56-.89-11.04-.63-18.55.16-4.78-.22-7.8-1.78-12.38-.81-2.38-3.09-6.43-2.57-7.76.71-1.81-.2-1.25 1.19-2.45.67-.58 2.27-.27 3.4.05.05.01 2.12 1 3.02.34.66-.6.25-1.65.39-1.77 1.09-1.66 4.02-.53 4.93-3.09-2.56-3.14-3-3.19-3.96-4.29'
          />
          <path
            fill='#80583F'
            d='M4.34 56.448c.22-.6.48-1.19.66-1.81l-.15-1.32a.35.35 0 0 0-.28-.3c-.15-.03-.21.2-.28.34-.05.09-.72 1.44-.02 3.05.01.03.05.03.07.04'
          />
          <path
            stroke='#80583F'
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeMiterlimit='10'
            strokeWidth='0.4'
            d='M11.812 17.502c-.12 4.56.64 6.13 1.19 7.91.21.68.39 1.26.46 1.81.19 1.38-.69 3.08-1.8 5.22-1.27 2.45-2.86 5.49-3.51 9.29-1.25 7.27-3.16 13.12-6.23 21.31.82.79 5.37 4.6 17.75 5.29 8.73.49 12.66-2.05 13.45-2.64-1.47-7-1.25-9.65-.92-13.64.12-1.41.25-3 .32-5.01.17-4.93-.26-7.95-1.78-12.4-.25-.74-.65-1.65-1.07-2.61-1-2.28-1.94-4.43-1.53-5.48.7-1.79 1.59-5.22 1.2-7.36-.12-.7-.16-1.41-.18-2.09'
          />
          <path
            stroke='#80583F'
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeMiterlimit='10'
            strokeWidth='0.4'
            d='M13.361 26.67c-1.15.81-1.51.02-2.54-.65s-1.11-.04-2.77 0-1.86-1.94-1.86-1.94l1.43-6.27c2.8 1.37 4.2-.3 4.2-.3s9.19-4.39 17.35-.42c0 0 4.3.91 7.99-1.95l4.17 4.51-.44.84s-.9 1.54-2.75 1.33-2.01 1.85-2.01 1.85 0 1.95-2.75.79-3.62-.11-4.49.23'
          />
          <path
            stroke='#80583F'
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeMiterlimit='10'
            strokeWidth='0.4'
            d='M41.033 20.203c1.25 1.43 2.19 2.55 2.37 2.9.55 1.1.33 1.84-.65 3.31s-8.59 11.08-9.97 11.54c-1.11.37-1.22-.31-2.12-.06-.58.16-1.23.48-2.09.83 0 0-1.47.76-1.7.28l3.34-2.21s-1.65-.03-2.39.49c-.58.41-1.27.69-1.77.44 0 0 .44-.34 1.86-1.46.48-.38 1.15-.58 2.01-.58 0 0-2.02.03-2.48.38-.54.4-1.22 1.08-1.56.77 0 0 .6-.78 1.11-1.3 0 0 .72-.93 2.36-.72l-1.03-.34s-.55.11-.79.26c-.95.63-1.42.13-1.42.13l1.56-1.11c.07-.05.44-.1.53-.09l4.35.52 5.35-9.14-1.69-1.55M7.761 17.949c-.04-2.69.27-4.71 1.43-5.59 1.59-1.21 5.1-1.65 6.85-2.64 1.74-.98 1.2-6.75 1.2-6.75h6.32s-.55 6.5 2.73 6.63c3.27.12 5.93-.32 7.24 1.77.46.73 1.74 2.26 3.24 3.98M3.882 57.663c-.3-.6-.33-.7-.58-1.34-.55-1.39-.59-2.64-.54-4.13.12-3.39.95-6.72 1.92-9.96.45-1.5.86-3.04 1.51-4.47.66-1.47 1.5-2.86 2.04-4.38.2-.55.29-1.12.29-1.71 0-1.89-.1-3.78-.23-5.61'
          />
          <mask
            id='mask1_13163_57243'
            width='13'
            height='13'
            x='24'
            y='30'
            maskUnits='userSpaceOnUse'
            style={{
              maskType: 'luminance',
            }}
          >
            <path
              fill='#fff'
              d='M35.87 36.527c3.11-.6-2.57 5.74-5.74 5.74a5.74 5.74 0 0 1-5.74-5.74c0-3.17 3.35-7.83 5.74-5.74 2.3 2.01 2.27 6.41 5.74 5.74'
            />
          </mask>
          <g mask='url(#mask1_13163_57243)'>
            <path
              fill='#fff'
              d='M5.403 58.328s.66 1.73-.17 1.04c-.96-.8-1.29-2.03-1.74-3.18-.54-1.37-.59-2.62-.54-4.09.12-3.36.95-6.66 1.9-9.87.44-1.49.85-3.01 1.49-4.43.66-1.46 1.48-2.83 2.02-4.34.2-.55.29-1.11.29-1.7 0-8.51-2.07-17.05.67-19.13 1.57-1.2 5.06-1.64 6.79-2.61s1.19-6.69 1.19-6.69h6.27s-.54 6.45 2.7 6.57 5.88-.32 7.18 1.75 9.25 10.53 9.79 11.63c.54 1.09.32 1.82-.65 3.28s-8.51 10.98-9.88 11.43c-1.1.36-1.21-.3-2.1-.06-.57.16-1.22.47-2.07.82 0 0-1.45.75-1.69.28l3.31-2.19s-1.64-.03-2.37.49c-.57.4-1.26.68-1.75.44 0 0 .43-.33 1.84-1.44.48-.37 1.14-.58 1.99-.57 0 0-2 .03-2.46.38-.53.4-1.21 1.07-1.54.76 0 0 .59-.77 1.1-1.29 0 0 .72-.92 2.34-.71l-1.02-.33s-.55.1-.78.26c-.94.63-1.41.13-1.41.13l1.55-1.1c.07-.05.44-.1.52-.09l4.31.52 5.3-9.06-8.22-7.51-8.11 7.21-6.22 3.69-2.88 7.15-3.63 5.79-1.36 5.2-1.26 7.57c.16 3.89.06 3.29-.7 4'
            />
          </g>
          <mask
            id='mask2_13163_57243'
            width='13'
            height='13'
            x='24'
            y='30'
            maskUnits='userSpaceOnUse'
            style={{
              maskType: 'luminance',
            }}
          >
            <path
              fill='#fff'
              d='M35.533 37.091c2.8 1.49-2.24 5.17-5.41 5.17a5.74 5.74 0 0 1-5.74-5.74c0-3.17 3.96-8.37 5.74-5.74 2.36 3.49 3.67 5.39 5.41 6.31'
            />
          </mask>
          <g mask='url(#mask2_13163_57243)'>
            <path
              stroke='#80583F'
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeMiterlimit='10'
              strokeWidth='0.4'
              d='M5.403 58.328s.66 1.73-.17 1.04c-.96-.8-1.29-2.03-1.74-3.18-.54-1.37-.59-2.62-.54-4.09.12-3.36.95-6.66 1.9-9.87.44-1.49.85-3.01 1.49-4.43.66-1.46 1.48-2.83 2.02-4.34.2-.55.29-1.11.29-1.7 0-8.51-2.07-17.05.67-19.13 1.57-1.2 5.06-1.64 6.79-2.61s1.19-6.69 1.19-6.69h6.27s-.54 6.45 2.7 6.57 5.88-.32 7.18 1.75 9.25 10.53 9.79 11.63c.54 1.09.32 1.82-.65 3.28s-8.51 10.98-9.88 11.43c-1.1.36-1.21-.3-2.1-.06-.57.16-1.22.47-2.07.82 0 0-1.45.75-1.69.28l3.31-2.19s-1.64-.03-2.37.49c-.57.4-1.26.68-1.75.44 0 0 .43-.33 1.84-1.44.48-.37 1.14-.58 1.99-.57 0 0-2 .03-2.46.38-.53.4-1.21 1.07-1.54.76 0 0 .59-.77 1.1-1.29 0 0 .72-.92 2.34-.71l-1.02-.33s-.55.1-.78.26c-.94.63-1.41.13-1.41.13l1.55-1.1c.07-.05.44-.1.52-.09l4.31.52 5.3-9.06-8.22-7.51-8.11 7.21-6.22 3.69-2.88 7.15-3.63 5.79-1.36 5.2-1.26 7.57c.16 3.89.06 3.29-.7 4'
            />
          </g>
        </g>
      </svg>
    ),
  },
};

export default DressStyleFilterItem;
